// fresh cpy 7.1.2026

// src/components/RPA/RpaDashboard.js - COMPLETE FIXED VERSION
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { io } from 'socket.io-client';
import UploadDoc from './UploadDoc';
import ProjectForm from './ProjectForm';
// ⭐ Client & Findings/Issues dashboard module
import ClientsPanel from './ClientsPanel';
import AnalyzeSOCButton from './buttons/AnalyzeSOCButton';
import AnalyzeRegulatoryButton from './buttons/AnalyzeRegulatoryButton';
import ClassifyComplianceButton from './buttons/ClassifyComplianceButton';
import WorkflowAssignmentButton from './buttons/WorkflowAssignmentButton';
import SamplingButton from './buttons/SamplingButton';
import UploadFinancialDataButton from './buttons/UploadFinancialDataButton';
import ExtractControlObjectivesButton from './buttons/ExtractControlObjectivesButton';
import FormulateTestingAttributesButton from './buttons/FormulateTestingAttributesButton';
import UploadPopulationButton from './buttons/UploadPopulationButton';
import ConductTestingButton from './buttons/ConductTestingButton';
import ConductComplianceTestingButton from './buttons/ConductComplianceTestingButton';
// Financial Statement Audit buttons
import AnalyzeFinancialStatementButton from './buttons/AnalyzeFinancialStatementButton';
import FormulateAuditProceduresButton from './buttons/FormulateAuditProceduresButton';
import ConductFinancialAuditButton from './buttons/ConductFinancialAuditButton';
import ExtractBalancesButton from './buttons/ExtractBalancesButton';
import AssessMaterialityButton from './buttons/AssessMaterialityButton';
import UploadTrialBalanceButton from './buttons/UploadTrialBalanceButton';
import UploadIncomeStatementButton from './buttons/UploadIncomeStatementButton'; // ✅ ADDED
import UploadBankStatementsButton from './buttons/UploadBankStatementsButton'; // ✅ BANK STATEMENTS
import UploadBankReconciliationButton from './buttons/UploadBankReconciliationButton'; // ✅ BANK RECONCILIATION
// ⭐ NEW: External Confirmation Buttons
import UploadBankConfirmationButton from './buttons/UploadBankConfirmationButton'; // ✅ BANK CONFIRMATIONS
import UploadARConfirmationButton from './buttons/UploadARConfirmationButton'; // ✅ AR CONFIRMATIONS
import UploadAPConfirmationButton from './buttons/UploadAPConfirmationButton'; // ✅ AP CONFIRMATIONS
import UploadInventoryConfirmationButton from './buttons/UploadInventoryConfirmationButton'; // ✅ INVENTORY CONFIRMATIONS
import UploadInventoryObservationButton from './buttons/UploadInventoryObservationButton'; // ✅ PHYSICAL INVENTORY
import TestAssertionsButton from './buttons/TestAssertionsButton';
import ConductSubstantiveTestingButton from './buttons/ConductSubstantiveTestingButton';
import GenerateFindingsButton from './buttons/GenerateFindingsButton';
import GenerateWorkpaperButton from './buttons/GenerateWorkpaperButton';
import GenerateAuditorsReportButton from './buttons/GenerateAuditorsReportButton';
// ⭐ RESTORED: Supporting Documents (generic upload — all 23 audit doc types)
import UploadSupportingDocumentButton from './buttons/UploadSupportingDocumentButton';
// Audit drawer UI
import AuditLogDrawer from './AuditLogDrawer';
// New panels
import ProjectMembersPanel from './ProjectMembersPanel';
import TaskBoard from './TaskBoard';
// ⭐ Human-in-the-Loop Dual-Track Audit System (Feature 3)
import HumanFindingsPanel from './HumanFindingsPanel';
import FindingsComparisonView from './FindingsComparisonView';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

// Normalize Socket target (strip '/api')
const SOCKET_URL = (() => {
  try {
    const u = new URL(API_URL);
    return `${u.protocol}//${u.host}`;
  } catch {
    return API_URL.replace(/\/api\/?$/, '');
  }
})();

/** ------------------------------------------------------------------
 * Events - Complete list including Financial Audit events
 * ----------------------------------------------------------------- */
const STEP_EVENT_TYPES = new Set([
  'classification_complete',
  'workflow_assigned',
  'soc_analysis_complete',
  'regulatory_analysis_complete',
  'financial_data_uploaded',
  'compliance_testing_queued',
  'compliance_testing_complete',
  'objectives_extraction_complete',
  'formulate_attributes_complete',
  'testing_complete',
  'workflow_steps_synced',
  'test_broadcast',
  // Financial Statement Audit events
  'financial_analysis_complete',
  'balance_extraction_complete',
  'balances_extracted',
  'extract_balances_complete',
  'materiality_assessed',
  'assess_materiality_complete',
  'trial_balance_uploaded',
  'upload_trial_balance_complete',
  'income_statement_uploaded', // ✅ ADDED
  'upload_income_statement_complete', // ✅ ADDED
  'bank_statements_uploaded', // ✅ BANK STATEMENTS
  'upload_bank_statements_complete', // ✅ BANK STATEMENTS
  'bank_reconciliation_uploaded', // ✅ BANK RECONCILIATION
  'upload_bank_reconciliation_complete', // ✅ BANK RECONCILIATION
  // ⭐ NEW: External Confirmation events
  'bank_confirmations_uploaded', // ✅ BANK CONFIRMATIONS
  'upload_bank_confirmations_complete', // ✅ BANK CONFIRMATIONS  
  'ar_confirmations_uploaded', // ✅ AR CONFIRMATIONS
  'upload_ar_confirmations_complete', // ✅ AR CONFIRMATIONS
  'ap_confirmations_uploaded', // ✅ AP CONFIRMATIONS
  'upload_ap_confirmations_complete', // ✅ AP CONFIRMATIONS
  'inventory_confirmations_uploaded', // ✅ INVENTORY CONFIRMATIONS
  'upload_inventory_confirmations_complete', // ✅ INVENTORY CONFIRMATIONS
  'inventory_observation_uploaded', // ✅ PHYSICAL INVENTORY
  'upload_inventory_observation_complete', // ✅ PHYSICAL INVENTORY
  'assertions_tested',
  'assertion_testing_complete',
  'test_assertions_complete',
  'substantive_testing_complete',
  'substantive_testing_queued',
  'audit_procedures_formulated',
  'formulate_procedures_complete',
  'financial_audit_testing_complete',
  'financial_audit_queued',
  'findings_generated',
  'workpapers_generated',
  'supporting_document_uploaded',      // ✅ supporting docs upload
  'workflow_reset_for_retesting',      // ✅ downstream reset after supporting doc upload
]);

// Map runtime events to workflow step IDs
const STEP_ALIASES = {
  classification_complete: ['classification', 'classify'],
  workflow_assigned: ['assign_workflow', 'workflow_assignment'],
  soc_analysis_complete: ['soc_analysis', 'analyze_soc'],
  regulatory_analysis_complete: ['regulatory_analysis', 'analyze_regulatory'],
  financial_data_uploaded: ['upload_financial', 'upload_financial_data'],
  compliance_testing_queued: ['compliance_testing', 'conduct_compliance', 'fws_compliance'],
  compliance_testing_complete: ['compliance_testing', 'conduct_compliance', 'fws_compliance'],
  objectives_extraction_complete: ['extract_objectives', 'control_objectives', 'extract_control_objectives'],
  formulate_attributes_complete: ['formulate_attributes', 'formulate_testing_attributes'],
  testing_complete: ['conduct_testing', 'testing'],
  // Financial Statement Audit aliases
  financial_analysis_complete: ['analyze_financial_statement', 'fs_analysis'],
  balance_extraction_complete: ['extract_balances'],
  balances_extracted: ['extract_balances'],
  extract_balances_complete: ['extract_balances'],
  materiality_assessed: ['assess_materiality'],
  assess_materiality_complete: ['assess_materiality'],
  trial_balance_uploaded: ['upload_trial_balance'],
  upload_trial_balance_complete: ['upload_trial_balance'],
  income_statement_uploaded: ['upload_income_statement', 'income_statement', 'income_statement_upload'], // ✅ ADDED
  upload_income_statement_complete: ['upload_income_statement', 'income_statement', 'income_statement_upload'], // ✅ ADDED
  bank_statements_uploaded: ['upload_bank_statements', 'bank_statements', 'bank_statement_upload'], // ✅ BANK STATEMENTS
  upload_bank_statements_complete: ['upload_bank_statements', 'bank_statements', 'bank_statement_upload'], // ✅ BANK STATEMENTS
  bank_reconciliation_uploaded: ['upload_bank_reconciliation', 'bank_reconciliation', 'bank_rec'], // ✅ BANK RECONCILIATION
  upload_bank_reconciliation_complete: ['upload_bank_reconciliation', 'bank_reconciliation', 'bank_rec'], // ✅ BANK RECONCILIATION
  // ⭐ NEW: External Confirmation aliases
  bank_confirmations_uploaded: ['upload_bank_confirmations', 'bank_confirmations'],
  upload_bank_confirmations_complete: ['upload_bank_confirmations', 'bank_confirmations'],
  ar_confirmations_uploaded: ['upload_ar_confirmations', 'ar_confirmations'],
  upload_ar_confirmations_complete: ['upload_ar_confirmations', 'ar_confirmations'],
  ap_confirmations_uploaded: ['upload_ap_confirmations', 'ap_confirmations'],
  upload_ap_confirmations_complete: ['upload_ap_confirmations', 'ap_confirmations'],
  inventory_confirmations_uploaded: ['upload_inventory_confirmations', 'inventory_confirmations'],
  upload_inventory_confirmations_complete: ['upload_inventory_confirmations', 'inventory_confirmations'],
  inventory_observation_uploaded: ['upload_inventory_observation', 'inventory_observation'],
  upload_inventory_observation_complete: ['upload_inventory_observation', 'inventory_observation'],
  assertions_tested: ['test_assertions'],
  test_assertions_complete: ['test_assertions'],
  assertion_testing_complete:  ['test_assertions'],
  substantive_testing_complete: ['conduct_substantive_testing', 'substantive_testing', 'substantive_tests'],
  substantive_testing_queued: ['conduct_substantive_testing', 'substantive_testing', 'substantive_tests'],
  audit_procedures_formulated: ['formulate_audit_procedures', 'audit_procedures'],
  formulate_procedures_complete: ['formulate_audit_procedures'],
  financial_audit_testing_complete: ['conduct_audit_testing', 'fs_testing'],
  financial_audit_queued: ['conduct_audit_testing'],
  findings_generated: ['generate_findings'],
  workpapers_generated: ['generate_workpapers'],
};

/** ------------------------------------------------------------------
 * Small helpers
 * ----------------------------------------------------------------- */
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleString();
};

const formatFileSize = (bytes) => {
  if (!bytes || typeof bytes !== 'number') return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const isPDF = (d) =>
  String(d.mimeType || d.mime || d.originalName || '').toLowerCase().includes('pdf') ||
  String(d.originalName || '').match(/\.pdf$/i);

// Deep merge utility to preserve nested objects
const deepMerge = (target, source) => {
  const result = { ...target };
  
  for (const key in source) {
    if (source.hasOwnProperty(key)) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        result[key] = deepMerge(target[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    }
  }
  
  return result;
};

/** ------------------------------------------------------------------
 * Formatted Renderers for Audit Results
 * ----------------------------------------------------------------- */

/** Render Document Classification */
const renderClassification = (data) => {
  if (!data) return <div>No classification data available.</div>;
  const classification = data.classification || 'Unknown';
  const details = data.details || {};

  return (
    <div>
      <div style={summaryBox}>
        <h4 style={{ margin: '0 0 8px 0' }}>Document Classification</h4>
        <div style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 8 }}>
          Classification{' '}
          <span
            style={{
              ...pill,
              fontSize: 14,
              padding: '4px 12px',
              background:
                classification === 'SOC Report'
                  ? '#f0fdf4'
                  : classification === 'Regulatory Document'
                  ? '#f0f9ff'
                  : classification === 'Financial Statement'
                  ? '#fef3c7'
                  : '#fef2f2',
              borderColor:
                classification === 'SOC Report'
                  ? '#22c55e'
                  : classification === 'Regulatory Document'
                  ? '#0ea5e9'
                  : classification === 'Financial Statement'
                  ? '#f59e0b'
                  : '#fca5a5',
              color:
                classification === 'SOC Report'
                  ? '#15803d'
                  : classification === 'Regulatory Document'
                  ? '#0369a1'
                  : classification === 'Financial Statement'
                  ? '#92400e'
                  : '#991b1b'
            }}
          >
            {classification}
          </span>
        </div>
        {details.confidence && (
          <div>
            <strong>Confidence:</strong> {Math.round(details.confidence * 100)}%
          </div>
        )}
        {details.document_type && <div><strong>Document Type:</strong> {details.document_type}</div>}
        {details.subtype && <div><strong>Subtype:</strong> {details.subtype}</div>}
      </div>
      {details.key_indicators && Array.isArray(details.key_indicators) && details.key_indicators.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4>Key Indicators</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {details.key_indicators.map((indicator, i) => (
              <span
                key={i}
                style={{
                  ...pill,
                  fontSize: 10,
                  background: '#f0fdf4',
                  borderColor: '#86efac',
                  color: '#15803d'
                }}
              >
                ✓ {indicator}
              </span>
            ))}
          </div>
        </div>
      )}
      {details.reasoning && (
        <div style={{ marginTop: 12 }}>
          <h4>Classification Reasoning</h4>
          <div style={{ ...controlCard, fontSize: 12 }}>{details.reasoning}</div>
        </div>
      )}
      {details.recommended_workflow && (
        <div style={{ marginTop: 12 }}>
          <h4>Recommended Workflow</h4>
          <div style={{ ...controlCard, fontSize: 12, fontWeight: 'bold', color: '#3b82f6' }}>
            {details.recommended_workflow}
          </div>
        </div>
      )}
    </div>
  );
};

/** Render SOC Analysis Findings */
const renderSOCAnalysis = (data) => {
  if (!data) return <div>No SOC analysis data available.</div>;

  return (
    <div>
      <div style={summaryBox}>
        <h4 style={{ margin: '0 0 8px 0' }}>SOC Analysis Summary</h4>
        {data.report_type && <div><strong>Report Type:</strong> {data.report_type}</div>}
        {data.service_organization && <div><strong>Organization:</strong> {data.service_organization}</div>}
        {data.report_period && <div><strong>Period:</strong> {data.report_period}</div>}
        {data.trust_services_criteria && (
          <div>
            <strong>Trust Services Criteria:</strong>{' '}
            {Array.isArray(data.trust_services_criteria)
              ? data.trust_services_criteria.join(', ')
              : data.trust_services_criteria}
          </div>
        )}
        {data.control_objectives && Array.isArray(data.control_objectives) && (
          <div><strong>Control Objectives Found:</strong> {data.control_objectives.length}</div>
        )}
      </div>
      {data.control_objectives && Array.isArray(data.control_objectives) && data.control_objectives.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4>Control Objectives ({data.control_objectives.length})</h4>
          {data.control_objectives.map((obj, i) => {
            const objectiveId = obj.id || obj.objective_id || `Objective ${i + 1}`;
            const objectiveText =
              obj.title ||
              obj.description ||
              obj.objective ||
              obj.text ||
              obj.control_objective ||
              obj.name ||
              (typeof obj === 'string' ? obj : null);
            const category = obj.category || obj.trust_service_category || obj.criteria;

            if (!objectiveText) return null;

            return (
              <div key={i} style={controlCard}>
                <div style={{ fontWeight: 'bold', fontSize: 12, marginBottom: 4 }}>{objectiveId}</div>
                <div style={{ fontSize: 11, marginBottom: 4, lineHeight: '1.4' }}>{objectiveText}</div>
                {category && <span style={{ ...pill, fontSize: 10 }}>{category}</span>}
              </div>
            );
          }).filter(Boolean)}
        </div>
      )}
      {data.soc_controls && Array.isArray(data.soc_controls) && data.soc_controls.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4>SOC Controls ({data.soc_controls.length})</h4>
          {data.soc_controls.slice(0, 5).map((control, i) => (
            <div key={i} style={controlCard}>
              <div style={{ fontWeight: 'bold', fontSize: 12, marginBottom: 4 }}>
                {control.control_id || control.id || `Control ${i + 1}`}
              </div>
              <div style={{ fontSize: 11, marginBottom: 4 }}>
                {control.description || control.title || control.control_description}
              </div>
              {control.control_activity && (
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                  <strong>Activity:</strong> {control.control_activity}
                </div>
              )}
            </div>
          ))}
          {data.soc_controls.length > 5 && (
            <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
              +{data.soc_controls.length - 5} more controls…
            </div>
          )}
        </div>
      )}
      {data.exceptions && Array.isArray(data.exceptions) && data.exceptions.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4 style={{ color: '#dc2626' }}>⚠️ Exceptions ({data.exceptions.length})</h4>
          {data.exceptions.map((exc, i) => (
            <div key={i} style={{ ...controlCard, borderColor: '#fca5a5', background: '#fef2f2' }}>
              <div style={{ fontSize: 11, color: '#991b1b' }}>
                {exc.description || exc.title || exc.exception || (typeof exc === 'string' ? exc : 'Exception found')}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** Render Control Objectives */
const renderControlObjectives = (data) => {
  if (!data) return <div>No control objectives data available.</div>;
  const objectives = data.objectives || data.control_objectives || (Array.isArray(data) ? data : []);

  return (
    <div>
      <div style={summaryBox}>
        <h4 style={{ margin: '0 0 8px 0' }}>Control Objectives Extracted</h4>
        <div><strong>Total Objectives:</strong> {objectives.length}</div>
      </div>
      {objectives.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {objectives.map((obj, i) => (
            <div key={i} style={controlCard}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 4
                }}
              >
                <div style={{ fontWeight: 'bold', fontSize: 12 }}>
                  {obj.id || obj.objective_id || `CO-${i + 1}`}
                </div>
                {obj.criticality && (
                  <span
                    style={{
                      ...pill,
                      fontSize: 10,
                      background: obj.criticality === 'High' ? '#fef2f2' : '#f0f9ff',
                      borderColor: obj.criticality === 'High' ? '#fca5a5' : '#bfdbfe',
                      color: obj.criticality === 'High' ? '#991b1b' : '#1e40af'
                    }}
                  >
                    {obj.criticality}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, marginBottom: 6 }}>
                {obj.description || obj.objective || obj.text}
              </div>
              {obj.control_activities && Array.isArray(obj.control_activities) && (
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4 }}>
                  <strong>Activities:</strong> {obj.control_activities.length} control(s)
                </div>
              )}
              {obj.testing_procedures && (
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                  <strong>Testing:</strong> {obj.testing_procedures}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** Render Testing Attributes */
const renderTestingAttributes = (data) => {
  if (!data) return <div>No testing attributes data available.</div>;
  const attributes = data.attributes || data.testing_attributes || (Array.isArray(data) ? data : []);

  return (
    <div>
      <div style={summaryBox}>
        <h4 style={{ margin: '0 0 8px 0' }}>Testing Attributes Defined</h4>
        <div><strong>Total Attributes:</strong> {attributes.length}</div>
        {data.sampling_methodology && (
          <div><strong>Sampling Method:</strong> {data.sampling_methodology}</div>
        )}
      </div>
      {attributes.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {attributes.map((attr, i) => (
            <div key={i} style={controlCard}>
              <div style={{ fontWeight: 'bold', fontSize: 12, marginBottom: 4 }}>
                {attr.attribute_name || attr.name || `Attribute ${i + 1}`}
              </div>
              {attr.description && (
                <div style={{ fontSize: 11, marginBottom: 6 }}>{attr.description}</div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                {attr.data_type && <span style={{ ...pill, fontSize: 10 }}>Type: {attr.data_type}</span>}
                {attr.sample_size && <span style={{ ...pill, fontSize: 10 }}>Sample: {attr.sample_size}</span>}
                {attr.expected_value && <span style={{ ...pill, fontSize: 10 }}>Expected: {attr.expected_value}</span>}
              </div>
              {attr.testing_procedure && (
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4 }}>
                  <strong>Procedure:</strong> {attr.testing_procedure}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** Render Population Data */
const renderPopulationData = (data) => {
  return (
    <div>
      <div style={summaryBox}>
        <h4 style={{ margin: '0 0 8px 0' }}>Population Data Summary</h4>
        <div><strong>File:</strong> {data?.originalName || data?.fileName || 'Unknown'}</div>
        <div><strong>Records:</strong> {data?.rowCount?.toLocaleString?.() || 0}</div>
        <div><strong>Size:</strong> {formatFileSize(data?.size || 0)}</div>
        <div><strong>Uploaded:</strong> {fmtDate(data?.uploadedAt)}</div>
      </div>
      {data?.columns && Array.isArray(data.columns) && (
        <div style={{ marginTop: 12 }}>
          <h4>Data Columns ({data.columns.length})</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {data.columns.map((col, i) => (
              <span key={i} style={{ ...pill, fontSize: 10 }}>{col}</span>
            ))}
          </div>
        </div>
      )}
      {data?.sample_preview && Array.isArray(data.sample_preview) && data.sample_preview.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4>Sample Records (First {Math.min(data.sample_preview.length, 3)})</h4>
          <div style={{ fontSize: 10, overflowX: 'auto' }}>
            {data.sample_preview.slice(0, 3).map((record, i) => (
              <div key={i} style={{ ...controlCard, marginBottom: 6 }}>
                <strong>Record {i + 1}:</strong>
                <pre style={{ margin: '4px 0 0 0', fontSize: 10 }}>
                  {JSON.stringify(record, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/** Normalize SOC/CUEC testing payloads */
const normalizeTestingData = (dataIn) => {
  if (!dataIn) return { results: [], summary: {} };

  const data = typeof dataIn === 'string' ? (() => {
    try {
      return JSON.parse(dataIn);
    } catch {
      return {};
    }
  })() : dataIn;

  let results =
    data.control_test_results ||
    data.test_results ||
    data.results ||
    data.tests ||
    data.items ||
    data.findings ||
    data.output?.results ||
    data.payload?.results ||
    data.details?.tests ||
    [];

  if (!Array.isArray(results) && results && typeof results === 'object') {
    results = Object.entries(results).map(([k, v]) => ({ test_id: k, ...(v || {}) }));
  }

  if (Array.isArray(data) && !Array.isArray(results)) results = data;

  const norm = (t, idx) => {
    const failedNum = Number(t.tests_failed ?? t.failed ?? 0);
    const performedNum = Number(t.tests_performed ?? t.performed ?? t.count ?? 0);

    let result = failedNum > 0 ? 'Fail' : performedNum > 0 ? 'Pass' : undefined;

    if (result === undefined) {
      const raw = t.result ?? t.status ?? (t.passed === true ? 'Pass' : (t.passed === false ? 'Fail' : undefined)) ?? t.conclusion;
      if (typeof raw === 'string') {
        if (/^pass(ed)?$/i.test(raw) || /^effective$/i.test(raw)) result = 'Pass';
        else if (/^fail(ed)?$/i.test(raw) || /ineffective|partially[_\s]?effective/i.test(raw)) result = 'Fail';
        else result = raw;
      } else if (raw === true) result = 'Pass';
      else if (raw === false) result = 'Fail';
    }

    if (!result) result = '—';

    let exceptions = [];
    if (Array.isArray(t.exceptions)) exceptions = t.exceptions;
    else if (t.exceptions) exceptions = [t.exceptions];
    else if (Array.isArray(t.findings)) exceptions = t.findings;

    return {
      test_id: t.test_id || t.id || t.control_id || t.objective_id || t.name || `Test ${idx + 1}`,
      description: t.description || t.control_name || t.objective || t.testing_attribute || t.control_description || '',
      sample_size: ((t.sample_size ?? t.sampleSize ?? performedNum) || undefined),
      items_tested: ((t.items_tested ?? performedNum) || undefined),
      exceptions,
      result
    };
  };

  const normalizedResults = Array.isArray(results) ? results.map(norm) : [];
  const computedPassed = normalizedResults.filter((r) => r.result === 'Pass').length;
  const computedFailed = normalizedResults.filter((r) => r.result !== 'Pass').length;
  const computedExceptions = normalizedResults.reduce(
    (n, r) => n + (Array.isArray(r.exceptions) ? r.exceptions.length : 0),
    0
  );

  const overall = data.overall_results || data.summary || data.overall || data.metrics || data.stats || {};

  let total_tests = normalizedResults.length;
  const totalFromOverall = Number(overall.total_controls_tested ?? overall.total_tests ?? NaN);
  if (Number.isFinite(totalFromOverall)) total_tests = totalFromOverall;

  let exceptionsValue = computedExceptions;
  const exFromOverall = Number(overall.total_exceptions ?? NaN);
  if (Number.isFinite(exFromOverall)) exceptionsValue = exFromOverall;

  let overall_result = overall.overall_result;
  if (!overall_result && typeof overall.soc_opinion === 'string') {
    overall_result = /unqualified/i.test(overall.soc_opinion) ? 'Pass' : 'Fail';
  }
  if (!overall_result) {
    overall_result = computedFailed > 0 ? 'Fail' : (total_tests > 0 ? 'Pass' : '—');
  }

  const summary = {
    total_tests,
    passed: computedPassed,
    failed: computedFailed,
    exceptions: exceptionsValue,
    overall_result
  };

  return { results: normalizedResults, summary };
};

/** Render Testing Results (SOC/CUEC) */
const renderTestingResults = (data) => {
  if (!data) return <div>No testing results available.</div>;
  const { results, summary } = normalizeTestingData(data);

  return (
    <div>
      <div style={summaryBox}>
        <h4 style={{ margin: '0 0 8px 0' }}>SOC/CUEC Testing Results</h4>
        <div><strong>Total Tests:</strong> {summary.total_tests || results.length || 0}</div>
        <div><strong>Passed:</strong> <span style={{ color: '#059669' }}>{summary.passed || 0}</span></div>
        <div><strong>Failed:</strong> <span style={{ color: '#dc2626' }}>{summary.failed || 0}</span></div>
        {summary.exceptions !== undefined && (
          <div><strong>Exceptions:</strong> <span style={{ color: '#f59e0b' }}>{summary.exceptions}</span></div>
        )}
        {summary.overall_result && (
          <div style={{ marginTop: 8 }}>
            <strong>Overall Result:</strong>{' '}
            <span
              style={{
                ...pill,
                background: summary.overall_result === 'Pass' ? '#f0fdf4' : '#fef2f2',
                borderColor: summary.overall_result === 'Pass' ? '#22c55e' : '#fca5a5',
                color: summary.overall_result === 'Pass' ? '#15803d' : '#991b1b'
              }}
            >
              {summary.overall_result}
            </span>
          </div>
        )}
      </div>
      {results.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4>Test Details</h4>
          {results.slice(0, 10).map((test, i) => (
            <div
              key={i}
              style={{
                ...controlCard,
                borderColor: test.result === 'Pass' || test.status === 'passed' ? '#86efac' : '#fca5a5',
                background: test.result === 'Pass' || test.status === 'passed' ? '#f0fdf4' : '#fef2f2'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 4
                }}
              >
                <div style={{ fontWeight: 'bold', fontSize: 12 }}>
                  {test.test_id || test.objective_id || `Test ${i + 1}`}
                </div>
                <span
                  style={{
                    ...pill,
                    fontSize: 10,
                    background: test.result === 'Pass' || test.status === 'passed' ? '#d1fae5' : '#fee2e2',
                    borderColor: test.result === 'Pass' || test.status === 'passed' ? '#22c55e' : '#dc2626',
                    color: test.result === 'Pass' || test.status === 'passed' ? '#065f46' : '#991b1b'
                  }}
                >
                  {test.result || test.status}
                </span>
              </div>
              {test.description && <div style={{ fontSize: 11, marginBottom: 4 }}>{test.description}</div>}
              {test.sample_size && (
                <div style={{ fontSize: 10, color: '#6b7280' }}>
                  Sample Size: {test.sample_size}
                  {test.items_tested ? ` | Tested: ${test.items_tested}` : null}
                </div>
              )}
              {test.exceptions && test.exceptions.length > 0 && (
                <div style={{ fontSize: 10, color: '#991b1b', marginTop: 4 }}>
                  <strong>Exceptions:</strong> {test.exceptions.length}
                  {test.exceptions.slice(0, 2).map((exc, j) => (
                    <div key={j} style={{ marginLeft: 8, marginTop: 2 }}>• {exc.description || exc}</div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {results.length > 10 && (
            <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
              +{results.length - 10} more test results…
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const renderRegulatory = (data) => (
  <div>
    <div style={summaryBox}>
      <h4>Regulatory Analysis Summary</h4>
      <div>Compliance Rules: {data?.compliance_rules?.length || 0}</div>
      <div>Fund Restrictions: {data?.fund_restrictions?.length || 0}</div>
    </div>
    {Array.isArray(data?.compliance_rules) && data.compliance_rules.length > 0 && (
      <div style={{ marginTop: 12 }}>
        <h4>Compliance Rules</h4>
        {data.compliance_rules.slice(0, 5).map((rule, i) => (
          <div key={i} style={controlCard}>
            <div style={{ fontWeight: 'bold', fontSize: 12 }}>{rule.rule_id}</div>
            <div style={{ fontSize: 11, marginTop: 4 }}>{rule.requirement}</div>
            <div style={{ fontSize: 10, color: '#666', marginTop: 2 }}>{rule.citation}</div>
          </div>
        ))}
        {data.compliance_rules.length > 5 && (
          <div style={{ fontSize: 12, color: '#666' }}>
            +{data.compliance_rules.length - 5} more rules…
          </div>
        )}
      </div>
    )}
  </div>
);

const renderFinancial = (data) => (
  <div>
    <div style={summaryBox}>
      <h4>Financial Data Summary</h4>
      <div>File: {data?.originalName || data?.fileName || data?.storedFilename || 'Unknown'}</div>
      <div>Rows: {data?.rowCount?.toLocaleString?.() || 0}</div>
      <div>Size: {formatFileSize(data?.size || 0)}</div>
      <div>Uploaded: {fmtDate(data?.uploadedAt)}</div>
    </div>
  </div>
);

const renderCompliance = (data) => (
  <div>
    <div style={summaryBox}>
      <h4>FWS WSFR Compliance Testing Summary</h4>
      <div>Total Violations: {data?.overall_compliance?.total_violations ?? '—'}</div>
      <div>Compliance Status: {data?.overall_compliance?.compliance_status ?? '—'}</div>
      <div>Audit Opinion: {data?.overall_compliance?.audit_opinion ?? '—'}</div>
    </div>
  </div>
);

/** Render Financial Statement Analysis */
const renderFinancialStatementAnalysis = (data) => {
  if (!data) return <div>No financial statement analysis available.</div>;

  return (
    <div>
      <div style={summaryBox}>
        <h4 style={{ margin: '0 0 8px 0' }}>Financial Statement Analysis</h4>
        {data.statement_type && <div><strong>Statement Type:</strong> {data.statement_type.replace('_', ' ')}</div>}
        {data.statement_date && <div><strong>Statement Date:</strong> {data.statement_date}</div>}
        {data.governmental_unit && <div><strong>Entity:</strong> {data.governmental_unit}</div>}
        {data.total_cash !== undefined && <div><strong>Total Cash:</strong> ${data.total_cash.toLocaleString()}</div>}
        {data.total_restricted_cash !== undefined && <div><strong>Restricted Cash:</strong> ${data.total_restricted_cash.toLocaleString()}</div>}
      </div>
      {data.cash_accounts && Array.isArray(data.cash_accounts) && data.cash_accounts.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4>Cash Accounts ({data.cash_accounts.length})</h4>
          {data.cash_accounts.map((acct, i) => (
            <div key={i} style={controlCard}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 4
                }}
              >
                <div style={{ fontWeight: 'bold', fontSize: 12 }}>{acct.account_name}</div>
                <span style={{ ...pill, fontSize: 10 }}>{acct.classification}</span>
              </div>
              {acct.account_number && <div style={{ fontSize: 11 }}>Account: {acct.account_number}</div>}
              <div style={{ fontSize: 13, fontWeight: 'bold', marginTop: 4 }}>
                Balance: ${(acct.balance || 0).toLocaleString()}
              </div>
              {acct.financial_institution && (
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                  Bank: {acct.financial_institution}
                </div>
              )}
              {acct.restrictions && (
                <div style={{ fontSize: 10, color: '#f59e0b', marginTop: 2 }}>
                  ⚠️ Restrictions: {acct.restrictions}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {data.significant_accounts && Array.isArray(data.significant_accounts) && data.significant_accounts.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4>Significant Accounts</h4>
          {data.significant_accounts.map((acct, i) => (
            <div key={i} style={{ ...controlCard, borderColor: '#fca5a5', background: '#fef2f2' }}>
              <div style={{ fontWeight: 'bold', fontSize: 12 }}>{acct.account}</div>
              <div style={{ fontSize: 13, marginTop: 2 }}>Balance: ${(acct.balance || 0).toLocaleString()}</div>
              <div style={{ fontSize: 11, color: '#991b1b', marginTop: 4 }}>{acct.reason}</div>
            </div>
          ))}
        </div>
      )}
      {data.audit_considerations && Array.isArray(data.audit_considerations) && data.audit_considerations.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4>Audit Considerations</h4>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12 }}>
            {data.audit_considerations.map((consideration, i) => (
              <li key={i} style={{ marginBottom: 4 }}>{consideration}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/** Render Audit Procedures */
const renderAuditProcedures = (data) => {
  if (!data) return <div>No audit procedures available.</div>;
  const procedures = data.audit_procedures || [];

  return (
    <div>
      <div style={summaryBox}>
        <h4 style={{ margin: '0 0 8px 0' }}>Audit Procedures Formulated</h4>
        <div><strong>Total Procedures:</strong> {procedures.length}</div>
      </div>
      {procedures.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4>Procedures</h4>
          {procedures.map((proc, i) => (
            <div key={i} style={controlCard}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 4
                }}
              >
                <div style={{ fontWeight: 'bold', fontSize: 12 }}>
                  {proc.procedure_id || `AP-${i + 1}`}: {proc.procedure_name}
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <span style={{ ...pill, fontSize: 10 }}>{proc.assertion}</span>
                  <span style={{ ...pill, fontSize: 10 }}>{proc.procedure_type}</span>
                </div>
              </div>
              {proc.description && (
                <div style={{ fontSize: 11, marginBottom: 6 }}>{proc.description}</div>
              )}
              {proc.steps && Array.isArray(proc.steps) && proc.steps.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  <strong style={{ fontSize: 10 }}>Steps:</strong>
                  <ol style={{ margin: '4px 0 0 0', paddingLeft: 20, fontSize: 10 }}>
                    {proc.steps.map((step, j) => (
                      <li key={j}>{step}</li>
                    ))}
                  </ol>
                </div>
              )}
              {proc.sample_size && (
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4 }}>
                  Sample Size: {proc.sample_size}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {data.confirmations_required && Array.isArray(data.confirmations_required) && data.confirmations_required.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4>Confirmations Required ({data.confirmations_required.length})</h4>
          {data.confirmations_required.map((conf, i) => (
            <div key={i} style={{ ...controlCard, background: '#f0f9ff' }}>
              <div style={{ fontWeight: 'bold', fontSize: 12 }}>{conf.account}</div>
              <div style={{ fontSize: 11, marginTop: 2 }}>Bank: {conf.financial_institution}</div>
              <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                Type: {conf.confirmation_type} | Items: {conf.items_to_confirm?.join(', ')}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** Render Financial Audit Results */
const renderFinancialAuditResults = (data) => {
  if (!data) return <div>No audit testing results available.</div>;
  const testResults = data.test_results || [];
  const overall = data.overall_results || {};

  return (
    <div>
      <div style={summaryBox}>
        <h4 style={{ margin: '0 0 8px 0' }}>Financial Audit Testing Results</h4>
        <div><strong>Procedures Performed:</strong> {overall.total_procedures_performed || testResults.length}</div>
        <div><strong>Passed:</strong> <span style={{ color: '#059669' }}>{overall.procedures_passed || 0}</span></div>
        <div><strong>Failed:</strong> <span style={{ color: '#dc2626' }}>{overall.procedures_failed || 0}</span></div>
        <div><strong>Total Exceptions:</strong> <span style={{ color: '#f59e0b' }}>{overall.total_exceptions || 0}</span></div>
        {overall.overall_conclusion && (
          <div style={{ marginTop: 8 }}>
            <strong>Overall Conclusion:</strong>{' '}
            <span
              style={{
                ...pill,
                background: overall.overall_conclusion === 'Unqualified' ? '#f0fdf4' : '#fef2f2',
                borderColor: overall.overall_conclusion === 'Unqualified' ? '#22c55e' : '#fca5a5',
                color: overall.overall_conclusion === 'Unqualified' ? '#15803d' : '#991b1b'
              }}
            >
              {overall.overall_conclusion}
            </span>
          </div>
        )}
      </div>
      {testResults.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4>Test Results</h4>
          {testResults.map((test, i) => (
            <div
              key={i}
              style={{
                ...controlCard,
                borderColor: test.conclusion === 'Pass' ? '#86efac' : '#fca5a5',
                background: test.conclusion === 'Pass' ? '#f0fdf4' : '#fef2f2'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 4
                }}
              >
                <div style={{ fontWeight: 'bold', fontSize: 12 }}>
                  {test.procedure_id}: {test.procedure_name}
                </div>
                <span
                  style={{
                    ...pill,
                    fontSize: 10,
                    background: test.conclusion === 'Pass' ? '#d1fae5' : '#fee2e2',
                    borderColor: test.conclusion === 'Pass' ? '#22c55e' : '#dc2626',
                    color: test.conclusion === 'Pass' ? '#065f46' : '#991b1b'
                  }}
                >
                  {test.conclusion}
                </span>
              </div>
              <div style={{ fontSize: 11, marginBottom: 4 }}>
                Tests: {test.tests_performed || 0} performed, {test.tests_passed || 0} passed, {test.tests_failed || 0} failed
              </div>
              {test.evidence_obtained && (
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4 }}>
                  <strong>Evidence:</strong> {test.evidence_obtained}
                </div>
              )}
              {test.exceptions && Array.isArray(test.exceptions) && test.exceptions.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <strong style={{ fontSize: 10, color: '#dc2626' }}>Exceptions ({test.exceptions.length}):</strong>
                  {test.exceptions.map((exc, j) => (
                    <div
                      key={j}
                      style={{
                        ...controlCard,
                        marginTop: 4,
                        borderColor: '#fca5a5',
                        background: '#fef2f2'
                      }}
                    >
                      <div style={{ fontSize: 10, fontWeight: 'bold' }}>{exc.exception_type}</div>
                      <div style={{ fontSize: 10, marginTop: 2 }}>{exc.description}</div>
                      {exc.amount && <div style={{ fontSize: 10, marginTop: 2 }}>Amount: ${exc.amount.toLocaleString()}</div>}
                      <div style={{ fontSize: 9, color: '#991b1b', marginTop: 2 }}>
                        Severity: {exc.severity} | Recommendation: {exc.recommendation}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {data.follow_up_procedures && Array.isArray(data.follow_up_procedures) && data.follow_up_procedures.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4 style={{ color: '#f59e0b' }}>⚠️ Follow-up Procedures Required</h4>
          {data.follow_up_procedures.map((followUp, i) => (
            <div key={i} style={{ ...controlCard, borderColor: '#fbbf24', background: '#fef3c7' }}>
              <div style={{ fontSize: 11, fontWeight: 'bold' }}>Reason: {followUp.reason}</div>
              <div style={{ fontSize: 10, marginTop: 2 }}>Procedure: {followUp.procedure}</div>
              <div style={{ fontSize: 10, color: '#92400e', marginTop: 2 }}>Urgency: {followUp.urgency}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** Additional FS Audit Renderers */
const renderExtractedBalances = (data) => {
  if (!data) return <div>No balances extracted.</div>;
  const items = data.accounts || data.balances || data.items || [];

  return (
    <div>
      <div style={summaryBox}>
        <h4 style={{ margin: 0 }}>Extracted Balances</h4>
        <div><strong>Total accounts:</strong> {items.length}</div>
      </div>
      {items.slice(0, 12).map((a, i) => (
        <div key={i} style={controlCard}>
          <div style={{ fontWeight: 'bold', fontSize: 12 }}>
            {a.account || a.name || a.account_name || `Account ${i+1}`}
          </div>
          {a.number && <div style={{ fontSize: 11 }}>No: {a.number}</div>}
          <div style={{ fontSize: 13, marginTop: 2 }}>
            Balance: ${(a.balance ?? a.amount ?? 0).toLocaleString()}
          </div>
          {a.classification && <span style={{ ...pill, fontSize: 10, marginTop: 4 }}>{a.classification}</span>}
        </div>
      ))}
      {items.length > 12 && (
        <div style={{ fontSize: 12, color: '#666' }}>+{items.length - 12} more…</div>
      )}
    </div>
  );
};

const renderMateriality = (raw) => {
  if (!raw) return <div>No materiality has been assessed.</div>;

  let data = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch (e) {
      console.warn('[FS AUDIT] Failed to parse materiality JSON', e);
    }
  }

  const auto = data.autoCalculated || {};
  const override = data.auditorOverride || {};

  const overall =
    data.materiality ??
    data.finalMateriality ??
    data.final_materiality ??
    auto.amount ??
    null;

  const performance =
    data.performance_materiality ??
    data.performanceMateriality ??
    null;

  const trivial =
    data.tolerable_misstatement ??
    data.trivialThreshold ??
    data.trivial_threshold ??
    null;

  const assessedAt = data.assessedAt || data.assessed_at;

  const overrideActive = !!(override && override.enabled && override.amount != null);

  let basis =
    data.basis ||
    data.materialityType ||
    null;

  if (!basis) {
    if (overrideActive) {
      basis = 'Auditor override';
    } else if (auto.benchmark || auto.percentage != null) {
      basis = `Auto – ${auto.percentage != null ? `${auto.percentage}% of ` : ''}${auto.benchmark || 'benchmark'}`;
    } else {
      basis = 'Auto';
    }
  }

  return (
    <div>
      <div style={summaryBox}>
        <h4 style={{ margin: 0 }}>Materiality</h4>

        {overall != null && (
          <div>
            <strong>Overall Materiality:</strong>{' '}
            ${Number(overall).toLocaleString()}
          </div>
        )}

        {performance != null && (
          <div>
            <strong>Performance Materiality:</strong>{' '}
            ${Number(performance).toLocaleString()}
          </div>
        )}

        {trivial != null && (
          <div>
            <strong>Trivial Threshold:</strong>{' '}
            ${Number(trivial).toLocaleString()}
          </div>
        )}

        <div>
          <strong>Basis:</strong> {basis}
        </div>

        {assessedAt && (
          <div>
            <strong>Assessed:</strong> {fmtDate(assessedAt)}
          </div>
        )}
      </div>

      {(auto.amount != null || overrideActive || override.justification) && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
          {auto.amount != null && (
            <div style={{ ...controlCard, flex: '1 1 220px', fontSize: 11 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 4 }}>Auto-calculated</div>
              <div>Amount: ${Number(auto.amount).toLocaleString()}</div>
              {auto.benchmark && <div>Benchmark: {auto.benchmark}</div>}
              {auto.percentage != null && <div>Percentage: {auto.percentage}%</div>}
            </div>
          )}

          {(override.enabled || override.amount != null || override.justification) && (
            <div style={{ ...controlCard, flex: '1 1 220px', fontSize: 11 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 4 }}>Auditor override</div>
              <div>Enabled: {override.enabled ? 'Yes' : 'No'}</div>
              {override.amount != null && (
                <div>Override amount: ${Number(override.amount).toLocaleString()}</div>
              )}
              {override.justification && (
                <div style={{ marginTop: 4 }}>Reason: {override.justification}</div>
              )}
            </div>
          )}
        </div>
      )}

      {data.notes && (
        <div style={{ ...controlCard, fontSize: 12, marginTop: 8 }}>
          {data.notes}
        </div>
      )}
    </div>
  );
};

const renderTrialBalance = (data) => {
  if (!data) return <div>No trial balance uploaded.</div>;

  return (
    <div>
      <div style={summaryBox}>
        <h4 style={{ margin: 0 }}>Trial Balance</h4>
        <div><strong>File:</strong> {data.originalName || data.fileName || 'Unknown'}</div>
        <div><strong>Rows:</strong> {data.rowCount?.toLocaleString?.() || '—'}</div>
        <div><strong>Uploaded:</strong> {fmtDate(data.uploadedAt)}</div>
      </div>
      {Array.isArray(data.preview) && data.preview.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <h4>Preview (first {Math.min(3, data.preview.length)})</h4>
          {data.preview.slice(0, 3).map((r, i) => (
            <div key={i} style={controlCard}>
              <pre style={{ margin: 0, fontSize: 10 }}>{JSON.stringify(r, null, 2)}</pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const renderIncomeStatement = (data) => {
  if (!data) return <div>No income statement uploaded.</div>;

  const summary = data.summary || {};

  return (
    <div>
      <div style={summaryBox}>
        <h4 style={{ margin: 0 }}>Income Statement</h4>
        <div><strong>File:</strong> {data.originalName || data.fileName || 'Income Statement'}</div>
        {summary.rowCount && <div><strong>Rows:</strong> {summary.rowCount.toLocaleString()}</div>}
        <div><strong>Uploaded:</strong> {fmtDate(data.uploadedAt || summary?.uploadedAt)}</div>
      </div>
      {(summary.totalRevenue != null || summary.totalExpenses != null || summary.netIncome != null) && (
        <div style={{ marginTop: 12 }}>
          <h4 style={{ margin: '0 0 8px 0' }}>Summary</h4>
          {summary.totalRevenue != null && (
            <div style={{ marginBottom: 4 }}>
              <span style={{ fontWeight: 500 }}>Total Revenue:</span>{' '}
              ${(summary.totalRevenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          )}
          {summary.totalExpenses != null && (
            <div style={{ marginBottom: 4 }}>
              <span style={{ fontWeight: 500 }}>Total Expenses:</span>{' '}
              ${(summary.totalExpenses || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          )}
          {summary.netIncome != null && (
            <div style={{ marginBottom: 4 }}>
              <span style={{ fontWeight: 500 }}>Net Income:</span>{' '}
              <span style={{ color: summary.netIncome >= 0 ? '#059669' : '#dc2626' }}>
                ${(summary.netIncome || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ✅ NEW: Render bank statements with transaction analysis
const renderBankStatements = (data) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return <div>No bank statements uploaded.</div>;
  }

  const totalAccounts = data.length;
  const totalTransactions = data.reduce((sum, bs) => sum + (bs.parsed?.transactionCount || 0), 0);
  const totalFlagged = data.reduce((sum, bs) => sum + (bs.analysis?.totalFlags || 0), 0);
  const combinedBalance = data.reduce((sum, bs) => sum + (bs.parsed?.closingBalance || 0), 0);

  return (
    <div>
      <div style={summaryBox}>
        <h4 style={{ margin: 0 }}>Bank Statements</h4>
        <div><strong>Accounts:</strong> {totalAccounts}</div>
        <div><strong>Total Transactions:</strong> {totalTransactions.toLocaleString()}</div>
        <div><strong>Combined Balance:</strong> ${combinedBalance.toLocaleString()}</div>
        {totalFlagged > 0 && (
          <div style={{ color: '#f59e0b', marginTop: 4 }}>
            <strong>⚠️ Flagged Items:</strong> {totalFlagged}
          </div>
        )}
        <div><strong>Uploaded:</strong> {fmtDate(data[0]?.uploadedAt)}</div>
      </div>

      {data.map((bankStatement, idx) => (
        <div
          key={idx}
          style={{
            ...controlCard,
            marginTop: 12,
            borderLeft: bankStatement.analysis?.totalFlags > 0 ? '4px solid #f59e0b' : '4px solid #10b981'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <div>
              <div style={{ fontWeight: 'bold', fontSize: 14 }}>
                {bankStatement.accountName || 'Bank Account'}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                {bankStatement.bankName} • {bankStatement.accountNumber}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 16, fontWeight: 'bold', color: '#059669' }}>
                ${(bankStatement.parsed?.closingBalance || 0).toLocaleString()}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>Closing Balance</div>
            </div>
          </div>

          {bankStatement.statementPeriod && (
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>
              Period: {new Date(bankStatement.statementPeriod.startDate).toLocaleDateString()} - {new Date(bankStatement.statementPeriod.endDate).toLocaleDateString()}
            </div>
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 12,
              padding: 8,
              background: '#f9fafb',
              borderRadius: 4,
              marginTop: 8
            }}
          >
            <div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>Opening Balance</div>
              <div style={{ fontSize: 13, fontWeight: 'bold' }}>
                ${(bankStatement.parsed?.openingBalance || 0).toLocaleString()}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>Total Deposits</div>
              <div style={{ fontSize: 13, fontWeight: 'bold', color: '#059669' }}>
                ${(bankStatement.parsed?.totalDeposits || 0).toLocaleString()}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>Total Withdrawals</div>
              <div style={{ fontSize: 13, fontWeight: 'bold', color: '#dc2626' }}>
                ${(bankStatement.parsed?.totalWithdrawals || 0).toLocaleString()}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>Transactions</div>
              <div style={{ fontSize: 13, fontWeight: 'bold' }}>
                {bankStatement.parsed?.transactionCount || 0}
              </div>
            </div>
          </div>

          {bankStatement.analysis?.unusualTransactions && bankStatement.analysis.unusualTransactions.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 'bold',
                  color: '#92400e',
                  marginBottom: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                ⚠️ Unusual Transactions Flagged ({bankStatement.analysis.unusualTransactions.length})
              </div>
              {bankStatement.analysis.unusualTransactions.slice(0, 3).map((item, i) => (
                <div
                  key={i}
                  style={{
                    padding: 8,
                    background: '#fef3c7',
                    border: '1px solid #f59e0b',
                    borderRadius: 4,
                    marginBottom: 6,
                    fontSize: 11
                  }}
                >
                  <div style={{ fontWeight: 'bold', marginBottom: 2 }}>
                    {new Date(item.transaction.date).toLocaleDateString()} - ${Math.abs(item.transaction.amount || 0).toLocaleString()}
                  </div>
                  <div style={{ color: '#6b7280', marginBottom: 4 }}>
                    {item.transaction.description}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {item.flags.map((flag, j) => (
                      <span
                        key={j}
                        style={{
                          padding: '2px 6px',
                          borderRadius: 4,
                          fontSize: 9,
                          background:
                            flag.severity === 'high'
                              ? '#fef2f2'
                              : flag.severity === 'medium'
                              ? '#fef3c7'
                              : '#f0f9ff',
                          border: `1px solid ${
                            flag.severity === 'high'
                              ? '#fca5a5'
                              : flag.severity === 'medium'
                              ? '#fbbf24'
                              : '#bfdbfe'
                          }`,
                          color:
                            flag.severity === 'high'
                              ? '#991b1b'
                              : flag.severity === 'medium'
                              ? '#92400e'
                              : '#1e40af'
                        }}
                      >
                        {flag.type.replace(/_/g, ' ').toUpperCase()}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
              {bankStatement.analysis.unusualTransactions.length > 3 && (
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
                  +{bankStatement.analysis.unusualTransactions.length - 3} more flagged transactions...
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ✅ NEW: Bank Reconciliation renderer
const renderBankReconciliation = (data) => {
  if (!data) return <div>No bank reconciliation uploaded.</div>;

  const isReconciled = data.isReconciled || Math.abs(data.difference || 0) < 1;
  const items = data.reconciliationItems || data.items || [];
  const journalEntries = data.journalEntriesNeeded || [];

  return (
    <div>
      <div style={{
        ...summaryBox,
        background: isReconciled ? '#f0fdf4' : '#fef3c7',
        borderColor: isReconciled ? '#22c55e' : '#f59e0b'
      }}>
        <h4 style={{ margin: '0 0 8px 0' }}>Bank Reconciliation</h4>
        <div><strong>Bank Balance:</strong> ${(data.bankBalance || 0).toLocaleString()}</div>
        <div><strong>Book Balance:</strong> ${(data.bookBalance || 0).toLocaleString()}</div>
        <div><strong>Adjusted Bank Balance:</strong> ${(data.adjustedBankBalance || 0).toLocaleString()}</div>
        <div style={{
          marginTop: 8,
          padding: 8,
          background: isReconciled ? '#d1fae5' : '#fef3c7',
          borderRadius: 4
        }}>
          <strong>Difference:</strong>{' '}
          <span style={{
            color: isReconciled ? '#059669' : '#92400e',
            fontWeight: 'bold'
          }}>
            ${Math.abs(data.difference || 0).toLocaleString()}
          </span>
          {isReconciled ? ' ✅ Reconciled' : ' ⚠️ Unbalanced'}
        </div>
        <div><strong>Uploaded:</strong> {fmtDate(data.uploadedAt)}</div>
      </div>

      {items.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4>Reconciling Items ({items.length})</h4>
          {items.map((item, i) => (
            <div key={i} style={controlCard}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: 4
              }}>
                <div style={{ fontWeight: 'bold', fontSize: 12 }}>
                  {item.item || `Item ${i + 1}`}
                </div>
                <span style={{
                  fontSize: 13,
                  fontWeight: 'bold',
                  color: item.amount >= 0 ? '#059669' : '#dc2626'
                }}>
                  {item.amount >= 0 ? '+' : ''}${item.amount.toLocaleString()}
                </span>
              </div>
              {item.description && (
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
                  {item.description}
                </div>
              )}
              {item.needsJournalEntry && (
                <span style={{
                  ...pill,
                  fontSize: 10,
                  background: '#fef3c7',
                  borderColor: '#f59e0b',
                  color: '#92400e'
                }}>
                  Journal Entry Required
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {journalEntries.length > 0 && (
        <div style={{
          marginTop: 12,
          padding: 12,
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: 8
        }}>
          <h4 style={{ margin: '0 0 8px 0', color: '#92400e' }}>
            📝 Journal Entries Needed ({journalEntries.length})
          </h4>
          {journalEntries.map((je, i) => (
            <div key={i} style={{
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 4,
              padding: 8,
              marginBottom: 8
            }}>
              <div style={{ fontWeight: 'bold', fontSize: 12, marginBottom: 4 }}>
                {je.description}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
                Amount: ${(je.amount || 0).toLocaleString()}
              </div>
              <div style={{ fontSize: 10, fontFamily: 'monospace', color: '#374151' }}>
                {je.journalEntry || `Dr. ${je.debit} ${je.amount}, Cr. ${je.credit} ${je.amount}`}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** ⭐ NEW: Render Bank Confirmations (Step 10) */
const renderBankConfirmations = (data) => {
  if (!data) return <div>No bank confirmations uploaded.</div>;

  const confirmations = data.confirmations || [];
  const summary = data.summary || {};

  // Field name normalization — controller stores balancePerBank/balancePerBooks
  const getConfirmed = (c) => c.balancePerBank ?? c.confirmedBalance ?? c.confirmedAmount ?? 0;
  const getBook     = (c) => c.balancePerBooks ?? c.bookBalance ?? 0;
  const getDiff     = (c) => c.difference ?? (getBook(c) - getConfirmed(c));
  const hasExc      = (c) => c.exception || (c.exceptions && c.exceptions.length > 0) || (Math.abs(getDiff(c)) > 0 && !c.reconciled);
  const totalConfirmedAmt = summary.totalBankBalance ?? summary.totalConfirmed ?? confirmations.reduce((s, c) => s + getConfirmed(c), 0);

  return (
    <div>
      <div style={summaryBox}>
        <h4 style={{ margin: 0 }}>Bank Confirmations</h4>
        <div><strong>Total Confirmations:</strong> {confirmations.length}</div>
        {totalConfirmedAmt > 0 && <div><strong>Amount Confirmed:</strong> ${totalConfirmedAmt.toLocaleString()}</div>}
        {(summary.exceptions > 0 || summary.exceptionCount > 0) && (
          <div style={{ color: '#dc2626' }}>
            <strong>⚠️ Exceptions:</strong> {summary.exceptions ?? summary.exceptionCount}
          </div>
        )}
        {summary.allReconciled != null && (
          <div style={{ color: summary.allReconciled ? '#059669' : '#dc2626' }}>
            <strong>All Reconciled:</strong> {summary.allReconciled ? '✅ Yes' : '❌ No'}
          </div>
        )}
        <div><strong>Uploaded:</strong> {fmtDate(data.uploadedAt)}</div>
      </div>
      {confirmations.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4>Confirmations ({confirmations.length})</h4>
          {confirmations.map((conf, i) => {
            const confirmed = getConfirmed(conf);
            const book      = getBook(conf);
            const diff      = getDiff(conf);
            const exc       = hasExc(conf);
            return (
              <div key={i} style={{ ...controlCard, borderLeft: exc ? '4px solid #ef4444' : '4px solid #10b981' }}>
                <div style={{ fontWeight: 'bold', fontSize: 12, marginBottom: 4 }}>
                  {conf.bankName} - {conf.accountNumber}
                </div>
                <div style={{ fontSize: 11, marginBottom: 4 }}>Confirmed Balance: ${confirmed.toLocaleString()}</div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>Book Balance: ${book.toLocaleString()}</div>
                {Math.abs(diff) > 0 && (
                  <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>Variance: ${Math.abs(diff).toLocaleString()}</div>
                )}
                {exc && (
                  <div style={{ marginTop: 6, padding: 6, background: '#fef2f2', borderRadius: 4 }}>
                    <div style={{ fontSize: 10, color: '#991b1b', fontWeight: 'bold' }}>Exception:</div>
                    <div style={{ fontSize: 10, color: '#991b1b' }}>
                      {conf.exceptionDetails || (conf.exceptions || []).map(e => e.description).join('; ') || 'Variance identified'}
                    </div>
                  </div>
                )}
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4 }}>
                  Response Date: {conf.responseDate ? new Date(conf.responseDate).toLocaleDateString() : 'N/A'}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** ⭐ NEW: Render AR Confirmations (Step 11) */
const renderARConfirmations = (data) => {
  if (!data) return <div>No AR confirmations uploaded.</div>;

  const confirmations = data.confirmations || [];
  const summary = data.summary || {};

  return (
    <div>
      <div style={summaryBox}>
        <h4 style={{ margin: 0 }}>AR Confirmations</h4>
        <div><strong>Total Confirmations:</strong> {confirmations.length}</div>
        <div><strong>Responses Received:</strong> {summary.respondedCount || 0}</div>
        {summary.totalConfirmed != null && <div><strong>Amount Confirmed:</strong> ${(summary.totalConfirmed || 0).toLocaleString()}</div>}
        {summary.exceptionCount > 0 && (
          <div style={{ color: '#dc2626' }}>
            <strong>⚠️ Exceptions:</strong> {summary.exceptionCount}
          </div>
        )}
        {summary.noResponseCount > 0 && (
          <div style={{ color: '#f59e0b' }}>
            <strong>No Response:</strong> {summary.noResponseCount} (${(summary.totalNoResponse || 0).toLocaleString()})
          </div>
        )}
        <div><strong>Response Rate:</strong> {summary.responseRate || 0}%</div>
        <div><strong>Uploaded:</strong> {fmtDate(data.uploadedAt)}</div>
      </div>

      {confirmations.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4>Customer Confirmations ({confirmations.length})</h4>
          {confirmations.map((conf, i) => {
            const responded = !conf.noResponse;
            const isException = conf.matched === false && !conf.noResponse;
            const borderColor = isException ? '#ef4444' : responded ? '#10b981' : '#f59e0b';

            return (
              <div key={i} style={{ ...controlCard, borderLeft: `4px solid ${borderColor}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 4 }}>
                  <div style={{ fontWeight: 'bold', fontSize: 12 }}>{conf.customerName}</div>
                  <span style={{
                    ...pill,
                    fontSize: 9,
                    background: responded ? '#f0fdf4' : '#fef3c7',
                    borderColor: responded ? '#22c55e' : '#f59e0b',
                    color: responded ? '#15803d' : '#92400e'
                  }}>
                    {conf.noResponse ? 'No Response' : conf.status || 'Responded'}
                  </span>
                </div>
                <div style={{ fontSize: 11, marginBottom: 4 }}>
                  Book Balance: ${(conf.balancePerBooks || 0).toLocaleString()}
                </div>
                {responded && conf.balancePerCustomer != null && (
                  <div style={{ fontSize: 11, color: '#6b7280' }}>
                    Confirmed Balance: ${(conf.balancePerCustomer || 0).toLocaleString()}
                  </div>
                )}
                {isException && (
                  <div style={{ marginTop: 6, padding: 6, background: '#fef2f2', borderRadius: 4 }}>
                    <div style={{ fontSize: 10, color: '#991b1b', fontWeight: 'bold' }}>Exception:</div>
                    <div style={{ fontSize: 10, color: '#991b1b' }}>{conf.status}</div>
                    <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                      Variance: ${Math.abs(conf.difference || 0).toLocaleString()}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** ✅ NEW: Render AP Confirmations (Step 12 - Vendor/Supplier balances) */
const renderAPConfirmations = (data) => {
  if (!data) return <div>No AP confirmations uploaded.</div>;

  const confirmations = data.confirmations || [];
  const summary = data.summary || {};

  const unrecordedExposure = confirmations
    .filter(c => !c.noResponse && (c.balancePerVendor || 0) > (c.balancePerBooks || 0))
    .reduce((s, c) => s + ((c.balancePerVendor || 0) - (c.balancePerBooks || 0)), 0);

  return (
    <div>
      <div style={summaryBox}>
        <h4 style={{ margin: 0 }}>💳 AP Confirmations (Vendor)</h4>
        <div><strong>Total Confirmations:</strong> {confirmations.length}</div>
        <div><strong>Responses Received:</strong> {summary.respondedCount || 0}</div>
        {summary.totalBooks != null && (
          <div><strong>Total AP Per Books:</strong> ${(summary.totalBooks || 0).toLocaleString()}</div>
        )}
        {summary.totalConfirmed != null && (
          <div><strong>Total Confirmed By Vendors:</strong> ${(summary.totalConfirmed || 0).toLocaleString()}</div>
        )}
        {summary.exceptionCount > 0 && (
          <div style={{ color: '#dc2626' }}>
            <strong>⚠️ Vendor Disputes:</strong> {summary.exceptionCount}
          </div>
        )}
        {unrecordedExposure > 0 && (
          <div style={{ color: '#b45309', background: '#fef3c7', borderRadius: 4, padding: '4px 8px', marginTop: 4 }}>
            <strong>🚨 Unrecorded Liability Exposure:</strong> ${unrecordedExposure.toLocaleString()}
          </div>
        )}
        {summary.noResponseCount > 0 && (
          <div style={{ color: '#f59e0b' }}>
            <strong>No Response:</strong> {summary.noResponseCount} (${(summary.totalNoResponse || 0).toLocaleString()})
          </div>
        )}
        <div><strong>Response Rate:</strong> {summary.responseRate || 0}%</div>
        <div><strong>Uploaded:</strong> {fmtDate(data.uploadedAt)}</div>
      </div>

      {confirmations.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4>Vendor Confirmations ({confirmations.length})</h4>
          {confirmations.map((conf, i) => {
            const responded = !conf.noResponse;
            const isException = conf.matched === false && !conf.noResponse;
            const isUnrecordedRisk = responded && (conf.balancePerVendor || 0) > (conf.balancePerBooks || 0);
            const borderColor = isException || isUnrecordedRisk ? '#ef4444' : responded ? '#10b981' : '#f59e0b';

            return (
              <div key={i} style={{ ...controlCard, borderLeft: `4px solid ${borderColor}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 4 }}>
                  <div style={{ fontWeight: 'bold', fontSize: 12 }}>{conf.vendorName}</div>
                  <span style={{
                    ...pill, fontSize: 9,
                    background: isException || isUnrecordedRisk ? '#fef2f2' : responded ? '#f0fdf4' : '#fef3c7',
                    borderColor: isException || isUnrecordedRisk ? '#ef4444' : responded ? '#22c55e' : '#f59e0b',
                    color: isException || isUnrecordedRisk ? '#991b1b' : responded ? '#15803d' : '#92400e'
                  }}>
                    {conf.noResponse ? 'No Response' : isUnrecordedRisk ? '⚠️ Unrecorded Liability' : conf.matched ? 'Matched' : conf.status || 'Responded'}
                  </span>
                </div>
                {conf.accountNumber && (
                  <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 4 }}>Account: {conf.accountNumber}</div>
                )}
                <div style={{ fontSize: 11, marginBottom: 4 }}>
                  Book Balance: ${(conf.balancePerBooks || 0).toLocaleString()}
                </div>
                {responded && conf.balancePerVendor != null && (
                  <div style={{ fontSize: 11, color: '#6b7280' }}>
                    Vendor-Confirmed Balance: ${(conf.balancePerVendor || 0).toLocaleString()}
                  </div>
                )}
                {conf.difference != null && responded && (
                  <div style={{ fontSize: 11, marginTop: 2 }}>
                    Difference:{' '}
                    <span style={{ color: Math.abs(conf.difference) > 0 ? '#dc2626' : '#059669', fontWeight: 'bold' }}>
                      ${Math.abs(conf.difference || 0).toLocaleString()}
                      {conf.difference < 0 ? ' (understatement — unrecorded liability)' : conf.difference > 0 ? ' (overstatement in books)' : ''}
                    </span>
                  </div>
                )}
                {isUnrecordedRisk && (
                  <div style={{ marginTop: 6, padding: 6, background: '#fef3c7', borderRadius: 4, border: '1px solid #f59e0b' }}>
                    <div style={{ fontSize: 10, color: '#92400e', fontWeight: 'bold' }}>🚨 Unrecorded Liability Risk:</div>
                    <div style={{ fontSize: 10, color: '#92400e' }}>
                      Vendor claims ${((conf.balancePerVendor || 0) - (conf.balancePerBooks || 0)).toLocaleString()} more than books reflect. Investigate for unrecorded invoices.
                    </div>
                  </div>
                )}
                {isException && !isUnrecordedRisk && (
                  <div style={{ marginTop: 6, padding: 6, background: '#fef2f2', borderRadius: 4 }}>
                    <div style={{ fontSize: 10, color: '#991b1b', fontWeight: 'bold' }}>Exception:</div>
                    <div style={{ fontSize: 10, color: '#991b1b' }}>{conf.status || conf.notes || 'Balance mismatch'}</div>
                    {conf.difference != null && (
                      <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                        Variance: ${Math.abs(conf.difference || 0).toLocaleString()}
                      </div>
                    )}
                  </div>
                )}
                {conf.notes && (
                  <div style={{ marginTop: 4, fontSize: 10, color: '#6b7280', fontStyle: 'italic' }}>Note: {conf.notes}</div>
                )}
                <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
                  Method: {conf.confirmationMethod || 'positive'}
                  {conf.responseDate && ` | Response: ${new Date(conf.responseDate).toLocaleDateString()}`}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** ⭐ NEW: Render Inventory Confirmations (Step 12) */
const renderInventoryConfirmations = (data) => {
  if (!data) return <div>No inventory confirmations uploaded.</div>;

  const confirmations = data.confirmations || [];
  const summary = data.summary || {};

  return (
    <div>
      <div style={summaryBox}>
        <h4 style={{ margin: 0 }}>Inventory Confirmations</h4>
        <div><strong>Items Confirmed:</strong> {confirmations.length}</div>
        {summary.totalQuantity && <div><strong>Total Quantity:</strong> {summary.totalQuantity.toLocaleString()}</div>}
        {summary.totalValue && <div><strong>Total Value:</strong> ${summary.totalValue.toLocaleString()}</div>}
        {summary.exceptions > 0 && (
          <div style={{ color: '#dc2626' }}>
            <strong>⚠️ Exceptions:</strong> {summary.exceptions}
          </div>
        )}
        <div><strong>Uploaded:</strong> {fmtDate(data.uploadedAt)}</div>
      </div>
      
      {confirmations.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4>Inventory Items ({confirmations.length})</h4>
          {confirmations.map((item, i) => (
            <div key={i} style={{
              ...controlCard,
              borderLeft: item.exception ? '4px solid #ef4444' : '4px solid #10b981'
            }}>
              <div style={{ fontWeight: 'bold', fontSize: 12, marginBottom: 4 }}>
                {item.itemDescription}
              </div>
              <div style={{ fontSize: 11, marginBottom: 2 }}>
                SKU: {item.sku}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 6 }}>
                <div>
                  <div style={{ fontSize: 10, color: '#6b7280' }}>Book Quantity</div>
                  <div style={{ fontSize: 12, fontWeight: 'bold' }}>{item.bookQuantity}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: '#6b7280' }}>Confirmed Quantity</div>
                  <div style={{ fontSize: 12, fontWeight: 'bold' }}>{item.confirmedQuantity}</div>
                </div>
              </div>
              {item.exception && (
                <div style={{ marginTop: 6, padding: 6, background: '#fef2f2', borderRadius: 4 }}>
                  <div style={{ fontSize: 10, color: '#991b1b', fontWeight: 'bold' }}>Exception:</div>
                  <div style={{ fontSize: 10, color: '#991b1b' }}>{item.exceptionDetails}</div>
                  <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                    Variance: {item.variance} units
                  </div>
                </div>
              )}
              <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4 }}>
                Location: {item.warehouseLocation}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** ⭐ NEW: Render Physical Inventory Observation (Step 13) */
const renderInventoryObservation = (data) => {
  if (!data) return <div>No physical inventory observation uploaded.</div>;

  const testCounts = data.testCounts || [];
  const summary = data.summary || {};

  return (
    <div>
      <div style={summaryBox}>
        <h4 style={{ margin: 0 }}>Physical Inventory Observation</h4>
        <div><strong>Test Counts Performed:</strong> {testCounts.length}</div>
        <div><strong>Observation Date:</strong> {new Date(data.observationDate).toLocaleDateString()}</div>
        {summary.exceptions > 0 && (
          <div style={{ color: '#dc2626' }}>
            <strong>⚠️ Discrepancies Found:</strong> {summary.exceptions}
          </div>
        )}
        <div><strong>Uploaded:</strong> {fmtDate(data.uploadedAt)}</div>
      </div>
      
      {testCounts.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4>Test Count Results ({testCounts.length})</h4>
          {testCounts.map((count, i) => (
            <div key={i} style={{
              ...controlCard,
              borderLeft: count.discrepancy ? '4px solid #ef4444' : '4px solid #10b981'
            }}>
              <div style={{ fontWeight: 'bold', fontSize: 12, marginBottom: 4 }}>
                {count.itemDescription}
              </div>
              <div style={{ fontSize: 11, marginBottom: 2 }}>
                SKU: {count.sku} | Location: {count.location}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 6 }}>
                <div>
                  <div style={{ fontSize: 10, color: '#6b7280' }}>Book Count</div>
                  <div style={{ fontSize: 12, fontWeight: 'bold' }}>{count.bookCount}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: '#6b7280' }}>Physical Count</div>
                  <div style={{ fontSize: 12, fontWeight: 'bold' }}>{count.physicalCount}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: '#6b7280' }}>Variance</div>
                  <div style={{ 
                    fontSize: 12, 
                    fontWeight: 'bold',
                    color: count.variance !== 0 ? '#dc2626' : '#059669'
                  }}>
                    {count.variance}
                  </div>
                </div>
              </div>
              {count.discrepancy && (
                <div style={{ marginTop: 6, padding: 6, background: '#fef2f2', borderRadius: 4 }}>
                  <div style={{ fontSize: 10, color: '#991b1b', fontWeight: 'bold' }}>Discrepancy Notes:</div>
                  <div style={{ fontSize: 10, color: '#991b1b' }}>{count.notes}</div>
                </div>
              )}
              <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4 }}>
                Counted by: {count.countedBy} | Verified by: {count.verifiedBy}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {data.overallObservations && (
        <div style={{ marginTop: 12, padding: 12, background: '#f0f9ff', borderRadius: 6 }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: 13 }}>Overall Observations</h4>
          <div style={{ fontSize: 11, lineHeight: 1.6 }}>
            {data.overallObservations}
          </div>
        </div>
      )}
    </div>
  );
};

const renderSupportingDocuments = (data) => {
  if (!data || Object.keys(data).length === 0) {
    return <div style={{ color: '#6b7280', fontSize: 13 }}>No supporting documents uploaded.</div>;
  }

  const docTypeLabels = {
    cash_flow_statement:              'Cash Flow Statement',
    statement_of_changes_equity:      'Statement of Changes in Equity',
    notes_to_financial_statements:    'Notes to Financial Statements',
    general_ledger:                   'General Ledger',
    ar_aging_report:                  'AR Aging Report',
    vendor_statements:                'Vendor Statements',
    inventory_valuation_schedules:    'Inventory Valuation Schedules',
    fixed_asset_register:             'Fixed Asset Register',
    depreciation_schedules:           'Depreciation Schedules',
    debt_confirmations:               'Debt Confirmations',
    loan_agreements:                  'Loan Agreements',
    lease_agreements:                 'Lease Agreements',
    legal_documents:                  'Legal Documents',
    title_documents:                  'Title Documents',
    contracts:                        'Contracts',
    supporting_schedules:             'Supporting Schedules',
    prior_year_financials:            'Prior Year Financials',
    budget_documents:                 'Budget Documents',
    industry_benchmarks:              'Industry Benchmarks',
    additional_evidence:              'Additional Evidence',
    management_representation_letter: 'Management Representation Letter',
    tax_returns:                      'Tax Returns',
    regulatory_filings:               'Regulatory Filings',
  };

  const entries = Object.entries(data);

  return (
    <div>
      <div style={summaryBox}>
        <h4 style={{ margin: '0 0 6px 0' }}>Supporting Documents</h4>
        <div style={{ fontSize: 12, color: '#374151' }}>{entries.length} document type{entries.length !== 1 ? 's' : ''} uploaded</div>
      </div>
      {entries.map(([docType, doc]) => {
        if (!doc) return null;
        const label  = docTypeLabels[docType] || docType;
        const flags  = doc.auditFlags || doc.audit_flags || [];
        const findings = doc.keyFindings || doc.key_findings || [];
        const summary  = doc.summary || doc.analysisResult || '';
        return (
          <div key={docType} style={{ ...controlCard, marginBottom: 10, borderLeft: flags.length > 0 ? '4px solid #f59e0b' : '4px solid #10b981' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontWeight: 700, fontSize: 12 }}>{label}</div>
              {flags.length > 0 && (
                <span style={{ fontSize: 10, background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '1px 6px', fontWeight: 600 }}>
                  ⚠️ {flags.length} flag{flags.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            {doc.uploadedAt && (
              <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 6 }}>Uploaded: {fmtDate(doc.uploadedAt)}</div>
            )}
            {summary && (
              <div style={{ fontSize: 11, color: '#374151', marginBottom: 6, lineHeight: 1.5 }}>{typeof summary === 'string' ? summary : JSON.stringify(summary)}</div>
            )}
            {findings.length > 0 && (
              <div style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>KEY FINDINGS</div>
                {findings.map((f, i) => (
                  <div key={i} style={{ fontSize: 11, color: '#374151', paddingLeft: 8, borderLeft: '2px solid #e5e7eb', marginBottom: 3 }}>
                    {typeof f === 'string' ? f : f.finding || JSON.stringify(f)}
                  </div>
                ))}
              </div>
            )}
            {flags.length > 0 && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: '#92400e', marginBottom: 3 }}>AUDIT FLAGS</div>
                {flags.map((flag, i) => (
                  <div key={i} style={{ fontSize: 11, color: '#92400e', background: '#fef3c7', borderRadius: 4, padding: '3px 8px', marginBottom: 3 }}>
                    {typeof flag === 'string' ? flag : flag.flag || JSON.stringify(flag)}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const renderAssertions = (raw) => {
  if (!raw) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{ ...summaryBox, background: '#fef2f2', borderColor: '#fca5a5' }}>
          <h4 style={{ margin: 0, color: '#991b1b' }}>⚠️ No Assertion Test Data</h4>
          <p style={{ margin: '8px 0 0 0', fontSize: 13 }}>
            No assertion testing results found. Please run the "Test Assertions" step first.
          </p>
        </div>
      </div>
    );
  }

  let data = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch (e) {
      console.warn('[ASSERTIONS] Failed to parse JSON', e);
      return (
        <div style={{ padding: 16 }}>
          <div style={{ ...summaryBox, background: '#fef2f2', borderColor: '#fca5a5' }}>
            <h4 style={{ margin: 0, color: '#991b1b' }}>⚠️ Data Parsing Error</h4>
            <p style={{ margin: '8px 0 0 0', fontSize: 13 }}>
              Failed to parse assertion testing data. The data may be corrupted.
            </p>
          </div>
        </div>
      );
    }
  }

  const assertionTests = data.assertion_tests || [];
  const highRiskAccounts = data.high_risk_accounts || [];
  const auditStrategy = data.audit_strategy_summary || {};
  const fallbackError = data.fallback_error || data.error;

  if (assertionTests.length === 0 && !fallbackError) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{ ...summaryBox, background: '#fef3c7', borderColor: '#f59e0b' }}>
          <h4 style={{ margin: 0, color: '#92400e' }}>ℹ️ No Material Accounts to Test</h4>
          <p style={{ margin: '8px 0 0 0', fontSize: 13 }}>
            No accounts exceeded the materiality threshold.
          </p>
        </div>
      </div>
    );
  }

  if (fallbackError) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{ ...summaryBox, background: '#fef2f2', borderColor: '#fca5a5' }}>
          <h4 style={{ margin: 0, color: '#991b1b' }}>⚠️ Assertion Testing Error</h4>
          <p style={{ margin: '8px 0 0 0', fontSize: 13 }}>
            {fallbackError.message || 'An error occurred during assertion testing.'}
          </p>
        </div>
      </div>
    );
  }

  const getRiskBadge = (level) => {
    const styles = {
      low: { bg: '#f0fdf4', border: '#22c55e', color: '#15803d', icon: '🟢' },
      moderate: { bg: '#fef3c7', border: '#f59e0b', color: '#92400e', icon: '🟡' },
      high: { bg: '#fef2f2', border: '#ef4444', color: '#991b1b', icon: '🔴' }
    };
    const style = styles[level?.toLowerCase()] || styles.moderate;
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: 999,
          fontSize: 10,
          background: style.bg,
          border: `1px solid ${style.border}`,
          color: style.color
        }}
      >
        {style.icon} {level?.toUpperCase()}
      </span>
    );
  };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ ...summaryBox, background: '#f0fdf4', borderColor: '#22c55e' }}>
        <h4 style={{ margin: '0 0 8px 0', color: '#15803d' }}>✅ Assertion Testing Complete</h4>
        <div style={{ fontSize: 13 }}>
          <strong>Accounts Tested:</strong> {assertionTests.length}
        </div>
        <div style={{ fontSize: 13 }}>
          <strong>High-Risk Accounts:</strong> {highRiskAccounts.length}
        </div>
      </div>

      {highRiskAccounts.length > 0 && (
        <div style={{ marginTop: 12, padding: 12, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8 }}>
          <h4 style={{ margin: '0 0 8px 0', color: '#991b1b', fontSize: 14 }}>
            🔴 High-Risk Accounts Requiring Additional Attention
          </h4>
          {highRiskAccounts.map((acct, i) => (
            <div key={i} style={{ 
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 4,
              padding: 8,
              marginBottom: 8
            }}>
              <div style={{ fontWeight: 'bold', fontSize: 12, marginBottom: 4 }}>
                {acct.account_name || acct.name}
                <span style={{ marginLeft: 8 }}>
                  {getRiskBadge('high')}
                </span>
              </div>
              {acct.risk_factors && Array.isArray(acct.risk_factors) && (
                <ul style={{ margin: '4px 0 0 20px', fontSize: 11, color: '#991b1b' }}>
                  {acct.risk_factors.map((factor, j) => (
                    <li key={j}>{factor}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <h4 style={{ fontSize: 14, marginBottom: 12 }}>Account Assertion Results</h4>
        {assertionTests.map((account, idx) => {
          const assertions = account.assertion_results || {};
          const assertionTypes = ['existence', 'completeness', 'valuation', 'rights_obligations', 'presentation'];
          
          return (
            <div key={idx} style={{ 
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: 4,
              padding: 16,
              marginBottom: 16
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 4 }}>
                    {account.account_name}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    {account.category?.toUpperCase()} | Balance: ${(account.balance || 0).toLocaleString()}
                  </div>
                </div>
                <div>
                  {getRiskBadge(account.overall_risk_assessment)}
                </div>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
                gap: 10,
                marginTop: 12
              }}>
                {assertionTypes.map((type) => {
                  const assertion = assertions[type];
                  if (!assertion) return null;

                  return (
                    <div key={type} style={{ 
                      border: '1px solid #e5e7eb', 
                      borderRadius: 6, 
                      padding: 10,
                      background: '#fafafa'
                    }}>
                      <div style={{ 
                        fontWeight: 'bold', 
                        fontSize: 11, 
                        marginBottom: 6,
                        textTransform: 'capitalize'
                      }}>
                        {type.replace('_', ' & ')}
                      </div>
                      <div style={{ marginBottom: 6 }}>
                        {getRiskBadge(assertion.risk_level)}
                      </div>
                      
                      {assertion.procedures_needed && Array.isArray(assertion.procedures_needed) && (
                        <details style={{ marginTop: 6 }}>
                          <summary style={{ 
                            cursor: 'pointer', 
                            fontSize: 10, 
                            color: '#3b82f6',
                            fontWeight: 500
                          }}>
                            Procedures ({assertion.procedures_needed.length})
                          </summary>
                          <ul style={{ margin: '4px 0 0 16px', paddingLeft: 0, fontSize: 10 }}>
                            {assertion.procedures_needed.slice(0, 3).map((proc, i) => (
                              <li key={i} style={{ marginBottom: 2 }}>{proc}</li>
                            ))}
                            {assertion.procedures_needed.length > 3 && (
                              <li style={{ color: '#6b7280', fontStyle: 'italic' }}>
                                +{assertion.procedures_needed.length - 3} more...
                              </li>
                            )}
                          </ul>
                        </details>
                      )}
                    </div>
                  );
                })}
              </div>

              {account.sampling_method && (
                <div style={{ marginTop: 12, fontSize: 11, color: '#6b7280' }}>
                  <strong>Sampling:</strong> {account.sampling_method}
                  {account.recommended_sample_size > 0 && ` (${account.recommended_sample_size} items)`}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {auditStrategy && Object.keys(auditStrategy).length > 0 && (
        <div style={{ marginTop: 16 }}>
          <details style={{ 
            border: '1px solid #0ea5e9', 
            borderRadius: 8, 
            padding: 12,
            background: '#f0f9ff'
          }}>
            <summary style={{ 
              cursor: 'pointer', 
              fontWeight: 'bold', 
              fontSize: 14,
              color: '#0369a1'
            }}>
              📋 Audit Strategy Summary
            </summary>
            <div style={{ marginTop: 12 }}>
              {auditStrategy.substantive_analytical_procedures && (
                <div style={{ marginBottom: 12 }}>
                  <strong style={{ fontSize: 12 }}>Substantive Analytical Procedures:</strong>
                  <ul style={{ margin: '4px 0 0 20px', fontSize: 11 }}>
                    {Array.isArray(auditStrategy.substantive_analytical_procedures) && 
                      auditStrategy.substantive_analytical_procedures.map((proc, i) => (
                        <li key={i}>{proc}</li>
                      ))
                    }
                  </ul>
                </div>
              )}
              {auditStrategy.tests_of_details && (
                <div style={{ marginBottom: 12 }}>
                  <strong style={{ fontSize: 12 }}>Tests of Details:</strong>
                  <ul style={{ margin: '4px 0 0 20px', fontSize: 11 }}>
                    {Array.isArray(auditStrategy.tests_of_details) && 
                      auditStrategy.tests_of_details.map((test, i) => (
                        <li key={i}>{test}</li>
                      ))
                    }
                  </ul>
                </div>
              )}
              {auditStrategy.documentation_requirements && (
                <div>
                  <strong style={{ fontSize: 12 }}>Documentation Requirements:</strong>
                  <ul style={{ margin: '4px 0 0 20px', fontSize: 11 }}>
                    {Array.isArray(auditStrategy.documentation_requirements) && 
                      auditStrategy.documentation_requirements.map((req, i) => (
                        <li key={i}>{req}</li>
                      ))
                    }
                  </ul>
                </div>
              )}
            </div>
          </details>
        </div>
      )}

      <details style={{ marginTop: 16 }}>
        <summary style={{ cursor: 'pointer', fontSize: 12, color: '#666' }}>
          🔍 Raw JSON (for debugging)
        </summary>
        <pre style={{ 
          background: '#f8f9fa',
          border: '1px solid #e5e7eb',
          borderRadius: 4,
          padding: 8,
          fontSize: 11,
          maxHeight: 240,
          overflow: 'auto',
          marginTop: 8
        }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      </details>
    </div>
  );
};

const renderSubstantiveTesting = (raw) => {
  if (!raw) return <div style={{ padding: 16, color: '#6b7280' }}>No substantive testing results yet.</div>;

  let data = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch (e) {
      console.warn('[SUBSTANTIVE] Failed to parse JSON', e);
      return <div style={{ padding: 16, color: '#991b1b' }}>Error parsing substantive testing data.</div>;
    }
  }

  const isDualSampling = !!(data.statistical_testing || data.judgmental_testing);
  const isFallback = data.fallback === true;
  
  if (isFallback || (!isDualSampling && data.test_results)) {
    const testResults = data.test_results || [];
    const summary = data.summary || {};
    const auditOpinion = data.audit_opinion_impact || {};
    
    return (
      <div style={{ padding: 16 }}>
        {isFallback && (
          <div style={{ 
            background: '#fef2f2',
            border: '1px solid #fca5a5',
            borderRadius: 6,
            padding: 10,
            marginBottom: 12
          }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#991b1b' }}>⚠️ Substantive Testing Incomplete</h4>
            <div style={{ fontSize: 12 }}>
              The AI response was truncated or failed to parse. The backend saved fallback data.
            </div>
            {auditOpinion.qualitative_factors && (
              <div style={{ fontSize: 11, marginTop: 6 }}>
                <strong>Factors:</strong>
                <ul style={{ margin: '4px 0 0 20px', paddingLeft: 0 }}>
                  {auditOpinion.qualitative_factors.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              </div>
            )}
            {auditOpinion.recommended_actions && (
              <div style={{ fontSize: 11, marginTop: 6 }}>
                <strong>Recommended Actions:</strong>
                <ul style={{ margin: '4px 0 0 20px', paddingLeft: 0 }}>
                  {auditOpinion.recommended_actions.map((a, i) => (
                    <li key={i}>{a}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        
        <div style={{ 
          background: '#f0f9ff',
          border: '1px solid #0ea5e9',
          borderRadius: 6,
          padding: 10,
          marginBottom: 12
        }}>
          <h4 style={{ margin: '0 0 8px 0' }}>Substantive Testing Summary</h4>
          <div style={{ fontSize: 12 }}>
            <strong>Total Tested:</strong> {summary.total_tested || 0}
          </div>
          <div style={{ fontSize: 12 }}>
            <strong>Passed:</strong> <span style={{ color: '#059669' }}>{summary.passed || 0}</span>
          </div>
          <div style={{ fontSize: 12 }}>
            <strong>Failed:</strong> <span style={{ color: '#dc2626' }}>{summary.failed || 0}</span>
          </div>
          <div style={{ fontSize: 12 }}>
            <strong>Exceptions:</strong> <span style={{ color: '#f59e0b' }}>{summary.exceptions || 0}</span>
          </div>
          {summary.conclusion && (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              <strong>Conclusion:</strong> {summary.conclusion}
            </div>
          )}
        </div>
        
        {testResults.length > 0 && (
          <div>
            <h4 style={{ fontSize: 14, marginBottom: 12 }}>Test Results</h4>
            {testResults.map((test, i) => (
              <div key={i} style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: 4,
                padding: 12,
                marginBottom: 12
              }}>
                <div style={{ fontWeight: 'bold', fontSize: 12 }}>
                  {test.account_name || test.procedure_name || `Test ${i + 1}`}
                </div>
                <div style={{ fontSize: 11, marginTop: 4 }}>
                  {test.overall_result || test.conclusion || 'No result'}
                </div>
              </div>
            ))}
          </div>
        )}
        
        <details style={{ marginTop: 16 }}>
          <summary style={{ cursor: 'pointer', fontSize: 12, color: '#666' }}>
            🔍 Raw JSON (for debugging)
          </summary>
          <pre style={{ 
            background: '#f8f9fa',
            border: '1px solid #e5e7eb',
            borderRadius: 4,
            padding: 8,
            fontSize: 11,
            maxHeight: 240,
            overflow: 'auto',
            marginTop: 8
          }}>
            {JSON.stringify(data, null, 2)}
          </pre>
        </details>
      </div>
    );
  }

  const statTesting = data.statistical_testing || {};
  const judgTesting = data.judgmental_testing || {};
  const combinedConclusion = data.combined_conclusion || {};
  
  const statResults = statTesting.results?.test_results || [];
  const judgResults = judgTesting.results?.test_results || [];
  
  const statSummary = statTesting.summary || {};
  const judgSummary = judgTesting.summary || {};

  return (
    <div style={{ padding: 16 }}>
      <div style={{ 
        background: '#f0f9ff',
        border: '1px solid #0ea5e9',
        borderRadius: 6,
        padding: 10,
        marginBottom: 12
      }}>
        <h4 style={{ margin: '0 0 8px 0' }}>Substantive Testing Results</h4>
        
        {statTesting.method && (
          <div style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ fontWeight: 'bold', fontSize: 12, marginBottom: 4 }}>📊 Statistical Testing</div>
            <div style={{ fontSize: 11 }}>
              <strong>Method:</strong> {statTesting.method}
            </div>
            <div style={{ fontSize: 11 }}>
              <strong>Sample Size:</strong> {statTesting.sample_size} of {statTesting.population_size}
            </div>
            {statTesting.confidence_level && (
              <div style={{ fontSize: 11 }}>
                <strong>Confidence Level:</strong> {statTesting.confidence_level}%
              </div>
            )}
            {statSummary.total_tested != null && (
              <div style={{ fontSize: 11 }}>
                <strong>Tested:</strong> {statSummary.total_tested} | 
                <span style={{ color: '#059669', marginLeft: 4 }}>Passed: {statSummary.passed || 0}</span> | 
                <span style={{ color: '#dc2626', marginLeft: 4 }}>Failed: {statSummary.failed || 0}</span>
              </div>
            )}
          </div>
        )}
        
        {judgTesting.method && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontWeight: 'bold', fontSize: 12, marginBottom: 4 }}>🎯 Judgmental Testing</div>
            <div style={{ fontSize: 11 }}>
              <strong>Method:</strong> {judgTesting.method}
            </div>
            <div style={{ fontSize: 11 }}>
              <strong>Sample Size:</strong> {judgTesting.sample_size}
            </div>
            {judgTesting.selection_criteria && (
              <div style={{ fontSize: 11 }}>
                <strong>Criteria:</strong> {judgTesting.selection_criteria}
              </div>
            )}
            {judgSummary.total_tested != null && (
              <div style={{ fontSize: 11 }}>
                <strong>Tested:</strong> {judgSummary.total_tested} | 
                <span style={{ color: '#059669', marginLeft: 4 }}>Passed: {judgSummary.passed || 0}</span> | 
                <span style={{ color: '#dc2626', marginLeft: 4 }}>Failed: {judgSummary.failed || 0}</span>
              </div>
            )}
          </div>
        )}

        {combinedConclusion.overall_audit_conclusion && (
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #e5e7eb' }}>
            <strong>Overall Conclusion:</strong>{' '}
            <span
              style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: 999,
                fontSize: 10,
                background: combinedConclusion.overall_audit_conclusion === 'unqualified' ? '#f0fdf4' : '#fef2f2',
                border: `1px solid ${combinedConclusion.overall_audit_conclusion === 'unqualified' ? '#22c55e' : '#fca5a5'}`,
                color: combinedConclusion.overall_audit_conclusion === 'unqualified' ? '#15803d' : '#991b1b'
              }}
            >
              {combinedConclusion.overall_audit_conclusion}
            </span>
          </div>
        )}

        {combinedConclusion.total_exceptions != null && (
          <div style={{ fontSize: 11, marginTop: 4 }}>
            <strong>Total Exceptions:</strong>{' '}
            <span style={{ color: '#f59e0b' }}>
              {combinedConclusion.total_exceptions}
            </span>
          </div>
        )}
      </div>

      {(statResults.length > 0 || judgResults.length > 0) && (
        <div>
          <h4 style={{ fontSize: 14, marginBottom: 12 }}>Detailed Test Results</h4>
          
          {statResults.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 8, color: '#0369a1' }}>
                📊 Statistical Testing ({statResults.length} items)
              </div>
              {statResults.map((test, i) => (
                <div
                  key={`stat-${i}`}
                  style={{
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: 4,
                    padding: 12,
                    marginBottom: 8,
                    borderLeftWidth: 4,
                    borderLeftColor: test.overall_result === 'pass' ? '#22c55e' : '#ef4444'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ fontWeight: 'bold', fontSize: 12 }}>
                      {test.account_name || `Item ${i + 1}`}
                    </div>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 999,
                        fontSize: 10,
                        background: test.overall_result === 'pass' ? '#d1fae5' : '#fee2e2',
                        border: `1px solid ${test.overall_result === 'pass' ? '#22c55e' : '#dc2626'}`,
                        color: test.overall_result === 'pass' ? '#065f46' : '#991b1b'
                      }}
                    >
                      {test.overall_result?.toUpperCase() || 'N/A'}
                    </span>
                  </div>
                  
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>
                    Debit: ${(test.debit_balance || 0).toLocaleString()} | Credit: ${(test.credit_balance || 0).toLocaleString()}
                  </div>

                  {test.assertions_tested && (
                    <div style={{ marginTop: 6 }}>
                      <div style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 4 }}>Assertions:</div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {Object.entries(test.assertions_tested).map(([assertion, details]) => (
                          <div
                            key={assertion}
                            style={{
                              fontSize: 9,
                              padding: '2px 6px',
                              borderRadius: 4,
                              background: details.result === 'pass' ? '#d1fae5' : '#fee2e2',
                              border: `1px solid ${details.result === 'pass' ? '#22c55e' : '#dc2626'}`,
                              color: details.result === 'pass' ? '#065f46' : '#991b1b'
                            }}
                          >
                            {assertion}: {details.result}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {test.exception_details && (
                    <div style={{ marginTop: 6, fontSize: 10, color: '#dc2626' }}>
                      <strong>Exception:</strong> {test.exception_details}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {judgResults.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 8, color: '#7c3aed' }}>
                🎯 Judgmental Testing ({judgResults.length} items)
              </div>
              {judgResults.map((test, i) => (
                <div
                  key={`judg-${i}`}
                  style={{
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: 4,
                    padding: 12,
                    marginBottom: 8,
                    borderLeftWidth: 4,
                    borderLeftColor: test.overall_result === 'pass' ? '#22c55e' : '#ef4444'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ fontWeight: 'bold', fontSize: 12 }}>
                      {test.account_name || `Item ${i + 1}`}
                    </div>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 999,
                        fontSize: 10,
                        background: test.overall_result === 'pass' ? '#d1fae5' : '#fee2e2',
                        border: `1px solid ${test.overall_result === 'pass' ? '#22c55e' : '#dc2626'}`,
                        color: test.overall_result === 'pass' ? '#065f46' : '#991b1b'
                      }}
                    >
                      {test.overall_result?.toUpperCase() || 'N/A'}
                    </span>
                  </div>
                  
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>
                    Debit: ${(test.debit_balance || 0).toLocaleString()} | Credit: ${(test.credit_balance || 0).toLocaleString()}
                  </div>

                  {test.assertions_tested && (
                    <div style={{ marginTop: 6 }}>
                      <div style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 4 }}>Assertions:</div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {Object.entries(test.assertions_tested).map(([assertion, details]) => (
                          <div
                            key={assertion}
                            style={{
                              fontSize: 9,
                              padding: '2px 6px',
                              borderRadius: 4,
                              background: details.result === 'pass' ? '#d1fae5' : '#fee2e2',
                              border: `1px solid ${details.result === 'pass' ? '#22c55e' : '#dc2626'}`,
                              color: details.result === 'pass' ? '#065f46' : '#991b1b'
                            }}
                          >
                            {assertion}: {details.result}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {test.exception_details && (
                    <div style={{ marginTop: 6, fontSize: 10, color: '#dc2626' }}>
                      <strong>Exception:</strong> {test.exception_details}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {statResults.length === 0 && judgResults.length === 0 && (statSummary || judgSummary) && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>
            Test summary available. Individual test details not provided by backend.
          </div>
        </div>
      )}

      <details style={{ marginTop: 16 }}>
        <summary style={{ cursor: 'pointer', fontSize: 12, color: '#666' }}>
          🔍 Raw JSON (for debugging)
        </summary>
        <pre style={{ 
          background: '#f8f9fa',
          border: '1px solid #e5e7eb',
          borderRadius: 4,
          padding: 8,
          fontSize: 11,
          maxHeight: 240,
          overflow: 'auto',
          marginTop: 8
        }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      </details>
    </div>
  );
};

const renderFindings = (raw) => {
  if (!raw) return <div style={{ padding: 16 }}>No final findings generated.</div>;

  let data = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch (e) {
      console.warn('[FINDINGS] Failed to parse JSON', e);
      return <div style={{ padding: 16, color: '#991b1b' }}>Error parsing audit findings data.</div>;
    }
  }

  const auditOpinion = data.audit_opinion || {};
  const auditFindings = data.audit_findings || {};
  const balanceSheetSummary = data.balance_sheet_summary || {};
  const gaapCompliance = data.gaap_compliance || {};
  const managementRecs = data.management_recommendations || [];
  const scopeOfAudit = data.scope_of_audit || {};

  return (
    <div style={{ padding: 16 }}>
      <div style={{ 
        background: auditOpinion.opinion_type === 'unqualified' ? '#f0fdf4' : '#fef2f2',
        border: `2px solid ${auditOpinion.opinion_type === 'unqualified' ? '#22c55e' : '#fca5a5'}`,
        borderRadius: 8,
        padding: 16,
        marginBottom: 16
      }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: 18, color: '#111827' }}>
          🎯 Audit Opinion
        </h3>
        <div style={{ 
          display: 'inline-block',
          padding: '6px 16px',
          borderRadius: 999,
          fontSize: 14,
          fontWeight: 'bold',
          background: auditOpinion.opinion_type === 'unqualified' ? '#d1fae5' : '#fee2e2',
          border: `2px solid ${auditOpinion.opinion_type === 'unqualified' ? '#22c55e' : '#dc2626'}`,
          color: auditOpinion.opinion_type === 'unqualified' ? '#065f46' : '#991b1b',
          textTransform: 'uppercase',
          marginBottom: 12
        }}>
          {auditOpinion.opinion_type || 'N/A'}
        </div>
        <p style={{ margin: '12px 0 0 0', fontSize: 13, lineHeight: 1.6, color: '#374151' }}>
          {auditOpinion.opinion_text}
        </p>
      </div>

      <div style={{ 
        background: '#f0f9ff',
        border: '1px solid #0ea5e9',
        borderRadius: 8,
        padding: 16,
        marginBottom: 16
      }}>
        <h4 style={{ margin: '0 0 12px 0', fontSize: 16, color: '#0369a1' }}>
          📊 Balance Sheet Summary
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Total Assets</div>
            <div style={{ fontSize: 20, fontWeight: 'bold', color: '#111827' }}>
              ${(balanceSheetSummary.total_assets || 0).toLocaleString()}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Total Liabilities</div>
            <div style={{ fontSize: 20, fontWeight: 'bold', color: '#111827' }}>
              ${(balanceSheetSummary.total_liabilities || 0).toLocaleString()}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Total Equity</div>
            <div style={{ fontSize: 20, fontWeight: 'bold', color: '#111827' }}>
              ${(balanceSheetSummary.total_equity || 0).toLocaleString()}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Balanced</div>
            <div style={{ fontSize: 16, fontWeight: 'bold', color: balanceSheetSummary.balanced ? '#059669' : '#dc2626' }}>
              {balanceSheetSummary.balanced ? '✅ Yes' : '❌ No'}
            </div>
            {balanceSheetSummary.variance !== 0 && (
              <div style={{ fontSize: 10, color: '#ef4444', marginTop: 2 }}>
                Variance: ${Math.abs(balanceSheetSummary.variance || 0).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ 
        background: auditFindings.material_misstatement ? '#fef2f2' : '#f0fdf4',
        border: `1px solid ${auditFindings.material_misstatement ? '#fca5a5' : '#86efac'}`,
        borderRadius: 8,
        padding: 16,
        marginBottom: 16
      }}>
        <h4 style={{ 
          margin: '0 0 12px 0', 
          fontSize: 16, 
          color: auditFindings.material_misstatement ? '#991b1b' : '#15803d' 
        }}>
          {auditFindings.material_misstatement ? '⚠️ Audit Findings' : '✅ Audit Findings'}
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Exceptions</div>
            <div style={{ fontSize: 18, fontWeight: 'bold', color: auditFindings.exceptions_identified > 0 ? '#f59e0b' : '#059669' }}>
              {auditFindings.exceptions_identified || 0}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Exception Amount</div>
            <div style={{ fontSize: 18, fontWeight: 'bold', color: auditFindings.exceptions_amount > 0 ? '#f59e0b' : '#059669' }}>
              ${(auditFindings.exceptions_amount || 0).toLocaleString()}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Material Misstatement</div>
            <div style={{ fontSize: 16, fontWeight: 'bold', color: auditFindings.material_misstatement ? '#dc2626' : '#059669' }}>
              {auditFindings.material_misstatement ? '❌ Yes' : '✅ No'}
            </div>
          </div>
          {auditFindings.supporting_document_flags_count > 0 && (
            <div>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Supporting Doc Flags</div>
              <div style={{ fontSize: 18, fontWeight: 'bold', color: '#f59e0b' }}>
                {auditFindings.supporting_document_flags_count}
              </div>
            </div>
          )}
          {auditFindings.total_review_items > auditFindings.exceptions_identified && (
            <div>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Total Review Items</div>
              <div style={{ fontSize: 18, fontWeight: 'bold', color: '#374151' }}>
                {auditFindings.total_review_items}
              </div>
            </div>
          )}
        </div>

        {auditFindings.detailed_exceptions && auditFindings.detailed_exceptions.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <strong style={{ fontSize: 13, color: '#991b1b' }}>Detailed Exceptions:</strong>
            {auditFindings.detailed_exceptions.map((exc, i) => (
              <div key={i} style={{ 
                background: '#fff',
                border: '1px solid #fca5a5',
                borderRadius: 4,
                padding: 10,
                marginTop: 8
              }}>
                <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 4 }}>
                  {exc.account} - {exc.source}
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
                  {exc.issue}
                </div>
                <div style={{ display: 'flex', gap: 12, fontSize: 11 }}>
                  <span style={{ color: '#dc2626' }}>
                    Amount: ${(exc.amount || 0).toLocaleString()}
                  </span>
                  <span style={{
                    padding: '2px 6px',
                    borderRadius: 4,
                    background: exc.severity === 'high' ? '#fef2f2' : '#fef3c7',
                    border: `1px solid ${exc.severity === 'high' ? '#fca5a5' : '#fbbf24'}`,
                    color: exc.severity === 'high' ? '#991b1b' : '#92400e'
                  }}>
                    {exc.severity}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ 
        background: '#faf5ff',
        border: '1px solid #c084fc',
        borderRadius: 8,
        padding: 16,
        marginBottom: 16
      }}>
        <h4 style={{ margin: '0 0 12px 0', fontSize: 16, color: '#7c3aed' }}>
          📋 Scope of Audit
        </h4>
        <div style={{ fontSize: 12, lineHeight: 1.8 }}>
          <div><strong>Standards:</strong> {scopeOfAudit.standards_followed?.join(', ')}</div>
          {scopeOfAudit.materiality && (
            <>
              <div style={{ marginTop: 8 }}>
                <strong>Planning Materiality:</strong> ${(scopeOfAudit.materiality.planning_materiality || 0).toLocaleString()}
              </div>
              <div>
                <strong>Performance Materiality:</strong> ${(scopeOfAudit.materiality.performance_materiality || 0).toLocaleString()}
              </div>
              <div>
                <strong>Trivial Threshold:</strong> ${(scopeOfAudit.materiality.trivial_threshold || 0).toLocaleString()}
              </div>
            </>
          )}
          <div style={{ marginTop: 8 }}>
            <strong>Accounts Tested:</strong> {scopeOfAudit.accounts_tested || 0}
          </div>
          <div>
            <strong>Material Accounts:</strong> {scopeOfAudit.material_accounts || 0}
          </div>
        </div>
      </div>

      {auditOpinion.basis_for_opinion && auditOpinion.basis_for_opinion.length > 0 && (
        <details style={{ marginBottom: 16 }}>
          <summary style={{ 
            cursor: 'pointer', 
            fontSize: 14, 
            fontWeight: 'bold',
            color: '#111827',
            marginBottom: 8
          }}>
            📝 Basis for Opinion
          </summary>
          <ul style={{ margin: '8px 0 0 20px', paddingLeft: 0, fontSize: 12, lineHeight: 1.6 }}>
            {auditOpinion.basis_for_opinion.map((item, i) => (
              <li key={i} style={{ marginBottom: 6 }}>{item}</li>
            ))}
          </ul>
        </details>
      )}

      {auditOpinion.emphasis_of_matter && auditOpinion.emphasis_of_matter.length > 0 && (
        <div style={{ 
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: 8,
          padding: 12,
          marginBottom: 16
        }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#92400e' }}>
            ⚠️ Emphasis of Matter
          </h4>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12 }}>
            {auditOpinion.emphasis_of_matter.map((item, i) => (
              <li key={i} style={{ marginBottom: 4 }}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      {managementRecs.length > 0 && (
        <details style={{ marginBottom: 16 }}>
          <summary style={{ 
            cursor: 'pointer', 
            fontSize: 14, 
            fontWeight: 'bold',
            color: '#111827',
            marginBottom: 8
          }}>
            💡 Management Recommendations ({managementRecs.length})
          </summary>
          <div style={{ marginTop: 12 }}>
            {managementRecs.map((rec, i) => (
              <div key={i} style={{ 
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                padding: 12,
                marginBottom: 8
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontSize: 10,
                    fontWeight: 'bold',
                    background: rec.priority === 'high' ? '#fef2f2' : rec.priority === 'medium' ? '#fef3c7' : '#f0f9ff',
                    border: `1px solid ${rec.priority === 'high' ? '#fca5a5' : rec.priority === 'medium' ? '#fbbf24' : '#bfdbfe'}`,
                    color: rec.priority === 'high' ? '#991b1b' : rec.priority === 'medium' ? '#92400e' : '#1e40af'
                  }}>
                    {rec.priority?.toUpperCase()}
                  </span>
                  <span style={{ fontWeight: 'bold', fontSize: 13 }}>{rec.area}</span>
                </div>
                <div style={{ fontSize: 12, marginBottom: 4 }}>
                  <strong>Recommendation:</strong> {rec.recommendation}
                </div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>
                  <strong>Rationale:</strong> {rec.rationale}
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {gaapCompliance.asc_210_presentation && (
        <details style={{ marginBottom: 16 }}>
          <summary style={{ 
            cursor: 'pointer', 
            fontSize: 14, 
            fontWeight: 'bold',
            color: '#111827',
            marginBottom: 8
          }}>
            📜 GAAP Compliance (ASC 210)
          </summary>
          <div style={{ 
            background: gaapCompliance.asc_210_presentation.compliant ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${gaapCompliance.asc_210_presentation.compliant ? '#86efac' : '#fca5a5'}`,
            borderRadius: 6,
            padding: 12,
            marginTop: 8
          }}>
            <div style={{ fontSize: 12, marginBottom: 8 }}>
              <strong>Status:</strong>{' '}
              <span style={{ color: gaapCompliance.asc_210_presentation.compliant ? '#059669' : '#dc2626' }}>
                {gaapCompliance.asc_210_presentation.compliant ? '✅ Compliant' : '❌ Non-Compliant'}
              </span>
            </div>
            {gaapCompliance.asc_210_presentation.issues && gaapCompliance.asc_210_presentation.issues.length > 0 && (
              <div>
                <strong style={{ fontSize: 12 }}>Issues:</strong>
                <ul style={{ margin: '4px 0 0 20px', paddingLeft: 0, fontSize: 11 }}>
                  {gaapCompliance.asc_210_presentation.issues.map((issue, i) => (
                    <li key={i}>{issue}</li>
                  ))}
                </ul>
              </div>
            )}
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 8 }}>
              {gaapCompliance.asc_210_presentation.notes}
            </div>
          </div>
        </details>
      )}

      {auditFindings.supporting_document_flags && auditFindings.supporting_document_flags.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <strong style={{ fontSize: 13, color: '#d97706' }}>
            📎 Supporting Document Flags ({auditFindings.supporting_document_flags.length}):
          </strong>
          {auditFindings.supporting_document_flags.map((flag, i) => (
            <div key={i} style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, padding: 10, marginTop: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 'bold', color: '#92400e', marginBottom: 3 }}>
                {flag.docName} — {flag.docType?.replace(/_/g, ' ')}
              </div>
              <div style={{ fontSize: 11, color: '#374151' }}>{flag.flag}</div>
              <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 3 }}>Source: supporting_document · Review required</div>
            </div>
          ))}
        </div>
      )}

      {data.ap_confirmations_summary && (
        <details style={{ marginBottom: 16 }}>
          <summary style={{
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 'bold',
            color: '#111827',
            marginBottom: 8
          }}>
            💳 AP Confirmations (Vendor)
          </summary>
          <div style={{
            background: '#fffbeb',
            border: '1px solid #f59e0b',
            borderRadius: 6,
            padding: 12,
            marginTop: 8
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
              <div><strong>Total Sent:</strong> {data.ap_confirmations_summary.total_confirmations_sent}</div>
              <div><strong>Responses:</strong> {data.ap_confirmations_summary.responses_received}</div>
              <div><strong>Response Rate:</strong> {data.ap_confirmations_summary.response_rate}</div>
              <div><strong>Matched:</strong> {data.ap_confirmations_summary.confirmations_matched}</div>
              {data.ap_confirmations_summary.exceptions_identified > 0 && (
                <div style={{ color: '#dc2626' }}>
                  <strong>⚠️ Vendor Disputes:</strong> {data.ap_confirmations_summary.exceptions_identified}
                </div>
              )}
              {data.ap_confirmations_summary.no_response_count > 0 && (
                <div style={{ color: '#f59e0b' }}>
                  <strong>No Response:</strong> {data.ap_confirmations_summary.no_response_count}
                </div>
              )}
            </div>
            {(data.ap_confirmations_summary.unrecorded_liability_exposure || 0) > 0 && (
              <div style={{
                marginTop: 10,
                padding: '8px 12px',
                background: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: 4
              }}>
                <div style={{ fontSize: 12, color: '#92400e', fontWeight: 'bold' }}>
                  🚨 Unrecorded Liability Exposure: ${(data.ap_confirmations_summary.unrecorded_liability_exposure || 0).toLocaleString()}
                </div>
                <div style={{ fontSize: 11, color: '#92400e', marginTop: 4 }}>
                  Vendor-confirmed balances exceed book balances — investigate for unrecorded invoices.
                </div>
              </div>
            )}
            {data.ap_confirmations_summary.total_amount_per_books > 0 && (
              <div style={{ marginTop: 8, fontSize: 11, color: '#6b7280' }}>
                Total AP per books: ${(data.ap_confirmations_summary.total_amount_per_books || 0).toLocaleString()}
                {data.ap_confirmations_summary.total_amount_confirmed > 0 && (
                  <> | Vendor-confirmed: ${(data.ap_confirmations_summary.total_amount_confirmed || 0).toLocaleString()}</>
                )}
              </div>
            )}
          </div>
        </details>
      )}

      {data.supporting_documents_summary && (
        <details style={{ marginBottom: 16 }}>
          <summary style={{ cursor: 'pointer', fontSize: 14, fontWeight: 'bold', color: '#111827', marginBottom: 8 }}>
            📎 Supporting Documents Summary ({data.supporting_documents_summary.count} uploaded
            {data.supporting_documents_summary.total_audit_flags > 0
              ? ` · ⚠️ ${data.supporting_documents_summary.total_audit_flags} flags`
              : ' · ✅ No flags'})
          </summary>
          <div style={{ marginTop: 8 }}>
            {data.supporting_documents_summary.documents?.map((d, i) => (
              <div key={i} style={{
                background: d.audit_flags_count > 0 ? '#fffbeb' : '#f0fdf4',
                border: `1px solid ${d.audit_flags_count > 0 ? '#fde68a' : '#86efac'}`,
                borderRadius: 6, padding: 10, marginBottom: 8
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#111827' }}>{d.displayName}</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {d.completeness_score && (
                      <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 999, background: '#e0e7ff', color: '#3730a3', border: '1px solid #c7d2fe' }}>
                        {d.completeness_score <= 1 ? Math.round(d.completeness_score * 100) : Math.round(d.completeness_score)}/100
                      </span>
                    )}
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 999,
                      background: d.extractionStatus === 'success' ? '#d1fae5' : '#fef3c7',
                      color: d.extractionStatus === 'success' ? '#065f46' : '#92400e',
                      border: `1px solid ${d.extractionStatus === 'success' ? '#6ee7b7' : '#fde68a'}` }}>
                      {d.extractionStatus}
                    </span>
                  </div>
                </div>
                {d.key_findings?.slice(0, 3).map((f, j) => (
                  <div key={j} style={{ fontSize: 11, color: '#374151', marginBottom: 2 }}>• {f}</div>
                ))}
                {d.audit_flags_count > 0 && (
                  <div style={{ marginTop: 6, padding: 6, background: '#fef2f2', borderRadius: 4, border: '1px solid #fca5a5' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#991b1b', marginBottom: 3 }}>
                      ⚠️ {d.audit_flags_count} Audit Flag{d.audit_flags_count !== 1 ? 's' : ''}
                    </div>
                    {d.audit_flags?.map((f, j) => (
                      <div key={j} style={{ fontSize: 10, color: '#7f1d1d' }}>• {f}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </details>
      )}

      {data.audit_completion && (
        <div style={{ 
          background: '#f0fdf4',
          border: '1px solid #22c55e',
          borderRadius: 8,
          padding: 12,
          fontSize: 12
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <strong>✅ Audit Completed:</strong> {new Date(data.audit_completion.completed_at).toLocaleString()}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {data.audit_completion.azure_government_compliance && (
                <span style={{
                  padding: '2px 8px',
                  borderRadius: 4,
                  fontSize: 10,
                  background: '#dbeafe',
                  border: '1px solid #3b82f6',
                  color: '#1e40af'
                }}>
                  Azure Gov ✓
                </span>
              )}
              {data.audit_completion.fedramp_compliant && (
                <span style={{
                  padding: '2px 8px',
                  borderRadius: 4,
                  fontSize: 10,
                  background: '#dbeafe',
                  border: '1px solid #3b82f6',
                  color: '#1e40af'
                }}>
                  FedRAMP ✓
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** ------------------------------------------------------------------
 * StepResultsViewer (includes all workflow sections)
 * ----------------------------------------------------------------- */
const StepResultsViewer = ({ doc, onClose, onRefresh }) => {
  const [activeIdx, setActiveIdx] = useState(0);

  // ⭐ Human-in-the-Loop: effective comparison state for this doc.
  // { mode, modeSource, blindActive, humanFindingsSubmittedAt }
  // Until it loads, fail closed (treat blind as active) so AI findings
  // never flash on screen before the mode is known.
  const [comparisonInfo, setComparisonInfo] = useState(null);
  const isFsAudit = doc?.workflowAssignment?.templateId === 'financial_statement_audit_v1';
  const humanSubmittedAt = doc?.balanceSheetAudit?.humanFindings?.submittedAt || null;

  useEffect(() => {
    let cancelled = false;
    const loadComparisonInfo = async () => {
      if (!isFsAudit) { setComparisonInfo({ mode: 'review', blindActive: false }); return; }
      try {
        const res = await axios.get(`${API_URL}/admin/rpa/docs/${doc._id}/comparison-mode`, { withCredentials: true });
        if (!cancelled && res.data?.success) setComparisonInfo(res.data);
      } catch {
        // Fail closed: unknown mode on an FS audit = blind until submitted
        if (!cancelled) setComparisonInfo({ mode: 'blind', blindActive: !humanSubmittedAt });
      }
    };
    loadComparisonInfo();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?._id, humanSubmittedAt]);

  const blindActive = isFsAudit && (comparisonInfo ? !!comparisonInfo.blindActive : !humanSubmittedAt);

  const renderStepContent = (r) => {
    if (!r || r.status !== 'completed' || !r.data) {
      return (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>
            {r?.status === 'in_progress' ? '⏳' : '⏸'}
          </div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            {r?.status === 'in_progress' ? 'In Progress' : 'Step Not Yet Run'}
          </div>
        </div>
      );
    }
    switch (r.id) {
      case 'classification':              return renderClassification(r.data);
      case 'soc_analysis':                return renderSOCAnalysis(r.data);
      case 'control_objectives':          return renderControlObjectives(r.data);
      case 'testing_attributes':          return renderTestingAttributes(r.data);
      case 'population_data':             return renderPopulationData(r.data);
      case 'testing_results':             return renderTestingResults(r.data);
      case 'regulatory_analysis':         return renderRegulatory(r.data);
      case 'financial_data':              return renderFinancial(r.data);
      case 'compliance_testing_results':  return renderCompliance(r.data);
      case 'financial_statement_analysis': return renderFinancialStatementAnalysis(r.data);
      case 'extracted_balances':          return renderExtractedBalances(r.data);
      case 'materiality':                 return renderMateriality(r.data);
      case 'trial_balance':               return renderTrialBalance(r.data);
      case 'income_statement':            return renderIncomeStatement(r.data);
      case 'bank_statements':             return renderBankStatements(r.data);
      case 'bank_reconciliation':         return renderBankReconciliation(r.data);
      case 'bank_confirmations':          return renderBankConfirmations(r.data);
      case 'ar_confirmations':            return renderARConfirmations(r.data);
      case 'ap_confirmations':            return renderAPConfirmations(r.data);
      case 'inventory_confirmations':     return renderInventoryConfirmations(r.data);
      case 'inventory_observation':       return renderInventoryObservation(r.data);
      case 'assertions':                  return renderAssertions(r.data);
      case 'substantive_testing':         return renderSubstantiveTesting(r.data);
      case 'audit_procedures':            return renderAuditProcedures(r.data);
      case 'financial_audit_results':     return renderFinancialAuditResults(r.data);
      case 'final_findings':              return renderFindings(r.data);
      case 'final_findings_locked':       return (
        <div style={{ padding: 40, textAlign: 'center', color: '#6d28d9', background: '#faf5ff', border: '1px dashed #c4b5fd', borderRadius: 10 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🔒</div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>AI Findings Hidden — Blind Comparison Mode</div>
          <div style={{ fontSize: 12, marginTop: 6, color: '#7c3aed' }}>
            AI audit findings were generated{r.timestamp ? ` on ${fmtDate(r.timestamp)}` : ''} but are withheld until
            you submit your independent findings below. This preserves the independence of your professional judgment
            for the dual-track comparison workpaper.
          </div>
        </div>
      );
      case 'upload_supporting_documents': return renderSupportingDocuments(r.data);
      default:                            return <pre style={jsonStyle}>{JSON.stringify(r.data, null, 2)}</pre>;
    }
  };

  const results = (() => {
    const arr = [];

    if (doc.classification && doc.classificationDetails) {
      arr.push({
        id: 'classification',
        title: 'Document Classification',
        status: 'completed',
        timestamp: doc.classifiedAt || doc.updatedAt,
        data: { classification: doc.classification, details: doc.classificationDetails }
      });
    }

    if (doc.workflowAssignment && doc.workflowAssignment.templateId) {
      arr.push({
        id: 'workflow_assignment',
        title: 'Workflow Assignment',
        status: 'completed',
        timestamp: doc.workflowAssignedAt || doc.updatedAt,
        data: doc.workflowAssignment
      });
    }

    // SOC analysis
    if (doc.socFindings || doc.socAnalysisQueuedAt) {
      if (doc.socFindings) {
        let findings = doc.socFindings;
        try {
          if (typeof findings === 'string') findings = JSON.parse(findings);
        } catch {}
        arr.push({
          id: 'soc_analysis',
          title: 'SOC Analysis',
          status: 'completed',
          timestamp: doc.lastAnalyzedAt,
          data: findings
        });
      } else {
        arr.push({
          id: 'soc_analysis',
          title: 'SOC Analysis',
          status: 'in_progress',
          timestamp: doc.socAnalysisQueuedAt
        });
      }
    }

    // Control objectives
    if (doc.controlObjectives || doc.controlObjectivesQueuedAt) {
      let data = doc.controlObjectives;
      try {
        if (typeof data === 'string') data = JSON.parse(data);
      } catch {}
      arr.push({
        id: 'control_objectives',
        title: 'Control Objectives',
        status: doc.controlObjectives ? 'completed' : 'in_progress',
        timestamp: doc.lastControlObjectivesExtractedAt || doc.controlObjectivesQueuedAt,
        data
      });
    }

    // Testing attributes
    if (doc.testingAttributes || doc.testingAttributesQueuedAt) {
      let data = doc.testingAttributes;
      try {
        if (typeof data === 'string') data = JSON.parse(data);
      } catch {}
      arr.push({
        id: 'testing_attributes',
        title: 'Testing Attributes',
        status: doc.testingAttributes ? 'completed' : 'in_progress',
        timestamp: doc.lastTestingAttributesAt || doc.testingAttributesQueuedAt,
        data
      });
    }

    // Population
    if (doc.populationData && (doc.populationData.uploadedAt || doc.populationData.rowCount || doc.populationData.fileName || doc.populationData.originalName)) {
      arr.push({
        id: 'population_data',
        title: 'Population Data',
        status: 'completed',
        timestamp: doc.populationData.uploadedAt,
        data: doc.populationData
      });
    }

    // SOC/CUEC testing
    if (doc.testingResults || doc.testingQueuedAt) {
      let data = doc.testingResults;
      try {
        if (typeof data === 'string') data = JSON.parse(data);
      } catch {}
      arr.push({
        id: 'testing_results',
        title: 'SOC/CUEC Testing',
        status: doc.testingResults ? 'completed' : 'in_progress',
        timestamp: doc.lastTestingCompletedAt || doc.testingQueuedAt,
        data
      });
    }

    // WSFR
    if (doc.regulatoryFindings || doc.regulatoryAnalysisQueuedAt) {
      if (doc.regulatoryFindings) {
        let reg = doc.regulatoryFindings;
        try {
          if (typeof reg === 'string') reg = JSON.parse(reg);
        } catch {}
        arr.push({
          id: 'regulatory_analysis',
          title: 'Regulatory Analysis',
          status: 'completed',
          timestamp: doc.lastRegulatoryAnalysisAt,
          data: reg
        });
      } else {
        arr.push({
          id: 'regulatory_analysis',
          title: 'Regulatory Analysis',
          status: 'in_progress',
          timestamp: doc.regulatoryAnalysisQueuedAt
        });
      }
    }

    if (doc.financialData) {
      arr.push({
        id: 'financial_data',
        title: 'Financial Data',
        status: 'completed',
        timestamp: doc.financialData.uploadedAt,
        data: doc.financialData
      });
    }

    if (doc.complianceTestingResults || doc.complianceTestingQueuedAt) {
      if (doc.complianceTestingResults) {
        let data = doc.complianceTestingResults;
        try {
          if (typeof data === 'string') data = JSON.parse(data);
        } catch {}
        arr.push({
          id: 'compliance_testing_results',
          title: 'FWS WSFR Compliance Testing',
          status: 'completed',
          timestamp: doc.lastComplianceTestingAt || doc.lastComplianceTestingCompletedAt,
          data
        });
      } else {
        arr.push({
          id: 'compliance_testing_results',
          title: 'FWS WSFR Compliance Testing',
          status: 'in_progress',
          timestamp: doc.complianceTestingQueuedAt
        });
      }
    }

    // Financial Statement Audit results
    if (doc.balanceSheetAudit?.financialStatementAnalysis || doc.financialStatementAnalysis) {
      arr.push({
        id: 'financial_statement_analysis',
        title: 'Financial Statement Analysis',
        status: 'completed',
        timestamp: doc.lastFinancialAnalysisAt,
        data: doc.balanceSheetAudit?.financialStatementAnalysis || doc.financialStatementAnalysis
      });
    }

    if (doc.balanceSheetAudit?.extractedBalances || doc.extractedBalances) {
      let data = doc.balanceSheetAudit?.extractedBalances || doc.extractedBalances;

      try {
        if (typeof data === 'string') {
          data = JSON.parse(data);
        }
      } catch (e) {
        console.warn('[FS AUDIT] Failed to parse extractedBalances JSON', e, { raw: data });
      }

      arr.push({
        id: 'extracted_balances',
        title: 'Extracted Balances',
        status: 'completed',
        timestamp: doc.lastBalancesExtractedAt,
        data
      });
    }

    if (doc.balanceSheetAudit?.materialityAssessment || doc.materialityAssessment) {
      arr.push({
        id: 'materiality',
        title: 'Materiality',
        status: 'completed',
        timestamp: doc.lastMaterialityAssessedAt,
        data: doc.balanceSheetAudit?.materialityAssessment || doc.materialityAssessment
      });
    }

    if (doc.balanceSheetAudit?.trialBalance || doc.trialBalance) {
      arr.push({
        id: 'trial_balance',
        title: 'Trial Balance',
        status: 'completed',
        timestamp: (doc.balanceSheetAudit?.trialBalance || doc.trialBalance)?.uploadedAt,
        data: doc.balanceSheetAudit?.trialBalance || doc.trialBalance
      });
    }

    // ✅ Income Statement
    if (doc.balanceSheetAudit?.incomeStatement || doc.lastIncomeStatementUploadedAt) {
      arr.push({
        id: 'income_statement',
        title: 'Income Statement',
        status: 'completed',
        timestamp: doc.lastIncomeStatementUploadedAt || doc.balanceSheetAudit?.incomeStatement?.uploadedAt,
        data: doc.balanceSheetAudit?.incomeStatement || {
          uploadedAt: doc.lastIncomeStatementUploadedAt,
          summary: doc.balanceSheetAudit?.incomeStatement?.summary,
          note: 'Income statement uploaded'
        }
      });
    }

    // ✅ NEW: Bank Statements
    if (doc.balanceSheetAudit?.bankStatements || doc.lastBankStatementsUploadedAt) {
      arr.push({
        id: 'bank_statements',
        title: 'Bank Statements',
        status: 'completed',
        timestamp: doc.lastBankStatementsUploadedAt,
        data: doc.balanceSheetAudit?.bankStatements || []
      });
    }

    // ✅ NEW: Bank Reconciliation
    if (doc.balanceSheetAudit?.bankReconciliation || doc.lastBankReconciliationUploadedAt) {
      arr.push({
        id: 'bank_reconciliation',
        title: 'Bank Reconciliation',
        status: 'completed',
        timestamp: doc.lastBankReconciliationUploadedAt,
        data: doc.balanceSheetAudit?.bankReconciliation || {}
      });
    }

    // ⭐ NEW: Bank Confirmations (Step 10)
    if (doc.balanceSheetAudit?.bankConfirmations || doc.lastBankConfirmationUploadedAt) {
      arr.push({
        id: 'bank_confirmations',
        title: 'Bank Confirmations',
        status: 'completed',
        timestamp: doc.lastBankConfirmationUploadedAt,
        data: doc.balanceSheetAudit?.bankConfirmations || {}
      });
    }

    // ⭐ NEW: AR Confirmations (Step 11)
    if (doc.balanceSheetAudit?.arConfirmations || doc.lastARConfirmationUploadedAt) {
      arr.push({
        id: 'ar_confirmations',
        title: 'AR Confirmations',
        status: 'completed',
        timestamp: doc.lastARConfirmationUploadedAt,
        data: doc.balanceSheetAudit?.arConfirmations || {}
      });
    }

    // ✅ NEW: AP Confirmations (Step 12 - Vendor/Supplier)
    if (doc.balanceSheetAudit?.apConfirmations || doc.lastAPConfirmationUploadedAt) {
      arr.push({
        id: 'ap_confirmations',
        title: '💳 AP Confirmations',
        status: 'completed',
        timestamp: doc.lastAPConfirmationUploadedAt,
        data: doc.balanceSheetAudit?.apConfirmations || {}
      });
    }

    // ⭐ NEW: Inventory Confirmations (Step 12)
    if (doc.balanceSheetAudit?.inventoryConfirmations || doc.lastInventoryConfirmationUploadedAt) {
      arr.push({
        id: 'inventory_confirmations',
        title: 'Inventory Confirmations',
        status: 'completed',
        timestamp: doc.lastInventoryConfirmationUploadedAt,
        data: doc.balanceSheetAudit?.inventoryConfirmations || {}
      });
    }

    // ⭐ NEW: Physical Inventory Observation (Step 13)
    if (doc.balanceSheetAudit?.inventoryObservation || doc.lastInventoryObservationUploadedAt) {
      arr.push({
        id: 'inventory_observation',
        title: 'Physical Inventory Observation',
        status: 'completed',
        timestamp: doc.lastInventoryObservationUploadedAt,
        data: doc.balanceSheetAudit?.inventoryObservation || {}
      });
    }

    // ⭐ RESTORED: Supporting Documents (Step 15 — all 23 generic doc types).
    // Stored as balanceSheetAudit.supportingDocuments[docType]. Without this
    // entry renderSupportingDocuments() is defined but never reachable.
    if (doc.balanceSheetAudit?.supportingDocuments &&
        Object.keys(doc.balanceSheetAudit.supportingDocuments).length > 0) {
      arr.push({
        id: 'upload_supporting_documents',
        title: '📎 Supporting Documents',
        status: 'completed',
        timestamp: doc.updatedAt,
        data: doc.balanceSheetAudit.supportingDocuments
      });
    }

    if (doc.balanceSheetAudit?.assertionTests || doc.assertionTests) {
      arr.push({
        id: 'assertions',
        title: 'Assertion Testing',
        status: 'completed',
        timestamp: doc.lastAssertionsTestedAt,
        data: doc.balanceSheetAudit?.assertionTests || doc.assertionTests
      });
    }

    if (doc.balanceSheetAudit?.substantiveTestResults || doc.substantiveTestResults) {
      let data = doc.balanceSheetAudit?.substantiveTestResults || doc.substantiveTestResults;
      
      try {
        if (typeof data === 'string') {
          data = JSON.parse(data);
        }
      } catch (e) {
        console.warn('[SUBSTANTIVE] Failed to parse JSON', e);
      }
      
      arr.push({
        id: 'substantive_testing',
        title: 'Substantive Testing',
        status: 'completed',
        timestamp: doc.lastSubstantiveTestingAt,
        data
      });
    }

    if (doc.balanceSheetAudit?.auditProcedures || doc.auditProcedures) {
      arr.push({
        id: 'audit_procedures',
        title: 'Audit Procedures',
        status: 'completed',
        timestamp: doc.lastAuditProceduresFormulatedAt,
        data: doc.balanceSheetAudit?.auditProcedures || doc.auditProcedures
      });
    }

    if (doc.balanceSheetAudit?.financialAuditResults || doc.financialAuditResults) {
      arr.push({
        id: 'financial_audit_results',
        title: 'Financial Audit Testing',
        status: 'completed',
        timestamp: doc.lastFinancialAuditTestingAt,
        data: doc.balanceSheetAudit?.financialAuditResults || doc.financialAuditResults
      });
    }

    if (doc.balanceSheetAudit?.finalFindings || doc.finalFindings) {
      // ⭐ Human-in-the-Loop: while blind is active, findings exist but their
      // content is withheld — show a locked placeholder instead of the data.
      if (blindActive) {
        arr.push({
          id: 'final_findings_locked',
          title: 'Generate Findings',
          status: 'completed',
          timestamp: doc.lastFindingsGeneratedAt,
          data: { locked: true }
        });
      } else {
        arr.push({
          id: 'final_findings',
          title: 'Generate Findings',
          status: 'completed',
          timestamp: doc.lastFindingsGeneratedAt,
          data: doc.balanceSheetAudit?.finalFindings || doc.finalFindings
        });
      }
    } else if (doc.workflowAssignment?.templateId === 'financial_statement_audit_v1' && 
               (doc.balanceSheetAudit?.financialAuditResults || doc.financialAuditResults)) {
      arr.push({
        id: 'final_findings',
        title: 'Generate Findings',
        status: 'pending',
        timestamp: null,
        data: null
      });
    }

    return arr;
  })();

  const safeIdx        = Math.min(activeIdx, results.length - 1);
  const active         = results[safeIdx];
  const completedCount = results.filter((r) => r.status === 'completed').length;

  return (
    <div style={viewerContainer}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, paddingBottom: 10, borderBottom: '1px solid #e5e7eb' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{doc.originalName || doc.fileName}</div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{completedCount} of {results.length} steps completed</div>
        </div>
        <button onClick={onClose} style={closeButton}>✕ Close</button>
      </div>

      {/* Progress bar */}
      <div style={progressBarContainer}>
        <div style={progressBarFill(completedCount, results.length)} />
      </div>

      {/* ── Two-panel layout ── */}
      <div style={{ display: 'flex', gap: 0, height: 520, border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden', marginTop: 10 }}>

        {/* Left: scrollable step navigator (192px) */}
        <div style={{ width: 192, minWidth: 192, borderRight: '1px solid #e5e7eb', overflowY: 'auto', background: '#f9fafb' }}>
          {results.map((r, idx) => {
            const isActive = idx === safeIdx;
            const icon = r.status === 'completed' ? '✅' : r.status === 'in_progress' ? '⏳' : '⏸';
            return (
              <div
                key={r.id}
                onClick={() => setActiveIdx(idx)}
                style={{
                  padding: '9px 12px',
                  cursor: 'pointer',
                  borderLeft: isActive ? '3px solid #3b82f6' : '3px solid transparent',
                  background: isActive ? '#eff6ff' : 'transparent',
                  borderBottom: '1px solid #f3f4f6',
                  transition: 'background 0.12s',
                }}
              >
                <div style={{ fontSize: 10, marginBottom: 2 }}>{icon}</div>
                <div style={{ fontSize: 11, fontWeight: isActive ? 700 : 500, color: isActive ? '#1d4ed8' : '#374151', lineHeight: 1.3 }}>
                  {r.title}
                </div>
                {r.timestamp && (
                  <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 2 }}>{fmtDate(r.timestamp)}</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Right: full content pane */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#fff' }}>
          {active && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#111827' }}>{active.title}</div>
                  {active.timestamp && (
                    <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{fmtDate(active.timestamp)}</div>
                  )}
                </div>
                <span style={{ fontSize: 10, color: '#6b7280', background: '#f3f4f6', borderRadius: 4, padding: '2px 8px', whiteSpace: 'nowrap' }}>
                  Step {safeIdx + 1} of {results.length}
                </span>
              </div>

              {renderStepContent(active)}

              <details style={{ marginTop: 16 }}>
                <summary style={{ cursor: 'pointer', fontSize: 11, color: '#9ca3af' }}>🔍 Raw JSON</summary>
                <pre style={jsonStyle}>{JSON.stringify(active.data, null, 2)}</pre>
              </details>
            </>
          )}
        </div>
      </div>

      {/* ── Prev / Next navigation ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
        <button
          onClick={() => setActiveIdx(i => Math.max(0, i - 1))}
          disabled={safeIdx === 0}
          style={{ ...navBtn, opacity: safeIdx === 0 ? 0.35 : 1 }}
        >
          ← Prev
        </button>
        <span style={{ fontSize: 11, color: '#6b7280' }}>
          Step {safeIdx + 1} of {results.length} · {active?.status || ''}
        </span>
        <button
          onClick={() => setActiveIdx(i => Math.min(results.length - 1, i + 1))}
          disabled={safeIdx === results.length - 1}
          style={{ ...navBtn, opacity: safeIdx === results.length - 1 ? 0.35 : 1 }}
        >
          Next →
        </button>
      </div>

      {/* ⭐ Human-in-the-Loop Dual-Track (Feature 3) — FS audits only.
          Findings entry opens once audit testing evidence exists; the
          comparison workpaper renders after submission + AI findings. */}
      {isFsAudit &&
        (doc.balanceSheetAudit?.financialAuditResults || doc.financialAuditResults ||
         doc.balanceSheetAudit?.finalFindings || doc.finalFindings) && (
        <HumanFindingsPanel
          doc={doc}
          comparisonInfo={comparisonInfo}
          onChanged={onRefresh}
        />
      )}
      {isFsAudit && humanSubmittedAt &&
        (doc.balanceSheetAudit?.finalFindings || doc.finalFindings) && (
        <FindingsComparisonView docId={doc._id} />
      )}

    </div>
  );
};

// Styles
const summaryBox = {
  background: '#f0f9ff',
  border: '1px solid #0ea5e9',
  borderRadius: 6,
  padding: 10,
  marginBottom: 12
};

const controlCard = {
  background: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: 4,
  padding: 8,
  marginBottom: 8
};

const pill = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 999,
  background: '#eef2ff',
  border: '1px solid #c7d2fe',
  color: '#3730a3',
  fontSize: 11
};

const viewerContainer = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: 16,
  marginTop: 8,
  maxWidth: '100%',
  overflow: 'hidden'
};

// eslint-disable-next-line no-unused-vars
const overviewStyle = {
  padding: 8
};

const progressBarContainer = {
  height: 8,
  background: '#f3f4f6',
  borderRadius: 4,
  overflow: 'hidden',
  marginBottom: 8
};

const progressBarFill = (completed, total) => ({
  width: `${(Math.min(completed, total) / Math.max(total || 1, 1)) * 100}%`,
  height: '100%',
  background: '#10b981',
  transition: 'width 0.3s ease'
});

// stepsGrid and stepCard replaced by two-panel navigator
// eslint-disable-next-line no-unused-vars
const stepsGrid = { display: 'flex', flexDirection: 'column', gap: 0 };
// eslint-disable-next-line no-unused-vars
const stepCard  = (_status) => ({});

const navBtn = {
  background: '#f3f4f6',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  padding: '5px 14px',
  fontSize: 12,
  cursor: 'pointer',
  color: '#374151',
  fontWeight: 500,
};

// eslint-disable-next-line no-unused-vars
const viewButton = {
  background: '#3b82f6',
  color: 'white',
  border: 'none',
  padding: '4px 8px',
  borderRadius: 4,
  fontSize: 10,
  marginTop: 8,
  cursor: 'pointer'
};

const closeButton = {
  background: 'none',
  border: 'none',
  fontSize: 14,
  cursor: 'pointer',
  color: '#6b7280'
};

const jsonStyle = {
  background: '#f8f9fa',
  border: '1px solid #e5e7eb',
  borderRadius: 4,
  padding: 8,
  fontSize: 11,
  maxHeight: 240,
  overflow: 'auto'
};

/** ------------------------------------------------------------------
 * Main Dashboard Component
 * ----------------------------------------------------------------- */
const RpaDashboard = () => {
  const dispatch = useDispatch();
  // Expose dispatch globally so EngagementSetupCard can set agent context
  // after creating a new engagement without needing prop drilling
  React.useEffect(() => { window.__RPA_DISPATCH__ = dispatch; }, [dispatch]);
  const [loading, setLoading] = useState(false);
  const [docs, setDocs] = useState([]);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ client: '', project: '' });
  const [summaryError, setSummaryError] = useState('');
  const [counts, setCounts] = useState({ projects: 0, docs: 0, tasks: 0 });
  const [latestDocs, setLatestDocs] = useState([]);
  const [latestTasks, setLatestTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectsError, setProjectsError] = useState('');
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [connectionError, setConnectionError] = useState('');
  const [realtimeUpdates, setRealtimeUpdates] = useState([]);
  const [viewingResults, setViewingResults] = useState(null);
  const [selectedAgentDocId, setSelectedAgentDocId] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditDocId, setAuditDocId] = useState('');
  const [membersOpen, setMembersOpen] = useState(false);
  const [tasksOpen, setTasksOpen] = useState(false);
  const [activeProject, setActiveProject] = useState(null);
  // ⭐ Human-in-the-Loop: per-doc effective comparison state
  // { [docId]: { mode, blindActive, humanFindingsSubmittedAt } }
  const [comparisonInfoMap, setComparisonInfoMap] = useState({});

  const socketRef = useRef(null);
  const perDocListenersRef = useRef({});
  const subscribedDocIdsRef = useRef([]);
  const fetchDocsTimerRef = useRef(null);

  const getSocket = useCallback(() => {
    if (typeof window !== 'undefined' && window.__RPA_SOCKET__) return window.__RPA_SOCKET__;
    const s = io(SOCKET_URL, {
      withCredentials: true,
      autoConnect: true,
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      timeout: 20000,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5000
    });
    if (typeof window !== 'undefined') window.__RPA_SOCKET__ = s;
    return s;
  }, []);

  /** Data fetchers */
  const fetchDocs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${API_URL}/admin/rpa/docs`, {
        params: {
          client: filters.client || undefined,
          project: filters.project || undefined,
          _t: Date.now(),
          _socket: socketConnected ? 'connected' : 'disconnected'
        },
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache'
        },
        withCredentials: true
      });
      const serverItems = res.data?.items || res.data?.docs || res.data || [];
      const items = (Array.isArray(serverItems) ? serverItems : []).map((d) => ({ ...d }));
      setDocs(items);
    } catch (err) {
      console.error('[RPA] fetchDocs failed:', err);
      setError('Failed to load documents.');
    } finally {
      setLoading(false);
    }
  }, [filters.client, filters.project, socketConnected]);

  const debouncedFetchDocs = useCallback((delay = 1500) => {
    if (fetchDocsTimerRef.current) {
      clearTimeout(fetchDocsTimerRef.current);
    }
    
    fetchDocsTimerRef.current = setTimeout(() => {
      console.log('[RPA] Debounced fetch triggered');
      fetchDocs();
    }, delay);
  }, [fetchDocs]);

  const fetchSummary = useCallback(async () => {
    try {
      setSummaryError('');
      const res = await axios.get(`${API_URL}/admin/rpa/dashboard`, { withCredentials: true });
      const { counts: c = {}, latestDocs: ld = [], latestTasks: lt = [] } = res.data || {};
      setCounts({ projects: c.projects ?? 0, docs: c.docs ?? 0, tasks: c.tasks ?? 0 });
      setLatestDocs(ld || []);
      setLatestTasks(lt || []);
    } catch (err) {
      console.error('[RPA] fetchSummary failed:', err);
      setSummaryError('Failed to load summary.');
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      setLoadingProjects(true);
      setProjectsError('');
      const res = await axios.get(`${API_URL}/admin/rpa/projects`, { withCredentials: true });
      setProjects(res.data?.projects || []);
    } catch (err) {
      console.error('[RPA] fetchProjects failed:', err);
      setProjectsError('Failed to load projects.');
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  /** Socket (global) */
  useEffect(() => {
    const socket = getSocket();
    socketRef.current = socket;

    const onConnect = () => {
      setSocketConnected(true);
      setConnectionError('');
      socket.emit('rpa_dashboard_ready');
      if (docs.length) {
        const ids = docs.map((d) => d._id).filter(Boolean);
        socket.emit('rpa_subscribe_docs', { docIds: ids });
        subscribedDocIdsRef.current = ids;
      }
    };

    const onConnectError = (err) => {
      setSocketConnected(false);
      setConnectionError(err?.message || 'Connection failed');
    };

    const onDisconnect = (reason) => {
      setSocketConnected(false);
      setConnectionError(`Disconnected: ${reason}`);
    };

    const onGlobalUpdate = (evt) => {
      if (!evt?.docId || !STEP_EVENT_TYPES.has(evt.type)) return;

      setRealtimeUpdates((prev) => [
        {
          ...evt,
          receivedAt: new Date().toISOString(),
          id: `${evt.docId}:${evt.type}:${Date.now()}`
        },
        ...prev.slice(0, 49)
      ]);

      if (evt.data) {
        setDocs((prev) =>
          prev.map((d) => {
            if (d._id !== evt.docId) return d;

            console.log('[RPA] Updating doc:', d._id, 'with event:', evt.type);
            console.log('[RPA] Event data keys:', Object.keys(evt.data));
            console.log('[RPA] Current balanceSheetAudit keys:', Object.keys(d.balanceSheetAudit || {}));
            
            const merged = deepMerge(d, evt.data);
            
            console.log('[RPA] Merged balanceSheetAudit keys:', Object.keys(merged.balanceSheetAudit || {}));

            if (evt.data?.balanceSheetAudit || d.balanceSheetAudit) {
              merged.balanceSheetAudit = {
                ...d.balanceSheetAudit,
                ...evt.data?.balanceSheetAudit
              };
            }

            if (merged.workflowAssignment?.steps) {
              merged.workflowAssignment = {
                ...merged.workflowAssignment,
                steps: merged.workflowAssignment.steps.map((step) => {
                  const eventAliases = STEP_ALIASES[evt.type] || [];
                  const shouldComplete = eventAliases.includes(step.id) || evt.step === step.id;

                  if (shouldComplete) {
                    return {
                      ...step,
                      status: evt.status || 'completed',
                      completedAt: evt.timestamp || new Date().toISOString()
                    };
                  }
                  return step;
                })
              };
            }

            return merged;
          })
        );
      }

      if (evt.type === 'workflow_assigned' && evt.workflow) {
        setDocs((prev) =>
          prev.map((d) => (d._id === evt.docId ? { ...d, workflowAssignment: evt.workflow } : d))
        );
      }

      // ── Supporting doc upload: reset downstream steps to pending ──────────
      if (evt.type === 'workflow_reset_for_retesting') {
        const RESET_STEP_IDS = new Set([
          'test_assertions',
          'conduct_substantive_testing',
          'formulate_audit_procedures',
          'conduct_audit_testing',
          'generate_findings',
        ]);
        setDocs((prev) =>
          prev.map((d) => {
            if (d._id !== evt.docId) return d;
            const updatedSteps = (d.workflowAssignment?.steps || []).map(step =>
              RESET_STEP_IDS.has(step.id) ? { ...step, status: 'pending', completedAt: undefined } : step
            );
            return {
              ...d,
              workflowAssignment: { ...d.workflowAssignment, steps: updatedSteps, status: 'in_progress' },
              balanceSheetAudit: {
                ...(d.balanceSheetAudit || {}),
                assertionTests:         undefined,
                substantiveTestResults: undefined,
                finalFindings:          undefined,
                workpaperPackage:       undefined,
              },
              auditProcedures:      undefined,
              financialAuditResults: undefined,
            };
          })
        );
        setRealtimeUpdates((prev) => [
          {
            ...evt,
            receivedAt: new Date().toISOString(),
            id: `${evt.docId}:reset:${Date.now()}`,
            _isBanner: true,
            _bannerMessage: `📎 ${evt.data?.displayName || 'Supporting document'} uploaded — testing steps reset. Re-run from Assertion Testing to incorporate all supporting documents.`,
          },
          ...prev.slice(0, 49),
        ]);
        setTimeout(() => fetchDocs(), 1500);
        return;
      }

      const financialAuditEvents = [
        'balance_extraction_complete',
        'balances_extracted',
        'extract_balances_complete',
        'materiality_assessed',
        'assess_materiality_complete',
        'trial_balance_uploaded',
        'upload_trial_balance_complete',
        'income_statement_uploaded',
        'upload_income_statement_complete',
        // ⭐ Confirmation uploads — controllers send sparse socket data so always refetch
        'bank_statements_uploaded',
        'upload_bank_statements_complete',
        'bank_reconciliation_uploaded',
        'upload_bank_reconciliation_complete',
        'bank_confirmations_uploaded',
        'upload_bank_confirmations_complete',
        'ar_confirmations_uploaded',
        'upload_ar_confirmations_complete',
        'ap_confirmations_uploaded',
        'upload_ap_confirmations_complete',
        'inventory_confirmations_uploaded',
        'upload_inventory_confirmations_complete',
        'inventory_observation_uploaded',
        'upload_inventory_observation_complete',
        'supporting_document_uploaded',
        // AI steps
        'assertion_testing_complete',
        'assertions_tested',
        'test_assertions_complete',
        'audit_procedures_formulated',
        'formulate_procedures_complete',
        'financial_audit_testing_complete',
        'findings_generated'
      ];

      if (financialAuditEvents.includes(evt.type)) {
        // Always debounce-fetch for these events — confirmation controllers send
        // sparse socket data that doesn't include balanceSheetAudit, so React
        // state can't update without a fresh fetch from MongoDB.
        console.log('[RPA] Financial audit event — scheduling debounced refresh:', evt.type);
        debouncedFetchDocs(2000);
      }

      if (evt.type === 'financial_data_uploaded') {
        setTimeout(() => fetchDocs(), 500);
      }
    };

    socket.on('connect', onConnect);
    socket.on('connect_error', onConnectError);
    socket.on('disconnect', onDisconnect);
    socket.on('rpa_workflow_update', onGlobalUpdate);

    return () => {
      socket.off('connect', onConnect);
      socket.off('connect_error', onConnectError);
      socket.off('disconnect', onDisconnect);
      socket.off('rpa_workflow_update', onGlobalUpdate);
    };
  }, [docs.length, debouncedFetchDocs, fetchDocs, getSocket]);

  /** Socket (per-doc rooms) */
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    Object.entries(perDocListenersRef.current).forEach(([eventName, handler]) => {
      socket.off(eventName, handler);
    });
    perDocListenersRef.current = {};

    const ids = docs.map((d) => d._id).filter(Boolean);
    if (socketConnected) {
      const prev = new Set(subscribedDocIdsRef.current);
      const curr = new Set(ids);
      const toLeave = [...prev].filter((id) => !curr.has(id));
      const toJoin = [...curr].filter((id) => !prev.has(id));

      if (toLeave.length) socket.emit('rpa_unsubscribe_docs', { docIds: toLeave });
      if (toJoin.length) socket.emit('rpa_subscribe_docs', { docIds: toJoin });

      subscribedDocIdsRef.current = ids;
    }

    const applyStepUpdate = (workflow, payload) => {
      if (!workflow?.steps?.length) return workflow;

      const eventType = payload?.type;
      const targetStatus = payload?.status || (eventType === 'compliance_testing_queued' ? 'in_progress' : 'completed');
      const aliases = new Set([...(STEP_ALIASES[eventType] || []), payload?.step].filter(Boolean));

      if (!aliases.size) return workflow;

      const steps = workflow.steps.map((s) => (aliases.has(s.id) ? { ...s, status: targetStatus } : s));
      const allDone = steps.every((s) => s.status === 'completed');

      return {
        ...workflow,
        steps,
        status: allDone ? 'completed' : (workflow.status || 'in_progress')
      };
    };

    ids.forEach((docId) => {
      const eventName = `rpa_doc_${docId}`;
      const handler = (payload) => {
        if (!payload?.type) return;

        setDocs((prev) =>
          prev.map((d) => {
            if (d._id !== docId) return d;

            const merged = { ...d, ...(payload.data || {}) };

            if (d.workflowAssignment) {
              merged.workflowAssignment = applyStepUpdate(d.workflowAssignment, payload);
            }

            return merged;
          })
        );
      };

      socket.on(eventName, handler);
      perDocListenersRef.current[eventName] = handler;
    });
  }, [docs, socketConnected]);

  /** Initial load */
  useEffect(() => {
    fetchDocs();
    fetchSummary();
    fetchProjects();
  }, [fetchDocs, fetchSummary, fetchProjects]);

  /** Step truth helpers - ✅ ALL FIXED */
  const getStep = (d, id) => d?.workflowAssignment?.steps?.find((s) => s.id === id) || null;
  const getStepStatus = (d, id) => getStep(d, id)?.status || null;
  const isStepCompleted = (d, id) => getStepStatus(d, id) === 'completed';
  const isStepInProgress = (d, id) => getStepStatus(d, id) === 'in_progress';
  const stepIndex = (d, id) => {
    const idx = d?.workflowAssignment?.steps?.findIndex((s) => s.id === id);
    return typeof idx === 'number' ? idx : -1;
  };
  const totalStepsCount = (d) => d?.workflowAssignment?.steps?.length || 0;

  const hasRegulatoryAnalysis = (d) => !!d.regulatoryFindings;
  const hasComplianceTesting = (d) => !!d.complianceTestingResults;
  const hasFinancialData = (d) => {
    if (isStepCompleted(d, 'upload_financial')) return true;
    const fd = d.financialData || {};
    return !!(
      fd &&
      (fd.uploadedAt ||
        fd.storedFilename ||
        fd.fileName ||
        fd.originalName ||
        fd.filePath ||
        fd.file_path ||
        fd.storagePath)
    );
  };

  const isRegulatoryAnalysisInProgress = (d) => !!(d.regulatoryAnalysisQueuedAt && !d.regulatoryFindings);
  const isComplianceTestingInProgress = (d) => !!(d.complianceTestingQueuedAt && !d.complianceTestingResults);
  const hasSocFindings = (d) => !!d.socFindings || isStepCompleted(d, 'analyze_soc');
  const isSocAnalysisInProgress = (d) =>
    !!(d.socAnalysisQueuedAt && !d.socFindings) || isStepInProgress(d, 'analyze_soc');
  const hasControlObjectives = (d) => !!d.controlObjectives || isStepCompleted(d, 'extract_objectives');
  const isObjectivesInProgress = (d) =>
    !!(d.controlObjectivesQueuedAt && !d.controlObjectives) || isStepInProgress(d, 'extract_objectives');
  const hasTestingAttributes = (d) => !!d.testingAttributes || isStepCompleted(d, 'formulate_attributes');
  const isTestingAttributesInProgress = (d) =>
    !!(d.testingAttributesQueuedAt && !d.testingAttributes) || isStepInProgress(d, 'formulate_attributes');
  const hasPopulationDataField = (d) =>
    (d.populationData &&
      (d.populationData.uploadedAt ||
        d.populationData.rowCount ||
        d.populationData.fileName ||
        d.populationData.originalName)) ||
    isStepCompleted(d, 'upload_population');
  const hasTestingResultsField = (d) => !!d.testingResults || isStepCompleted(d, 'conduct_testing');
  const isTestingInProgress = (d) =>
    !!(d.testingQueuedAt && !d.testingResults) || isStepInProgress(d, 'conduct_testing');

  // Financial Statement Audit helpers
  const hasFinancialStatementAnalysis = (d) =>
    !!(d.balanceSheetAudit?.financialStatementAnalysis || d.financialStatementAnalysis) ||
    isStepCompleted(d, 'analyze_financial_statement');
  const hasExtractedBalances = (d) => {
    const hasNested = !!(d.balanceSheetAudit?.extractedBalances);
    const hasFlat = !!d.extractedBalances;
    const hasStep = isStepCompleted(d, 'extract_balances');
    
    console.log('[RPA] hasExtractedBalances:', { 
      hasNested, 
      hasFlat, 
      hasStep, 
      docId: d._id,
      nestedKeys: Object.keys(d.balanceSheetAudit || {}),
      flatKeys: Object.keys(d).filter(k => k.includes('balance'))
    });
    
    return hasNested || hasFlat || hasStep;
  };
  const hasMateriality = (d) => {
    const hasNested = !!(d.balanceSheetAudit?.materialityAssessment);
    const hasFlat = !!d.materialityAssessment;
    const hasStep = isStepCompleted(d, 'assess_materiality');
    
    console.log('[RPA] hasMateriality:', { hasNested, hasFlat, hasStep, docId: d._id });
    return hasNested || hasFlat || hasStep;
  };
  const hasTrialBalance = (d) => {
    const hasNested = !!(
      d.balanceSheetAudit?.trialBalance?.uploadedAt ||
      d.balanceSheetAudit?.trialBalance?.originalName
    );
    const hasFlat = !!(d.trialBalance?.uploadedAt || d.trialBalance?.originalName);
    const hasStep = isStepCompleted(d, 'upload_trial_balance');
    
    console.log('[RPA] hasTrialBalance:', { hasNested, hasFlat, hasStep, docId: d._id });
    return hasNested || hasFlat || hasStep;
  };

  // ✅ NEW: Income statement helper function
  const hasIncomeStatement = (d) => {
    const hasNested = !!(
      d.balanceSheetAudit?.incomeStatement?.uploadedAt ||
      d.balanceSheetAudit?.incomeStatement?.data ||
      d.lastIncomeStatementUploadedAt
    );
    const hasFlat = !!(d.incomeStatement?.uploadedAt || d.lastIncomeStatementUploadedAt);
    const hasStep = isStepCompleted(d, 'upload_income_statement');
    
    console.log('[RPA] hasIncomeStatement:', { hasNested, hasFlat, hasStep, docId: d._id });
    return hasNested || hasFlat || hasStep;
  };

  // ✅ NEW: Bank statements helper function
  const hasBankStatements = (d) => {
    const hasNested = !!(
      d.balanceSheetAudit?.bankStatements &&
      Array.isArray(d.balanceSheetAudit.bankStatements) &&
      d.balanceSheetAudit.bankStatements.length > 0
    );
    const hasFlat = !!(d.bankStatements && Array.isArray(d.bankStatements) && d.bankStatements.length > 0);
    const hasStep = isStepCompleted(d, 'upload_bank_statements');
    const hasTimestamp = !!d.lastBankStatementsUploadedAt;
    
    console.log('[RPA] hasBankStatements:', { 
      hasNested, 
      hasFlat, 
      hasStep, 
      hasTimestamp,
      docId: d._id,
      nestedCount: d.balanceSheetAudit?.bankStatements?.length || 0
    });
    
    return hasNested || hasFlat || hasStep || hasTimestamp;
  };

  // ✅ NEW: Bank reconciliation helper function
  const hasBankReconciliation = (d) => {
    const hasNested = !!(
      d.balanceSheetAudit?.bankReconciliation?.uploadedAt ||
      d.balanceSheetAudit?.bankReconciliation?.reconciliationItems ||
      d.balanceSheetAudit?.bankReconciliation
    );
    const hasFlat = !!(d.bankReconciliation?.uploadedAt || d.bankReconciliation);
    const hasStep = isStepCompleted(d, 'upload_bank_reconciliation');
    const hasTimestamp = !!d.lastBankReconciliationUploadedAt;

    console.log('[RPA] hasBankReconciliation:', {
      hasNested,
      hasFlat,
      hasStep,
      hasTimestamp,
      docId: d._id
    });

    return hasNested || hasFlat || hasStep || hasTimestamp;
  };

  // ⭐ NEW: Bank confirmations helper function
  const hasBankConfirmations = (d) => {
    const hasNested = !!(
      d.balanceSheetAudit?.bankConfirmations?.confirmations?.length ||
      d.balanceSheetAudit?.bankConfirmations
    );
    const hasFlat = !!(d.bankConfirmations?.confirmations?.length || d.bankConfirmations);
    const hasStep = isStepCompleted(d, 'upload_bank_confirmations');
    const hasTimestamp = !!d.lastBankConfirmationUploadedAt;

    console.log('[RPA] hasBankConfirmations:', {
      hasNested,
      hasFlat,
      hasStep,
      hasTimestamp,
      docId: d._id
    });

    return hasNested || hasFlat || hasStep || hasTimestamp;
  };

  // ⭐ NEW: AR confirmations helper function
  const hasARConfirmations = (d) => {
    const hasNested = !!(
      d.balanceSheetAudit?.arConfirmations?.confirmations?.length ||
      d.balanceSheetAudit?.arConfirmations
    );
    const hasFlat = !!(d.arConfirmations?.confirmations?.length || d.arConfirmations);
    const hasStep = isStepCompleted(d, 'upload_ar_confirmations');
    const hasTimestamp = !!d.lastARConfirmationUploadedAt;

    console.log('[RPA] hasARConfirmations:', {
      hasNested,
      hasFlat,
      hasStep,
      hasTimestamp,
      docId: d._id
    });

    return hasNested || hasFlat || hasStep || hasTimestamp;
  };
  // ✅ AP confirmations helper function
  const hasAPConfirmations = (d) => {
    const hasNested = !!(
      d.balanceSheetAudit?.apConfirmations?.confirmations?.length ||
      d.balanceSheetAudit?.apConfirmations
    );
    const hasFlat = !!(d.apConfirmations?.confirmations?.length || d.apConfirmations);
    const hasStep = isStepCompleted(d, 'upload_ap_confirmations');
    const hasTimestamp = !!d.lastAPConfirmationUploadedAt;

    return hasNested || hasFlat || hasStep || hasTimestamp;
  };


  // ⭐ NEW: Inventory confirmations helper function
  const hasInventoryConfirmations = (d) => {
    const hasNested = !!(
      d.balanceSheetAudit?.inventoryConfirmations?.confirmations?.length ||
      d.balanceSheetAudit?.inventoryConfirmations
    );
    const hasFlat = !!(d.inventoryConfirmations?.confirmations?.length || d.inventoryConfirmations);
    const hasStep = isStepCompleted(d, 'upload_inventory_confirmations');
    const hasTimestamp = !!d.lastInventoryConfirmationUploadedAt;

    console.log('[RPA] hasInventoryConfirmations:', {
      hasNested,
      hasFlat,
      hasStep,
      hasTimestamp,
      docId: d._id
    });

    return hasNested || hasFlat || hasStep || hasTimestamp;
  };

  // ⭐ NEW: Inventory observation helper function
  const hasInventoryObservation = (d) => {
    const hasNested = !!(
      d.balanceSheetAudit?.inventoryObservation?.testCounts?.length ||
      d.balanceSheetAudit?.inventoryObservation
    );
    const hasFlat = !!(d.inventoryObservation?.testCounts?.length || d.inventoryObservation);
    const hasStep = isStepCompleted(d, 'upload_inventory_observation');
    const hasTimestamp = !!d.lastInventoryObservationUploadedAt;

    console.log('[RPA] hasInventoryObservation:', {
      hasNested,
      hasFlat,
      hasStep,
      hasTimestamp,
      docId: d._id
    });

    return hasNested || hasFlat || hasStep || hasTimestamp;
  };

  const hasAssertions = (d) => {
    const hasNested = !!(d.balanceSheetAudit?.assertionTests);
    const hasFlat = !!d.assertionTests;
    const hasStep = isStepCompleted(d, 'test_assertions');
    
    console.log('[RPA] hasAssertions:', { hasNested, hasFlat, hasStep, docId: d._id });
    return hasNested || hasFlat || hasStep;
  };
  const hasSubstantiveTesting = (d) => {
    const hasNested = !!(
      d.balanceSheetAudit?.substantiveTesting || 
      d.balanceSheetAudit?.substantiveTestResults
    );
    const hasFlat = !!(d.substantiveTesting || d.substantiveTestResults);
    const hasStep = isStepCompleted(d, 'conduct_substantive_testing');
    
    console.log('[RPA] hasSubstantiveTesting:', { 
      hasNested, 
      hasFlat, 
      hasStep, 
      docId: d._id,
      bsaKeys: Object.keys(d.balanceSheetAudit || {})
    });
    
    return hasNested || hasFlat || hasStep;
  };
  const hasAuditProcedures = (d) =>
    !!(d.balanceSheetAudit?.auditProcedures || d.auditProcedures) ||
    isStepCompleted(d, 'formulate_audit_procedures');
  const hasFinancialAuditResults = (d) =>
    !!(d.balanceSheetAudit?.financialAuditResults || d.financialAuditResults) ||
    isStepCompleted(d, 'conduct_audit_testing');
  const hasFinalFindings = (d) =>
    !!(d.balanceSheetAudit?.finalFindings || d.finalFindings) || isStepCompleted(d, 'generate_findings');

  // ⭐ Human-in-the-Loop: is the blind currently active for a doc?
  // Fail closed — if the mode hasn't loaded yet for an FS-audit doc with
  // findings and no submitted human findings, treat blind as active so AI
  // findings content never flashes before the mode is known.
  const isBlindActiveForDoc = (d) => {
    if (d.workflowAssignment?.templateId !== 'financial_statement_audit_v1') return false;
    if (!(d.balanceSheetAudit?.finalFindings || d.finalFindings)) return false;
    if (d.balanceSheetAudit?.humanFindings?.submittedAt) return false;
    const info = comparisonInfoMap[d._id];
    return info ? !!info.blindActive : true;
  };

  // Fetch effective comparison state for FS-audit docs that need it
  useEffect(() => {
    const need = docs.filter(d =>
      d.workflowAssignment?.templateId === 'financial_statement_audit_v1' &&
      (d.balanceSheetAudit?.finalFindings || d.finalFindings) &&
      !d.balanceSheetAudit?.humanFindings?.submittedAt &&
      !comparisonInfoMap[d._id]
    );
    if (!need.length) return;
    let cancelled = false;
    (async () => {
      const updates = {};
      for (const d of need) {
        try {
          const res = await axios.get(`${API_URL}/admin/rpa/docs/${d._id}/comparison-mode`, { withCredentials: true });
          if (res.data?.success) updates[d._id] = res.data;
        } catch {
          updates[d._id] = { mode: 'blind', blindActive: true }; // fail closed
        }
      }
      if (!cancelled && Object.keys(updates).length) {
        setComparisonInfoMap(prev => ({ ...prev, ...updates }));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs]);

  /** Workflow status - ✅ FIXED: Complete Financial Audit flow with income statement */
  const getWorkflowStatus = (d) => {
    if (!d.classification || d.classification === 'Unknown') {
      return {
        currentStep: 'Classification needed',
        nextAction: 'classify',
        stepNumber: 0,
        totalSteps: 5,
        canProceed: true
      };
    }

    if (!(d.workflowAssignment?.templateId && d.workflowAssignment?.steps?.length)) {
      return {
        currentStep: 'Workflow assignment needed',
        nextAction: 'assign_workflow',
        stepNumber: 1,
        totalSteps: 5,
        canProceed: true
      };
    }

    const wf = d.workflowAssignment;

    // Financial Statement Audit workflow - ✅ UPDATED: 18-step workflow with all confirmations
    if (wf.templateId === 'financial_statement_audit_v1') {
      const total = totalStepsCount(d) || 19; // ✅ Updated from 18 to 19 (added AP confirmations step)

      console.log('[RPA] Checking Financial Statement Audit steps:', {
        docId: d._id,
        hasFS: hasFinancialStatementAnalysis(d),
        hasBalances: hasExtractedBalances(d),
        hasMat: hasMateriality(d),
        hasTB: hasTrialBalance(d),
        hasIS: hasIncomeStatement(d),
        hasBS: hasBankStatements(d),
        hasBankRec: hasBankReconciliation(d),
        hasBankConf: hasBankConfirmations(d), // ✅ NEW
        hasARConf: hasARConfirmations(d), // ✅ NEW
        hasAPConf: hasAPConfirmations(d), // ✅ NEW
        hasInvConf: hasInventoryConfirmations(d), // ✅ NEW
        hasInvObs: hasInventoryObservation(d), // ✅ NEW
        hasAssert: hasAssertions(d)
      });

      if (!hasFinancialStatementAnalysis(d)) {
        return {
          currentStep: 'Analyze Financial Statement',
          nextAction: 'analyze_financial_statement',
          stepNumber: 1,
          totalSteps: total,
          canProceed: true
        };
      }

      if (!hasExtractedBalances(d)) {
        return {
          currentStep: 'Extract Balances',
          nextAction: 'extract_balances',
          stepNumber: 2,
          totalSteps: total,
          canProceed: true
        };
      }

      if (!hasMateriality(d)) {
        return {
          currentStep: 'Assess Materiality',
          nextAction: 'assess_materiality',
          stepNumber: 3,
          totalSteps: total,
          canProceed: true
        };
      }

      if (!hasTrialBalance(d)) {
        return {
          currentStep: 'Upload Trial Balance',
          nextAction: 'upload_trial_balance',
          stepNumber: 4,
          totalSteps: total,
          canProceed: true
        };
      }

      // ✅ EXISTING: Check for income statement
      if (!hasIncomeStatement(d)) {
        return {
          currentStep: 'Upload Income Statement',
          nextAction: 'upload_income_statement',
          stepNumber: 5,
          totalSteps: total,
          canProceed: true
        };
      }

      // ✅ NEW: Check for bank statements
      if (!hasBankStatements(d)) {
        return {
          currentStep: 'Upload Bank Statements',
          nextAction: 'upload_bank_statements',
          stepNumber: 6,
          totalSteps: total,
          canProceed: true
        };
      }

      // ✅ NEW: Check for bank reconciliation
      if (!hasBankReconciliation(d)) {
        return {
          currentStep: 'Upload Bank Reconciliation',
          nextAction: 'upload_bank_reconciliation',
          stepNumber: 7,
          totalSteps: total,
          canProceed: true
        };
      }

      // ⭐ NEW: Check for bank confirmations (Step 10)
      if (!hasBankConfirmations(d)) {
        return {
          currentStep: 'Upload Bank Confirmations',
          nextAction: 'upload_bank_confirmations',
          stepNumber: 8,
          totalSteps: total,
          canProceed: true
        };
      }

      // ⭐ NEW: Check for AR confirmations (Step 11)
      if (!hasARConfirmations(d)) {
        return {
          currentStep: 'Upload AR Confirmations',
          nextAction: 'upload_ar_confirmations',
          stepNumber: 9,
          totalSteps: total,
          canProceed: true
        };
      }

      // ✅ NEW: Check for AP confirmations
      if (!hasAPConfirmations(d)) {
        return {
          currentStep: 'Upload AP Confirmations',
          nextAction: 'upload_ap_confirmations',
          stepNumber: 10,
          totalSteps: total,
          canProceed: true
        };
      }

      // ⭐ NEW: Check for inventory confirmations (Step 12)
      if (!hasInventoryConfirmations(d)) {
        return {
          currentStep: 'Upload Inventory Confirmations',
          nextAction: 'upload_inventory_confirmations',
          stepNumber: 10,
          totalSteps: total,
          canProceed: true
        };
      }

      // ⭐ NEW: Check for physical inventory observation (Step 13)
      if (!hasInventoryObservation(d)) {
        return {
          currentStep: 'Upload Physical Inventory Observation',
          nextAction: 'upload_inventory_observation',
          stepNumber: 11,
          totalSteps: total,
          canProceed: true
        };
      }

      if (!hasAssertions(d)) {
        return {
          currentStep: 'Test Assertions',
          nextAction: 'test_assertions',
          stepNumber: 12, // ✅ Updated from 8
          totalSteps: total,
          canProceed: true
        };
      }

      if (!hasSubstantiveTesting(d)) {
        return {
          currentStep: 'Conduct Substantive Testing',
          nextAction: 'conduct_substantive_testing',
          stepNumber: 13, // ✅ Updated from 9
          totalSteps: total,
          canProceed: true
        };
      }

      if (!hasAuditProcedures(d)) {
        return {
          currentStep: 'Formulate Audit Procedures',
          nextAction: 'formulate_audit_procedures',
          stepNumber: 14, // ✅ Updated from 10 (now step 14 with confirmations)
          totalSteps: total,
          canProceed: true
        };
      }

      if (!hasFinancialAuditResults(d)) {
        return {
          currentStep: 'Conduct Audit Testing',
          nextAction: 'conduct_audit_testing',
          stepNumber: 15, // ✅ Updated from 11 (now step 15 with confirmations)
          totalSteps: total,
          canProceed: true
        };
      }

      if (!hasFinalFindings(d)) {
        return {
          currentStep: 'Generate Findings',
          nextAction: 'generate_findings',
          stepNumber: 16, // ✅ Updated from 12 (now step 16 with confirmations)
          totalSteps: total,
          canProceed: true
        };
      }

      return {
        currentStep: 'Financial Statement Audit Complete',
        nextAction: 'complete',
        stepNumber: total,
        totalSteps: total,
        canProceed: false
      };
    }

    // SOC/CUEC workflow (preserved - no changes)
    if (wf.templateId === 'soc_cuec_v1') {
      const total = totalStepsCount(d) || 7;
      const soc = 'analyze_soc';
      const obj = 'extract_objectives';
      const attr = 'formulate_attributes';
      const pop = 'upload_population';
      const test = 'conduct_testing';

      if (!hasSocFindings(d)) {
        if (isSocAnalysisInProgress(d)) {
          return {
            currentStep: 'Analyzing SOC report…',
            nextAction: 'wait',
            stepNumber: Math.max(stepIndex(d, soc), 2) + 1,
            totalSteps: total,
            canProceed: false
          };
        }
        return {
          currentStep: 'SOC Analysis needed',
          nextAction: 'analyze_soc',
          stepNumber: Math.max(stepIndex(d, soc), 2) + 1,
          totalSteps: total,
          canProceed: true
        };
      }

      if (!hasControlObjectives(d)) {
        if (isObjectivesInProgress(d)) {
          return {
            currentStep: 'Extracting control objectives…',
            nextAction: 'wait',
            stepNumber: Math.max(stepIndex(d, obj), 3) + 1,
            totalSteps: total,
            canProceed: false
          };
        }
        return {
          currentStep: 'Extract Control Objectives',
          nextAction: 'extract_objectives',
          stepNumber: Math.max(stepIndex(d, obj), 3) + 1,
          totalSteps: total,
          canProceed: true
        };
      }

      if (!hasTestingAttributes(d)) {
        if (isTestingAttributesInProgress(d)) {
          return {
            currentStep: 'Formulating testing attributes…',
            nextAction: 'wait',
            stepNumber: Math.max(stepIndex(d, attr), 4) + 1,
            totalSteps: total,
            canProceed: false
          };
        }
        return {
          currentStep: 'Formulate Testing Attributes',
          nextAction: 'formulate_attributes',
          stepNumber: Math.max(stepIndex(d, attr), 4) + 1,
          totalSteps: total,
          canProceed: true
        };
      }

      if (!hasPopulationDataField(d)) {
        return {
          currentStep: 'Upload Population Data',
          nextAction: 'upload_population',
          stepNumber: Math.max(stepIndex(d, pop), 5) + 1,
          totalSteps: total,
          canProceed: true
        };
      }

      if (!hasTestingResultsField(d)) {
        if (isTestingInProgress(d)) {
          return {
            currentStep: 'Conducting testing…',
            nextAction: 'wait',
            stepNumber: Math.max(stepIndex(d, test), 6) + 1,
            totalSteps: total,
            canProceed: false
          };
        }
        return {
          currentStep: 'Conduct Testing',
          nextAction: 'conduct_testing',
          stepNumber: Math.max(stepIndex(d, test), 6) + 1,
          totalSteps: total,
          canProceed: true
        };
      }

      return {
        currentStep: 'SOC-based Testing Complete',
        nextAction: 'complete',
        stepNumber: total,
        totalSteps: total,
        canProceed: false
      };
    }

    // FWS WSFR workflow (preserved - no changes)
    if (wf.templateId === 'fws_wsfr_v1') {
      const total = totalStepsCount(d) || 5;
      const reg = 'analyze_regulatory';
      const fin = 'upload_financial';
      const comp = 'compliance_testing';

      if (wf.steps?.length) {
        if (!isStepCompleted(d, reg)) {
          if (isStepInProgress(d, reg) || isRegulatoryAnalysisInProgress(d)) {
            return {
              currentStep: 'Analyzing regulatory compliance rules…',
              nextAction: 'wait',
              stepNumber: Math.max(stepIndex(d, reg), 1) + 1,
              totalSteps: total,
              canProceed: false
            };
          }
          return {
            currentStep: 'Regulatory Analysis needed',
            nextAction: 'analyze_regulatory',
            stepNumber: Math.max(stepIndex(d, reg), 1) + 1,
            totalSteps: total,
            canProceed: true
          };
        }

        if (!isStepCompleted(d, fin) && !hasFinancialData(d)) {
          if (isStepInProgress(d, fin)) {
            return {
              currentStep: 'Uploading financial data…',
              nextAction: 'wait',
              stepNumber: Math.max(stepIndex(d, fin), 2) + 1,
              totalSteps: total,
              canProceed: false
            };
          }
          return {
            currentStep: 'Upload Financial Data needed',
            nextAction: 'upload_financial',
            stepNumber: Math.max(stepIndex(d, fin), 2) + 1,
            totalSteps: total,
            canProceed: true
          };
        }

        if (!isStepCompleted(d, comp) && !hasComplianceTesting(d)) {
          if (isStepInProgress(d, comp) || isComplianceTestingInProgress(d)) {
            return {
              currentStep: 'Running FWS compliance tests…',
              nextAction: 'wait',
              stepNumber: Math.max(stepIndex(d, comp), 3) + 1,
              totalSteps: total,
              canProceed: false
            };
          }
          return {
            currentStep: 'FWS Compliance Testing needed',
            nextAction: 'compliance_testing',
            stepNumber: Math.max(stepIndex(d, comp), 3) + 1,
            totalSteps: total,
            canProceed: true
          };
        }

        return {
          currentStep: 'FWS WSFR Compliance Audit Complete',
          nextAction: 'complete',
          stepNumber: total,
          totalSteps: total,
          canProceed: false
        };
      }

      if (!hasRegulatoryAnalysis(d)) {
        if (isRegulatoryAnalysisInProgress(d))
          return {
            currentStep: 'Analyzing regulatory compliance rules…',
            nextAction: 'wait',
            stepNumber: 2,
            totalSteps: 5,
            canProceed: false
          };
        return {
          currentStep: 'Regulatory Analysis needed',
          nextAction: 'analyze_regulatory',
          stepNumber: 2,
          totalSteps: 5,
          canProceed: true
        };
      }

      if (!hasFinancialData(d)) {
        return {
          currentStep: 'Upload Financial Data needed',
          nextAction: 'upload_financial',
          stepNumber: 3,
          totalSteps: 5,
          canProceed: true
        };
      }

      if (!hasComplianceTesting(d)) {
        if (isComplianceTestingInProgress(d))
          return {
            currentStep: 'Running FWS compliance tests…',
            nextAction: 'wait',
            stepNumber: 4,
            totalSteps: 5,
            canProceed: false
          };
        return {
          currentStep: 'FWS Compliance Testing needed',
          nextAction: 'compliance_testing',
          stepNumber: 4,
          totalSteps: 5,
          canProceed: true
        };
      }

      return {
        currentStep: 'FWS WSFR Compliance Audit Complete',
        nextAction: 'complete',
        stepNumber: 5,
        totalSteps: 5,
        canProceed: false
      };
    }

    return {
      currentStep: 'Generic workflow assigned',
      nextAction: 'generic',
      stepNumber: 2,
      totalSteps: 4,
      canProceed: true
    };
  };

  /** Table rendering helpers */
  const kindLabel = (d) => d.kind || d.mimeType || d.mime || 'file';
  const clientLabel = (d) => d.clientName || d.client || '-';
  const projectLabel = (d) => d.projectName || d.project || '-';
  
  const classificationPill = (d) =>
    d.classification ? (
      <span style={pill}>{String(d.classification)}</span>
    ) : (
      <span style={{ ...pill, background: '#fef3c7', color: '#92400e' }}>Unclassified</span>
    );

  const nextPendingStepIdFromWorkflow = (d) =>
    d?.workflowAssignment?.steps?.find((s) => s.status === 'pending')?.id || null;

  const workflowPill = (d) => {
    const status = getWorkflowStatus(d);
    const colors = {
      financial_statement_audit_v1: { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
      soc_cuec_v1: { bg: '#f0fdf4', border: '#22c55e', text: '#15803d' },
      fws_wsfr_v1: { bg: '#f0f9ff', border: '#0ea5e9', text: '#0369a1' },
      generic_audit_v1: { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' }
    };

    const templateId = d.workflowAssignment?.templateId || 'generic_audit_v1';
    const color = colors[templateId] || colors.generic_audit_v1;
    const total = totalStepsCount(d) || status.totalSteps || 5;
    const nextId = nextPendingStepIdFromWorkflow(d);
    const currentIdx = nextId
      ? stepIndex(d, nextId)
      : totalStepsCount(d)
      ? totalStepsCount(d) - 1
      : status.stepNumber - 1;
    const stepNumber = Math.max(1, (currentIdx ?? 0) + 1);
    const progress = Math.round((stepNumber / Math.max(1, total)) * 100);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 140 }}>
        <span
          style={{
            ...pill,
            background: color.bg,
            borderColor: color.border,
            color: color.text,
            fontSize: 10
          }}
        >
          {d.workflowAssignment?.templateName?.replace(/\s+(v\d+|Audit|Workflow)$/i, '') || 'Workflow'}
        </span>
        <div style={{ fontSize: 9, color: '#6b7280', lineHeight: '1.2' }}>
          Step {stepNumber}/{total} ({progress}%)
        </div>
        <div style={{ fontSize: 9, color: '#374151', fontWeight: 500, lineHeight: '1.2' }}>
          {status.currentStep}
        </div>
      </div>
    );
  };

  const docLinkHref = (d) => {
    const stored = d.storagePath || d.fileName || d.storedFilename || d.filename || '';
    if (!stored) return '#';
    return `${API_URL}/uploads/admin/rpa/${encodeURIComponent(stored)}`;
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API_URL}/admin/rpa/docs/${id}`, { withCredentials: true });
      setDocs((prev) => prev.filter((d) => d._id !== id));
      fetchSummary();
    } catch (err) {
      console.error('[RPA] delete failed:', err);
      alert('Delete failed.');
    }
  };

  const onFilter = async (e) => {
    e.preventDefault();
    await fetchDocs();
  };

  const resetFilters = async () => {
    setFilters({ client: '', project: '' });
    await fetchDocs();
  };

  const forceRefresh = () => {
    setRealtimeUpdates([]);
    fetchDocs();
    fetchSummary();
    fetchProjects();
  };

  const handleProjectCreated = (_newProject) => {
    // Fetch fresh list so new card includes myRole/myAuditRole from the server
    setShowProjectForm(false);
    fetchProjects();
    fetchSummary();
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Delete this project? All associated documents will remain but be unlinked.')) return;
    try {
      await axios.delete(`${API_URL}/admin/rpa/projects/${projectId}`, { withCredentials: true });
      setProjects((prev) => prev.filter((p) => p._id !== projectId));
      fetchSummary();
    } catch (err) {
      console.error('[RPA] delete project failed:', err);
      alert('Failed to delete project.');
    }
  };

  /** Actions */
  // ── Agent row selection ──────────────────────────────────────────────────
  // Clicking a table row selects the doc for the Audit Agent chat.
  // The PDF open link moves to a small icon button inside the filename cell.
  const handleRowSelect = (d) => {
    const isAlreadySelected = selectedAgentDocId === d._id;
    const nextId = isAlreadySelected ? null : d._id;
    setSelectedAgentDocId(nextId);
    dispatch({
      type: nextId ? 'SET_RPA_AGENT_CONTEXT' : 'CLEAR_RPA_AGENT_CONTEXT',
      payload: nextId ? {
        docId:     d._id,
        projectId: d.projectId || d.project || null,
        docName:   d.originalName || d.fileName || 'Unknown',
        workflow:  d.workflowAssignment?.templateName || null,
      } : undefined,
    });
  };

  const renderViewResultsButton = (d) => (
    <button
      onClick={() => setViewingResults(d._id === viewingResults ? null : d._id)}
      style={{
        background: '#6366f1',
        color: 'white',
        border: 'none',
        padding: '4px 8px',
        borderRadius: 4,
        fontSize: 11,
        cursor: 'pointer'
      }}
    >
      {viewingResults === d._id ? 'Hide Results' : 'View Results'}
    </button>
  );

  const renderActionButtons = (d) => {
    const status = getWorkflowStatus(d);
    const onDone = () => {
      console.log('[RPA] Action completed, scheduling debounced refresh');
      debouncedFetchDocs(1500);
    };
    const financialReady = hasFinancialData(d);

    return (
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={() => handleDelete(d._id)} style={dangerBtn}>
          Del
        </button>
        {renderViewResultsButton(d)}
        <button
          onClick={() => {
            setAuditDocId(d._id);
            setAuditOpen(true);
          }}
          style={{
            background: '#111827',
            color: 'white',
            border: 'none',
            padding: '4px 8px',
            borderRadius: 4,
            fontSize: 11,
            cursor: 'pointer'
          }}
        >
          Audit
        </button>

        {status.nextAction === 'classify' && <ClassifyComplianceButton docId={d._id} onDone={onDone} />}
        {status.nextAction === 'assign_workflow' && <WorkflowAssignmentButton docId={d._id} onDone={onDone} />}

        {/* SOC/CUEC workflow actions */}
        {status.nextAction === 'analyze_soc' && (
          <AnalyzeSOCButton
            docId={d._id}
            disabledHint={!isPDF(d) ? 'SOC analysis works best with PDF files' : null}
            onDone={onDone}
          />
        )}
        {status.nextAction === 'extract_objectives' && (
          <ExtractControlObjectivesButton docId={d._id} onDone={onDone} />
        )}
        {status.nextAction === 'formulate_attributes' && (
          <FormulateTestingAttributesButton docId={d._id} onDone={onDone} />
        )}
        {status.nextAction === 'upload_population' && <UploadPopulationButton docId={d._id} onDone={onDone} />}
        {status.nextAction === 'conduct_testing' && <ConductTestingButton docId={d._id} onDone={onDone} />}

        {/* WSFR workflow actions */}
        {status.nextAction === 'analyze_regulatory' && (
          <AnalyzeRegulatoryButton
            docId={d._id}
            disabledHint={!isPDF(d) ? 'Regulatory analysis requires PDF documents' : null}
            onDone={onDone}
          />
        )}
        {status.nextAction === 'upload_financial' && (
          <UploadFinancialDataButton docId={d._id} onDone={onDone} />
        )}
        {status.nextAction === 'compliance_testing' && (
          <ConductComplianceTestingButton
            docId={d._id}
            disabledHint={financialReady ? null : 'Upload financial data first'}
            onDone={onDone}
          />
        )}

        {/* Financial Statement Audit workflow actions - ✅ COMPLETE with income statement */}
        {status.nextAction === 'analyze_financial_statement' && (
          <AnalyzeFinancialStatementButton
            docId={d._id}
            disabledHint={!isPDF(d) ? 'Financial statement analysis requires PDF documents' : null}
            onDone={onDone}
          />
        )}
        {status.nextAction === 'extract_balances' && (
          <ExtractBalancesButton
            docId={d._id}
            disabledHint={!hasFinancialStatementAnalysis(d) ? 'Run Financial Statement Analysis first' : null}
            onDone={onDone}
          />
        )}
        {status.nextAction === 'assess_materiality' && (
          <AssessMaterialityButton
            docId={d._id}
            disabledHint={!hasExtractedBalances(d) ? 'Extract balances first' : null}
            onDone={onDone}
          />
        )}
        {status.nextAction === 'upload_trial_balance' && (
          <UploadTrialBalanceButton
            docId={d._id}
            disabledHint={!hasMateriality(d) ? 'Assess materiality first' : null}
            onDone={onDone}
          />
        )}
        {/* ✅ NEW: Income statement button */}
        {status.nextAction === 'upload_income_statement' && (
          <UploadIncomeStatementButton
            docId={d._id}
            disabledHint={!hasTrialBalance(d) ? 'Upload Trial Balance first' : null}
            onDone={onDone}
          />
        )}
        {/* ✅ NEW: Bank statements button */}
        {status.nextAction === 'upload_bank_statements' && (
          <UploadBankStatementsButton
            docId={d._id}
            disabledHint={!hasIncomeStatement(d) ? 'Upload Income Statement first' : null}
            onDone={onDone}
          />
        )}
        {/* ✅ NEW: Bank reconciliation button */}
        {status.nextAction === 'upload_bank_reconciliation' && (
          <UploadBankReconciliationButton
            docId={d._id}
            disabledHint={!hasBankStatements(d) ? 'Upload Bank Statements first' : null}
            onDone={onDone}
          />
        )}
        {/* ⭐ NEW: Bank Confirmations (Step 15) */}
        {status.nextAction === 'upload_bank_confirmations' && (
          <UploadBankConfirmationButton
            docId={d._id}
            disabledHint={!hasBankReconciliation(d) ? 'Upload Bank Reconciliation first' : null}
            onDone={onDone}
          />
        )}
        {/* ⭐ NEW: AR Confirmations (Step 16) */}
        {status.nextAction === 'upload_ar_confirmations' && (
          <UploadARConfirmationButton
            docId={d._id}
            disabledHint={!hasBankConfirmations(d) ? 'Upload Bank Confirmations first' : null}
            onDone={onDone}
          />
        )}
        {/* ✅ NEW: AP Confirmations */}
        {status.nextAction === 'upload_ap_confirmations' && (
          <UploadAPConfirmationButton
            docId={d._id}
            disabledHint={!hasARConfirmations(d) ? 'Upload AR Confirmations first' : null}
            onDone={onDone}
          />
        )}
        {/* ⭐ NEW: Inventory Confirmations (Step 17) */}
        {status.nextAction === 'upload_inventory_confirmations' && (
          <UploadInventoryConfirmationButton
            docId={d._id}
            disabledHint={!hasARConfirmations(d) ? 'Upload AR Confirmations first' : null}
            onDone={onDone}
          />
        )}
        {/* ⭐ NEW: Physical Inventory Observation (Step 18) */}
        {status.nextAction === 'upload_inventory_observation' && (
          <UploadInventoryObservationButton
            docId={d._id}
            disabledHint={!hasInventoryConfirmations(d) ? 'Upload Inventory Confirmations first' : null}
            onDone={onDone}
          />
        )}
        {status.nextAction === 'test_assertions' && (
          <TestAssertionsButton
            docId={d._id}
            disabledHint={!hasBankReconciliation(d) ? 'Upload Bank Reconciliation first' : null} // ✅ Updated to require bank reconciliation
            onDone={onDone}
          />
        )}
        {status.nextAction === 'conduct_substantive_testing' && (
          <ConductSubstantiveTestingButton
            docId={d._id}
            disabledHint={!hasAssertions(d) ? 'Select assertions to test first' : null}
            onDone={onDone}
          />
        )}
        {status.nextAction === 'formulate_audit_procedures' && (
          <FormulateAuditProceduresButton docId={d._id} onDone={onDone} />
        )}
        {status.nextAction === 'conduct_audit_testing' && (
          <ConductFinancialAuditButton docId={d._id} onDone={onDone} />
        )}
        {status.nextAction === 'generate_findings' && (
          <GenerateFindingsButton
            docId={d._id}
            disabledHint={!hasFinancialAuditResults(d) ? 'Complete audit testing first' : null}
            onDone={onDone}
          />
        )}

        {status.nextAction === 'generic' && <SamplingButton docId={d._id} onDone={onDone} />}
        {status.nextAction === 'wait' && <span style={waitingBadge}>Processing…</span>}
        {status.nextAction === 'complete' && <span style={completeBadge}>✅ Complete</span>}

                {/* ⭐ NEW: Workpaper button — shows whenever findings are complete.
            ⭐ HITL: hidden while blind is active — the workpaper package
            contains findings/opinion sections that would break the blind. */}
        {hasFinalFindings(d) && !isBlindActiveForDoc(d) && (
          <>
            <GenerateWorkpaperButton
              docId={d._id}
              onDone={onDone}
            />

            {/* ⭐ Auditor's report — same gate as the workpaper package, since
                the report carries the opinion and findings sections. The
                register inside it is read live on every open. */}
            <GenerateAuditorsReportButton
              docId={d._id}
              onDone={onDone}
            />
          </>
        )}
        {hasFinalFindings(d) && isBlindActiveForDoc(d) && (
          <span
            title="AI findings and workpapers are hidden until independent auditor findings are submitted (blind comparison mode). Open the row to enter findings."
            style={{ fontSize: 11, color: '#6d28d9', background: '#faf5ff', border: '1px solid #c4b5fd', borderRadius: 4, padding: '2px 8px', whiteSpace: 'nowrap' }}
          >
            🔒 Awaiting independent findings
          </span>
        )}

        {/* ⭐ RESTORED: Supporting Documents — available on ANY financial
            statement audit at any point in the workflow, including after
            findings are generated. Deliberately NOT gated on status.nextAction:
            the step is optional/supplemental and getWorkflowStatus skips past
            it, so an eq-nextAction gate would never fire. Uploading resets the
            testing steps downstream so new evidence is incorporated. */}
        {d.workflowAssignment?.templateId === 'financial_statement_audit_v1' && (
          <UploadSupportingDocumentButton
            docId={d._id}
            onDone={onDone}
          />
        )}

        {debugMode && (
          <span style={debugBadge}>
            Action: {status.nextAction} · Step {status.stepNumber}/{status.totalSteps}
          </span>
        )}
      </div>
    );
  };

  /** Render Main UI */
  return (
    <div style={{ padding: '2rem', maxWidth: 1300, margin: '0 auto' }}>
      <h1>RPA Audit Dashboard</h1>
      <p style={{ marginTop: 8 }}>Multi-admin collaborative workspace for compliance automation</p>

      <div style={{ margin: '8px 0 16px 0' }}>
        <button
          onClick={() => {
            setAuditDocId('');
            setAuditOpen(true);
          }}
          style={{
            background: '#111827',
            color: '#fff',
            border: 'none',
            padding: '6px 10px',
            borderRadius: 6,
            fontSize: 12
          }}
        >
          Open Audit Log
        </button>
      </div>

      {/* Socket status */}
      <div style={{ margin: '16px 0' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '4px 8px',
            borderRadius: 6,
            background: socketConnected ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${socketConnected ? '#22c55e' : '#ef4444'}`,
            fontSize: 12
          }}
        >
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: socketConnected ? '#22c55e' : '#ef4444'
            }}
          />
          <span style={{ color: socketConnected ? '#15803d' : '#991b1b' }}>
            {socketConnected ? 'Real-time Connected' : 'Real-time Disconnected'}
          </span>
          {connectionError && (
            <span style={{ fontSize: 10, color: '#991b1b', marginLeft: 4 }}>({connectionError})</span>
          )}
          {realtimeUpdates.length > 0 && (
            <span
              style={{
                background: '#3b82f6',
                color: 'white',
                padding: '1px 6px',
                borderRadius: 12,
                fontSize: 10,
                fontWeight: 'bold'
              }}
            >
              {realtimeUpdates.length}
            </span>
          )}
          {socketRef.current?.id && (
            <span style={{ fontSize: 10, color: '#6b7280' }}>ID: {socketRef.current.id.slice(0, 8)}</span>
          )}
        </div>

        {realtimeUpdates.length > 0 && (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 8
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8
              }}
            >
              <strong style={{ fontSize: 14 }}>Real-time Updates</strong>
              <button
                onClick={() => setRealtimeUpdates([])}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#6b7280',
                  fontSize: 12,
                  cursor: 'pointer'
                }}
              >
                Clear
              </button>
            </div>
            <div style={{ maxHeight: 140, overflowY: 'auto' }}>
              {realtimeUpdates.map((u) => (
                <div
                  key={u.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '4px 0',
                    borderBottom: '1px solid #f1f5f9',
                    fontSize: 12
                  }}
                >
                  <span
                    style={{
                      padding: '2px 6px',
                      borderRadius: 4,
                      background:
                        u.status === 'completed'
                          ? '#f0fdf4'
                          : u.status === 'in_progress'
                          ? '#e0f2fe'
                          : '#fef2f2',
                      color:
                        u.status === 'completed'
                          ? '#15803d'
                          : u.status === 'in_progress'
                          ? '#0369a1'
                          : '#991b1b',
                      fontSize: 10,
                      fontWeight: 'bold'
                    }}
                  >
                    {u.type}
                  </span>
                  <span>{u.message || u.step}</span>
                  <span style={{ marginLeft: 'auto', color: '#6b7280', fontSize: 10 }}>
                    {new Date(u.receivedAt).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Debug controls */}
      <div
        style={{
          margin: '16px 0',
          padding: '8px',
          background: '#f3f4f6',
          border: '1px solid #d1d5db',
          borderRadius: 6
        }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={debugMode} onChange={(e) => setDebugMode(e.target.checked)} />
          <span style={{ fontSize: 14 }}>Debug Mode (workflow step details)</span>
        </label>
        <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={forceRefresh}
            style={{
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '4px 8px',
              borderRadius: 4,
              fontSize: 12
            }}
          >
            Force Refresh
          </button>
          <button
            onClick={async () => {
              try {
                await axios.post(
                  `${API_URL}/admin/rpa/test-socket`,
                  { docId: 'dashboard' },
                  { withCredentials: true }
                );
              } catch (e) {
                console.warn('[RPA] Backend test failed:', e.message);
              }
            }}
            style={{
              background: '#f59e0b',
              color: 'white',
              border: 'none',
              padding: '4px 8px',
              borderRadius: 4,
              fontSize: 12
            }}
          >
            Test Backend
          </button>
        </div>
      </div>

      {/* Summary */}
      <section style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <h2 style={h2}>Summary</h2>
          <button type="button" onClick={fetchSummary}>
            Refresh Summary
          </button>
        </div>
        {summaryError && <div style={errorBox}>{summaryError}</div>}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <SummaryCard label="Projects" value={counts.projects} sub="Total active" />
          <SummaryCard label="Documents" value={counts.docs} sub="Secure storage" />
          <SummaryCard label="Tasks" value={counts.tasks} sub="Last 30 days" />
          <SummaryCard
            label="Real-time"
            value={socketConnected ? '🟢' : '🔴'}
            sub={socketConnected ? 'Connected' : 'Disconnected'}
          />
        </div>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr', marginTop: 16 }}>
          <div style={panelStyle}>
            <div style={panelHeader}>
              <strong>Latest Documents</strong>
            </div>
            <div>
              {latestDocs?.length ? (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {latestDocs.map((d) => (
                    <li key={d._id} style={{ margin: '6px 0' }}>
                      <a href={docLinkHref(d)} target="_blank" rel="noreferrer">
                        {d.originalName || d.filename || 'Untitled'}
                      </a>{' '}
                      <span style={{ color: '#6b7280' }}>· {fmtDate(d.createdAt || d.uploadedAt)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ color: '#6b7280' }}>No recent documents.</div>
              )}
            </div>
          </div>
          <div style={panelStyle}>
            <div style={panelHeader}>
              <strong>Latest Tasks</strong>
            </div>
            <div>
              {latestTasks?.length ? (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {latestTasks.map((t) => (
                    <li key={t._id} style={{ margin: '6px 0' }}>
                      <strong>{t.title || 'Untitled Task'}</strong>{' '}
                      <span style={{ color: '#6b7280' }}>
                        · {t.status || 'queued'} · {fmtDate(t.createdAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ color: '#6b7280' }}>No recent tasks.</div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ⭐ Clients & Issues */}
      <ClientsPanel
        onOpenProject={(p) => {
          // Scope the document table below to the chosen project
          setFilters({ client: p.client || '', project: p.name || '' });
        }}
        onOpenDocument={(d) => {
          setViewingResults(d._id);
          setFilters({ client: d.clientName || '', project: d.projectName || '' });
        }}
      />

      {/* Projects */}
      <section style={sectionStyle}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12
          }}
        >
          <h2 style={h2}>Projects</h2>
          <button
            onClick={() => setShowProjectForm(!showProjectForm)}
            style={{
              background: showProjectForm ? '#dc2626' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {showProjectForm ? 'Cancel' : '+ New Project'}
          </button>
        </div>
        {showProjectForm && <ProjectForm onProjectCreated={handleProjectCreated} />}
        {projectsError && <div style={errorBox}>{projectsError}</div>}
        {loadingProjects ? (
          <p>Loading projects…</p>
        ) : projects.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
            <p style={{ margin: 0, fontSize: 16 }}>No projects yet</p>
            <p style={{ margin: '8px 0 0 0', fontSize: 14 }}>
              Create your first project to organize documents and collaborate with your team
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
            {projects.map((proj) => (
              <div key={proj._id} style={projectCard}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: 8
                  }}
                >
                  <div>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1f2937' }}>{proj.name}</h3>
                    <p style={{ margin: '4px 0 0 0', fontSize: 13, color: '#6b7280' }}>{proj.client}</p>
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {proj.isCollaborative && (
                <span style={{ ...pill, background: '#dbeafe', borderColor: '#3b82f6', color: '#1e40af', fontSize: 10 }}>
                  👥 Shared
                </span>
              )}
              {proj.myRole && (
                <span style={{ ...pill, background: '#f0fdf4', borderColor: '#22c55e', color: '#065f46', fontSize: 10 }}>
                  {proj.myRole}
                </span>
              )}
              {proj.myAuditRole && (
                <span style={{ ...pill, background: '#faf5ff', borderColor: '#c084fc', color: '#7c3aed', fontSize: 10 }}>
                  {proj.myAuditRole.replace(/_/g, ' ')}
                </span>
              )}
            </div>
                  {/* {proj.isCollaborative && (
                    <span
                      style={{
                        ...pill,
                        background: '#dbeafe',
                        borderColor: '#3b82f6',
                        color: '#1e40af',
                        fontSize: 10
                      }}
                    >
                      👥 Shared
                    </span>
                  )} */}
                </div>
                {proj.description && (
                  <p style={{ margin: '8px 0', fontSize: 13, color: '#4b5563', lineHeight: 1.5 }}>
                    {proj.description}
                  </p>
                )}
                <div
                  style={{
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <span style={{ fontSize: 12, color: '#9ca3af' }}>Created {fmtDate(proj.createdAt)}</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveProject(proj);
                        setMembersOpen(true);
                      }}
                      style={{
                        background: '#111827',
                        color: '#fff',
                        border: 'none',
                        padding: '4px 8px',
                        borderRadius: 4,
                        fontSize: 11,
                        cursor: 'pointer'
                      }}
                    >
                      👥 Members
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveProject(proj);
                        setTasksOpen(true);
                      }}
                      style={{
                        background: '#3b82f6',
                        color: '#fff',
                        border: 'none',
                        padding: '4px 8px',
                        borderRadius: 4,
                        fontSize: 11,
                        cursor: 'pointer'
                      }}
                    >
                      Tasks
                    </button>
                    {proj.myRole === 'owner' && (
                      <button
                        onClick={() => handleDeleteProject(proj._id)}
                        style={{
                          background: 'transparent',
                          border: '1px solid #fca5a5',
                          color: '#dc2626',
                          padding: '4px 8px',
                          borderRadius: 4,
                          fontSize: 11,
                          cursor: 'pointer'
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Upload */}
      <section style={sectionStyle}>
        <h2 style={h2}>Upload Document</h2>
        <UploadDoc
          onUploaded={() => {
            fetchDocs();
            fetchSummary();
          }}
        />
      </section>

      {/* Documents */}
      <section style={sectionStyle}>
        <h2 style={h2}>Documents</h2>
        <form onSubmit={onFilter} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <input
            type="text"
            placeholder="Filter by client"
            value={filters.client}
            onChange={(e) => setFilters({ ...filters, client: e.target.value })}
          />
          <input
            type="text"
            placeholder="Filter by project"
            value={filters.project}
            onChange={(e) => setFilters({ ...filters, project: e.target.value })}
          />
          <button type="submit">Apply Filters</button>
          <button type="button" onClick={resetFilters}>
            Reset
          </button>
          <button type="button" onClick={fetchDocs}>
            Refresh
          </button>
        </form>
        {loading && <p>Loading documents…</p>}
        {error && <p style={{ color: 'crimson' }}>{error}</p>}
        {docs.length === 0 ? (
          <p>No documents yet.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={table}>
              <thead>
                <tr>
                  <th>File</th>
                  <th>Type</th>
                  <th>Client</th>
                  <th>Project</th>
                  <th>Uploaded</th>
                  <th>Classification</th>
                  <th>Workflow Status</th>
                  <th>Next Action</th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d) => (
                  <React.Fragment key={d._id}>
                    <tr
                      onClick={() => handleRowSelect(d)}
                      style={{
                        cursor: 'pointer',
                        background: selectedAgentDocId === d._id ? '#eff6ff' : undefined,
                        outline: selectedAgentDocId === d._id ? '2px solid #3b82f6' : undefined,
                        outlineOffset: '-2px',
                        transition: 'background 0.15s',
                      }}
                      title={selectedAgentDocId === d._id ? 'Selected for Audit Agent — click to deselect' : 'Click to select for Audit Agent'}
                    >
                      <td>
                        {/* Agent selection indicator */}
                        {selectedAgentDocId === d._id && (
                          <span style={{
                            display: 'inline-block',
                            width: 8, height: 8,
                            borderRadius: '50%',
                            background: '#3b82f6',
                            marginRight: 6,
                            flexShrink: 0,
                            verticalAlign: 'middle',
                          }} title="Selected for Audit Agent" />
                        )}
                        <span style={{ verticalAlign: 'middle', marginRight: 6, fontWeight: selectedAgentDocId === d._id ? 600 : 400 }}>
                          {d.originalName || d.filename || 'Untitled'}
                        </span>
                        {/* PDF open — stops row-click propagation so it doesn't toggle selection */}
                        {docLinkHref(d) !== '#' && (
                          <a
                            href={docLinkHref(d)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={e => e.stopPropagation()}
                            style={{
                              fontSize: 10,
                              color: '#6b7280',
                              textDecoration: 'none',
                              border: '1px solid #d1d5db',
                              borderRadius: 3,
                              padding: '1px 5px',
                              verticalAlign: 'middle',
                            }}
                            title="Open file in new tab"
                          >
                            ↗ Open
                          </a>
                        )}
                      </td>
                      <td>{kindLabel(d)}</td>
                      <td>{clientLabel(d)}</td>
                      <td>{projectLabel(d)}</td>
                      <td>{fmtDate(d.createdAt || d.uploadedAt)}</td>
                      <td>{classificationPill(d)}</td>
                      <td>{workflowPill(d)}</td>
                      <td style={{ position: 'relative', minWidth: 260 }} onClick={e => e.stopPropagation()}>
                        {renderActionButtons(d)}
                      </td>
                    </tr>
                    {viewingResults === d._id && (
                      <tr>
                        <td colSpan="8" style={{ padding: 0, background: '#f8fafc' }}>
                          <StepResultsViewer doc={d} onClose={() => setViewingResults(null)} onRefresh={fetchDocs} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <AuditLogDrawer isOpen={auditOpen} onClose={() => setAuditOpen(false)} initialDocId={auditDocId} />
      <ProjectMembersPanel isOpen={membersOpen} onClose={() => setMembersOpen(false)} project={activeProject} />
      <TaskBoard isOpen={tasksOpen} onClose={() => setTasksOpen(false)} project={activeProject} />
    </div>
  );
};

/** Styles */
const sectionStyle = {
  border: '1px solid #e0e0e0',
  borderRadius: 10,
  padding: '1rem',
  marginTop: '1.25rem',
  background: '#fff'
};

const h2 = { margin: 0, marginBottom: 12, fontSize: 18 };
const table = { width: '100%', borderCollapse: 'collapse' };

const debugBadge = {
  display: 'inline-block',
  padding: '2px 6px',
  borderRadius: 4,
  background: '#fef3c7',
  border: '1px solid #f59e0b',
  color: '#92400e',
  fontSize: 10
};

const waitingBadge = {
  display: 'inline-block',
  padding: '2px 6px',
  borderRadius: 4,
  background: '#dbeafe',
  border: '1px solid #3b82f6',
  color: '#1e40af',
  fontSize: 10,
  fontWeight: 'bold'
};

const completeBadge = {
  display: 'inline-block',
  padding: '2px 6px',
  borderRadius: 4,
  background: '#f0fdf4',
  border: '1px solid #22c55e',
  color: '#15803d',
  fontSize: 10,
  fontWeight: 'bold'
};

const dangerBtn = {
  background: '#ffe3e3',
  border: '1px solid #ffb3b3',
  padding: '4px 6px',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 11,
  color: '#991b1b'
};

const cardStyle = {
  flex: 1,
  minWidth: 220,
  padding: '16px',
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  background: '#fff',
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
};

const projectCard = {
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: 16,
  background: '#fff',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  transition: 'box-shadow 0.2s'
};

const panelStyle = {
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  background: '#fff',
  padding: 12
};

const panelHeader = {
  borderBottom: '1px solid #e5e7eb',
  paddingBottom: 8,
  marginBottom: 8
};

const errorBox = {
  marginTop: 8,
  padding: 8,
  borderRadius: 8,
  background: '#fef2f2',
  color: '#991b1b',
  border: '1px solid #fecaca'
};

const SummaryCard = ({ label, value, sub }) => (
  <div style={cardStyle}>
    <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 700 }}>{value}</div>
    {sub ? <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>{sub}</div> : null}
  </div>
);

export default RpaDashboard;
